import React, { useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Music, Search, ThumbsUp, ThumbsDown, Send, Clock, User, MessageSquare, Play, Pause, ListMusic, BarChart3, Star, Lock, Link2, Check, X, Heart, Flame, Trophy, Loader2, QrCode, Share2, Smartphone, Copy, Shield, CheckCircle, MapPin, Phone, Mail, RefreshCw, ChevronDown, ChevronUp, ArrowUp, ArrowDown, ExternalLink, Flag, HelpCircle, Bell, Camera, Sparkles, Sun, Moon } from 'lucide-react';
import { PEOPLES_PICK_HIDDEN, OVERFLOW_POOL_ENABLED } from '../../utils/featureFlags';
import { useTheme } from '../../context/ThemeContext';
import { getEventByUrl, getEventById, Event, generateQrCodeUrl, isGameplayActive } from '../../services/events';
import { attendeeSongSkipsApproval } from '../../utils/autoApproval';
import { verifyAttendee, isAttendeeVerified, getVerifiedCache, setVerifiedCache } from '../../services/verification';
import { getRequestsByEventId, subscribeToRequestsByEventId, SongRequest, createRequest, voteForRequest, getVoteStatus, getNowPlaying, hasDuplicateRequest, findExistingSongRequest, countRequestsByAttendee, countRequestsByAttendeeAfter, countNominationsByAttendee, isInCooldown, getRequestById, isTrending, getAttendeeRanking, submitRanking, getSeenSongs, markSongAsSeen, checkAndApproveSuperRequest, approveSuperRequestManually, isFuzzySongMatch, VoteType, getOverflowByAttendee, reorderOverflow } from '../../services/requests';
import { cancelOwnRequest, cancelRefusalReason } from '../../services/requestCancel';
import { cancelBlockMessage, isAttendeeCancelled, requestOnlyCancelBlock, type RequestCancelBlock } from '../../utils/requestCancel';
import { getAttendeeIdAsync, getAttendeeNickname, setAttendeeNickname, setCodeWord, linkWithCodeWord, hasCodeWord, getCodeWordHash, getAnonDismissed, setAnonDismissed } from '../../services/attendeeIdentity';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';
import { isTestIdentityEnabled, signInAsTestUser } from '../../services/testIdentity';
import { doc, getDoc } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { auth, db } from '../../services/firebase';
import { hasTakenPart, recordEventGuest } from '../../services/eventGuests';
import { getDJMessagesForAttendees, Message, markAllMessagesReadByAttendee } from '../../services/messages';
import { submitFeedback, getAttendeeFeedback, Feedback } from '../../services/feedback';
import { SongSearchResult, formatDuration, isExplicit } from '../../services/itunesApi';
import { searchSongs } from '../../services/songSearch';
import { searchCatalogOf } from '../../services/playModes';
import { formatShortDate, formatEventDateTimeParts, DAY_ABBR } from '../../utils/dateUtils';
import { looksLikeLink, LINKS_NOT_SUPPORTED_MESSAGE } from '../../utils/attendeeSearchInput';
import { spotifyTrackIdFrom } from '../../utils/songIdentity';
import { addToRankingBottom, anchorBeforeMove, dropIntoOrder, effectiveBallot, isPartitioned, moveAboveDownvotedBlock, moveToDownvotedBlockEnd, moveToIndex, partitionDownvoted, rankSizeOf, replaceInRanking, resolveDivider, unrankTo } from '../../utils/rankingOrder';
import { isSongBlocked } from '../../services/songReviews';
import { flagSong } from '../../services/songFlags';
import { getActivePollsForEvent, getShowToNewUsersPollsForEvent, submitPollResponse, getPollResponse, Poll, PollAnswer } from '../../services/polls';
import PollDisplay from '../../components/polls/PollDisplay';
import PollResultsComponent from '../../components/polls/PollResults';
import { subscribeToAttendeePrizes, claimPrize, Prize } from '../../services/prizes';
import PrizeClaimBanner from '../../components/prizes/PrizeClaimBanner';
import PrizeSongPicker from '../../components/prizes/PrizeSongPicker';
import ShoutoutOverlay from '../../components/prizes/ShoutoutOverlay';
import { hasBeenAskedAboutPush, requestPushPermission, isPushEnabled, ensurePushTokenIfGranted, pushPermissionState, type PushPermissionState } from '../../services/pushNotifications';
import { setRallyChoice, subscribeToMyRallyChoices, type RallyChoice } from '../../services/crowdRally';
import { Html5Qrcode } from 'html5-qrcode';
import toast from 'react-hot-toast';
import AttendeeTutorial, { HelpTopicId } from '../../components/onboarding/AttendeeTutorial';
import { MoveSongModal } from '../../components/MoveSongModal';
import { SortedLinePicker } from '../../components/SortedLinePicker';
import { DragHandle, SortableItem, SortableList, type SortableRenderProps } from '../../components/SortableList';
import { arrayMove } from '@dnd-kit/sortable';
import { filterProfanity } from '../../utils/profanityFilter';
import {
  makePPGroupKey, upvoteNomination, removeNominationUpvote,
  getUpvotedGroupKeysByAttendee, subscribeToUpvoteCountsForEvent,
  getDismissedGroupKeys, dismissGroupKey,
} from '../../services/nominationPool';
import { PredictionGame, subscribeToPredictionGame } from '../../services/prediction';
import PredictionPlayModal from '../../components/games/PredictionPlayModal';
import { TriviaGame, subscribeToTriviaGame } from '../../services/trivia';
import TriviaPlayModal from '../../components/games/TriviaPlayModal';
import { DanceOff, subscribeToLatestDanceOff } from '../../services/danceOff';
import DanceOffVoteModal from '../../components/games/DanceOffVoteModal';
import DanceOffResultBanner from '../../components/games/DanceOffResultBanner';
import AwardRevealModal from '../../components/games/AwardRevealModal';
import EventAwardsCard from '../../components/games/EventAwardsCard';
import { subscribeToEventAwards, type EventAwards } from '../../services/eventAwards';
import { RouletteRound, subscribeToLatestRouletteRound } from '../../services/roulette';
import RouletteSpotlightBanner from '../../components/games/RouletteSpotlightBanner';
import { ShowWrapUpModal, type WrapUpStep } from '../../components/attendee/ShowWrapUpModal';
import { EventEndedView } from '../../components/attendee/EventEndedView';
import { EventMissedView } from '../../components/attendee/EventMissedView';
import { RequestsCloseNotice } from '../../components/attendee/RequestsCloseNotice';
import { useRequestWindow } from '../../hooks/useRequestWindow';
import { areRequestsClosed, isRequestsClosedForAttendee, isVotingBlockedBySchedule, requestsClosedBannerMessage, requestsJustClosedMessage, type AttendeeVotingStyle } from '../../utils/requestWindow';
import { VotedOutModal } from '../../components/VotedOutModal';
import { RallyDetailModal, type RallyDetailReason } from '../../components/RallyDetailModal';
import {
  crowdFavoriteLabel,
  isCrowdFavorite,
  isVotedOut,
  returnLinePercent,
  sharePercent,
  votedBackSeenKey,
  votedBackToastIds,
  voteOutActive,
  voteRulesOf,
} from '../../utils/voteOut';
import {
  rallyAlmostPopupIds,
  rallyAlmostSeenKey,
  rallyFlipPopupIds,
  rallyFlipSeenKey,
  rallyGoalOf,
  rallyNoticeQueue,
  rallyNoticeSeenKey,
  rallyShareOf,
} from '../../utils/crowdRally';
import {
  buildShareVoteBeatsText,
  canLeaveFeedback,
  endedView,
  nextEventContactPath,
  shouldShowWrapUp,
  wrapUpSeenKey,
  type TookPart,
} from '../../utils/endOfShow';

// QR Scanner Modal for on-site verification
function QrScannerModal({ onClose, onVerified, eventId, attendeeId }: {
  onClose: () => void;
  onVerified: () => void;
  eventId: string;
  attendeeId: string;
}) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const processedRef = useRef(false);
  const mountedRef = useRef(true);
  const scannerId = useRef('qr-scanner-' + Date.now());

  useEffect(() => {
    mountedRef.current = true;
    let scanner: Html5Qrcode | null = null;

    // Small delay to ensure DOM element is fully rendered (fixes iOS blank screen)
    const initTimer = setTimeout(() => {
      const el = document.getElementById(scannerId.current);
      if (!el || !mountedRef.current) return;

      scanner = new Html5Qrcode(scannerId.current);
      scannerRef.current = scanner;

      scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          if (processedRef.current || !mountedRef.current) return;
          processedRef.current = true;
          try {
            const url = new URL(decodedText);
            const token = url.searchParams.get('verify');
            if (!token) {
              if (mountedRef.current) setError('Invalid QR code — no verification token found');
              processedRef.current = false;
              return;
            }
            const result = await verifyAttendee(eventId, attendeeId, token);
            if (!mountedRef.current) return;
            if (result.success) {
              if (scanner?.isScanning) scanner.stop().catch(() => {});
              onVerified();
            } else {
              setError(result.message || 'Verification failed');
              processedRef.current = false;
            }
          } catch {
            if (mountedRef.current) setError('Invalid QR code');
            processedRef.current = false;
          }
        },
        () => {} // ignore scan failures (no QR found in frame)
      ).then(() => {
        if (mountedRef.current) setScanning(true);
      }).catch((err: unknown) => {
        if (mountedRef.current) {
          const msg = err instanceof Error ? err.message : String(err);
          if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
            setError('Camera permission denied. Please allow camera access and try again.');
          } else if (msg.includes('NotFoundError') || msg.includes('no camera')) {
            setError('No camera found on this device.');
          } else {
            setError(msg || 'Unable to start camera');
          }
        }
      });
    }, 100);

    return () => {
      mountedRef.current = false;
      clearTimeout(initTimer);
      if (scanner?.isScanning) {
        scanner.stop().catch(() => {});
      }
      scannerRef.current = null;
    };
  }, [eventId, attendeeId, onVerified]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Camera className="w-5 h-5 text-teal-600" />
            Scan Verification QR
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <div id={scannerId.current} className="w-full rounded-lg overflow-hidden" style={{ minHeight: '250px' }} />
          {!scanning && !error && (
            <div className="text-center py-8 text-sm text-gray-500">Starting camera...</div>
          )}
          {error && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
            Point your camera at the verification QR code displayed at the venue
          </p>
        </div>
      </div>
    </div>
  );
}

// Tab types
type TabId = 'request' | 'browse' | 'rankings' | 'peoples-pick' | 'my-requests' | 'messages' | 'stats' | 'feedback';

interface Tab {
  id: TabId;
  label: string;
  icon: typeof Music;
  color: string;
}

const RANKING_AUTOSAVE_MS = 1500;

// Dance-off results and roulette spotlights show for this long, so an old one never appears on first load
const GAME_BANNER_WINDOW_MS = 3 * 60 * 1000;

function msLeftInGameBannerWindow(iso: string | null | undefined, nowMs: number): number {
  if (!iso) return 0;
  const startedMs = Date.parse(iso);
  if (Number.isNaN(startedMs)) return 0;
  return Math.max(0, startedMs + GAME_BANNER_WINDOW_MS - nowMs);
}

const TABS: Tab[] = [
  { id: 'request', label: 'Request', icon: Send, color: 'text-emerald-500' },
  { id: 'browse', label: 'Vote', icon: ThumbsUp, color: 'text-blue-500' },
  { id: 'rankings', label: 'My Rankings', icon: Star, color: 'text-yellow-500' },
  { id: 'peoples-pick', label: "People's Pick", icon: Sparkles, color: 'text-amber-500' },
  { id: 'my-requests', label: 'My Requests', icon: ListMusic, color: 'text-violet-500' },
  { id: 'messages', label: 'Messages', icon: MessageSquare, color: 'text-pink-500' },
  { id: 'stats', label: 'Stats', icon: BarChart3, color: 'text-cyan-500' },
  { id: 'feedback', label: 'Feedback', icon: Heart, color: 'text-rose-500' },
];

export default function EventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { resolvedTheme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    try { return (sessionStorage.getItem('votebeats_active_tab') as TabId) || 'request'; }
    catch { return 'request'; }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const staticOrderRef = useRef<string[]>([]);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const [requestSong, setRequestSong] = useState('');
  const [requestArtist, setRequestArtist] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [event, setEvent] = useState<Event | null>(null);
  const searchCatalog = searchCatalogOf(event);
  const { nowMs: requestWindowNowMs, closeAtMs: requestsCloseAtMs } = useRequestWindow(event?.settings);
  const [requests, setRequests] = useState<SongRequest[]>([]);
  const allRequestsRef = useRef<SongRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [attendeeId, setAttendeeId] = useState('');
  const [votedRequests, setVotedRequests] = useState<Map<string, VoteType>>(new Map());
  const [trendingIds, setTrendingIds] = useState<Set<string>>(new Set());
  const [seenSongIds, setSeenSongIds] = useState<Set<string>>(new Set());
  const [djMessages, setDJMessages] = useState<Message[]>([]);
  const [nowPlaying, setNowPlaying] = useState<SongRequest | null>(null);
  const [playedRequests, setPlayedRequests] = useState<SongRequest[]>([]);
  const [nickname, setNickname] = useState('');
  const [isEditingNickname, setIsEditingNickname] = useState(true);
  const nicknameInputRef = useRef<HTMLInputElement>(null);
  const [myRequests, setMyRequests] = useState<SongRequest[]>([]);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelLockedReasons, setCancelLockedReasons] = useState<Record<string, RequestCancelBlock>>({});
  // Request status tracking (F19)
  const [requestStatusCount, setRequestStatusCount] = useState(0);
  const [requestStatusMax, setRequestStatusMax] = useState(5);
  const [nominationStatusCount, setNominationStatusCount] = useState(0);
  const [nominationStatusMax, setNominationStatusMax] = useState(0); // 0 = unlimited
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [voteCooldownUntil, setVoteCooldownUntil] = useState<number>(0); // timestamp when vote cooldown expires
  const [voteCooldownDisplay, setVoteCooldownDisplay] = useState(0); // seconds remaining for display
  const [showCodeWordPanel, setShowCodeWordPanel] = useState(false);
  const [codeWordInput, setCodeWordInput] = useState('');
  const [showLinkQr, setShowLinkQr] = useState(false);
  const [linkQrUrl, setLinkQrUrl] = useState<string | null>(null);
  const [hasCodeWordSet, setHasCodeWordSet] = useState(false);
  const [nominations, setNominations] = useState<SongRequest[]>([]);
  const [uniqueAttendeeCount, setUniqueAttendeeCount] = useState(0);
  const [upvotedGroupKeys, setUpvotedGroupKeys] = useState<Set<string>>(new Set());
  const [dismissedGroupKeys, setDismissedGroupKeys] = useState<Set<string>>(new Set());
  const [upvoteCounts, setUpvoteCounts] = useState<Map<string, number>>(new Map());
  const [isExplicitSong, setIsExplicitSong] = useState(false);
  const [explicitWarningAcknowledged, setExplicitWarningAcknowledged] = useState(false);
  // Feedback state
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState<Feedback | null>(null);
  // Ranked choice voting state — one order (ranked = first rankSize songs, the rest unranked), plus New and Played sections
  const [myOrder, setMyOrder] = useState<string[]>([]);
  const [sortedThroughId, setSortedThroughId] = useState<string | null>(null);
  const myOrderRef = useRef<string[]>([]);
  const sortedThroughIdRef = useRef<string | null>(null);
  myOrderRef.current = myOrder;
  sortedThroughIdRef.current = sortedThroughId;
  const [rankingSaveDue, setRankingSaveDue] = useState(false);
  const [rankingSaveState, setRankingSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // Crowd vote-out return toast (ITER-144) and crowd rally (ITER-190): one first notice per voted-out song per device,
  // a detail view, and flip pop-ups for opted-in attendees. rallyChoices is null until the first snapshot arrives.
  const [rallyChoices, setRallyChoices] = useState<Map<string, RallyChoice> | null>(null);
  const [rallyNoticeId, setRallyNoticeId] = useState<string | null>(null);
  const [rallyDetail, setRallyDetail] = useState<{ id: string; reason: RallyDetailReason } | null>(null);
  const [rallySaving, setRallySaving] = useState(false);
  const [rallyPushState, setRallyPushState] = useState<PushPermissionState>(() => pushPermissionState());
  const rallyDeepLinkRef = useRef<string | null | undefined>(undefined);
  if (rallyDeepLinkRef.current === undefined) {
    rallyDeepLinkRef.current = new URLSearchParams(window.location.search).get('rally');
  }
  const crowdNoticeSeenRef = useRef<Set<string>>(new Set());
  const prevQueuedVotedOutIdsRef = useRef<Set<string> | null>(null);
  // Rank swap modal (when ranked is full and user clicks +Rank)
  const [rankSwapModal, setRankSwapModal] = useState<{ incomingId: string } | null>(null);
  // Move window (GROUP-32): send a song straight to a position
  const [moveSongId, setMoveSongId] = useState<string | null>(null);
  const [linePickerOpen, setLinePickerOpen] = useState(false);
  // Songs leaving the queue drop out of the order without a save; the server already scores the next song.
  // The "sorted up to here" line first moves up to the nearest song that stays.
  const dropFromRankingOrder = useCallback((removeIds: Set<string>) => {
    const order = myOrderRef.current;
    if (!order.some(id => removeIds.has(id))) return;
    const keep = new Set(order.filter(id => !removeIds.has(id)));
    setSortedThroughId(resolveDivider(order, sortedThroughIdRef.current, keep));
    setMyOrder(order.filter(id => !removeIds.has(id)));
  }, []);
  // F21: Duplicate request modal state
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [duplicateExistingRequest, setDuplicateExistingRequest] = useState<SongRequest | null>(null);
  const [duplicateType, setDuplicateType] = useState<'same-user' | 'cross-user'>('same-user');
  // Poll state
  const [activePolls, setActivePolls] = useState<Poll[]>([]);
  const [answeredPollIds, setAnsweredPollIds] = useState<Set<string>>(new Set());
  const [dismissedPollIds, setDismissedPollIds] = useState<Set<string>>(new Set());
  const [previousAnswersMap, setPreviousAnswersMap] = useState<Map<string, PollAnswer[]>>(new Map());
  const [seenResendTimestamps, setSeenResendTimestamps] = useState<Map<string, string>>(new Map());
  // Song flagging state
  const [flagModal, setFlagModal] = useState<{ requestId: string; songTitle: string; songArtist: string } | null>(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  // Expandable song titles on tap
  const [expandedTitleIds, setExpandedTitleIds] = useState<Set<string>>(new Set());
  const toggleTitle = (id: string) => {
    setExpandedTitleIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const [flaggedRequestIds, setFlaggedRequestIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('votebeats_flagged_songs');
      return stored ? new Set(JSON.parse(stored)) : new Set<string>();
    } catch { return new Set<string>(); }
  });
  const [overflowBannerDismissed, setOverflowBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('votebeats_overflow_banner_dismissed') === 'true'; }
    catch { return false; }
  });
  const [rankBannerDismissed, setRankBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('votebeats_rank_banner_dismissed') === 'true'; }
    catch { return false; }
  });
  // Vote countdown state
  const [voteCloseCountdown, setVoteCloseCountdown] = useState<string | null>(null);
  const [countdownUrgency, setCountdownUrgency] = useState<'normal' | 'urgent' | 'last-call'>('normal');
  // Song search autocomplete state
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SongSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedSong, setSelectedSong] = useState<SongSearchResult | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sticky event name — shows in header when title scrolls out of view
  const eventTitleRef = useRef<HTMLHeadingElement>(null);
  const eventNameBannerRef = useRef<HTMLDivElement>(null);
  const [showStickyEventName, setShowStickyEventName] = useState(false);
  // Inline audio preview state
  const [playingPreviewUrl, setPlayingPreviewUrl] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  // QR Code / Share modal state
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  // Add to Home Screen modal state
  const [showA2HSModal, setShowA2HSModal] = useState(false);
  // Request submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Capture super mode state when the form renders so it doesn't change between render and submit
  const [nominateMode, setNominateMode] = useState(false);
  const [myPendingNominations, setMyPendingNominations] = useState<SongRequest[]>([]);
  const superModeAtRenderRef = useRef(false);
  // Manual refresh + idle detection
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastInteractionRef = useRef(Date.now());
  const idleCTAShownRef = useRef(false);
  const idleCTAShownAtRef = useRef(0);
  // Voting open countdown
  const [voteOpenCountdown, setVoteOpenCountdown] = useState<string | null>(null);
  // Notes for DJ (manual request)
  const [requestNotes, setRequestNotes] = useState('');
  const [manualEntryExpanded, setManualEntryExpanded] = useState(false);
  const [linkInputNotice, setLinkInputNotice] = useState(false);
  // Collapsible queue in request tab
  const [showQueueInRequest, setShowQueueInRequest] = useState(false);
  // New song notification tracking
  const previousRequestIdsRef = useRef<Set<string>>(new Set());
  // On-site verification state
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [tutorialMode, setTutorialMode] = useState<'consent' | 'help' | 'tour' | null>(null);
  const [tutorialHelpTopic, setTutorialHelpTopic] = useState<HelpTopicId | null>(null);
  // Push notification opt-in banner
  const [showPushBanner, setShowPushBanner] = useState(false);
  // Prize state
  const [unclaimedPrizes, setUnclaimedPrizes] = useState<Prize[]>([]);
  const [activeShoutouts, setActiveShoutouts] = useState<Prize[]>([]);
  const [prizeSongPickerPrize, setPrizeSongPickerPrize] = useState<Prize | null>(null);
  const [dismissedPrizeIds, setDismissedPrizeIds] = useState<Set<string>>(new Set());
  // Prediction game state
  const [activePredictionGame, setActivePredictionGame] = useState<PredictionGame | null>(null);
  const [showPredictionModal, setShowPredictionModal] = useState(false);
  // Trivia game state
  const [activeTriviaGame, setActiveTriviaGame] = useState<TriviaGame | null>(null);
  const [showTriviaModal, setShowTriviaModal] = useState(false);
  const dismissedTriviaGameId = useRef<string | null>(null);
  // Dance-off (ITER-022) and request roulette (ITER-020)
  const [latestDanceOff, setLatestDanceOff] = useState<DanceOff | null>(null);
  const [showDanceOffModal, setShowDanceOffModal] = useState(false);
  const dismissedDanceOffId = useRef<string | null>(null);
  const [dismissedDanceOffResultId, setDismissedDanceOffResultId] = useState<string | null>(null);
  const [latestRouletteRound, setLatestRouletteRound] = useState<RouletteRound | null>(null);
  const [dismissedRouletteRoundId, setDismissedRouletteRoundId] = useState<string | null>(null);
  // Event awards reveal (ITER-041): seen marker is revealed.length + 1 once this tab has seen the start
  const [eventAwards, setEventAwards] = useState<EventAwards | null>(null);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const seenAwardCount = useRef<number | null>(null);
  const [gameBannerClock, setGameBannerClock] = useState(() => Date.now());
  // End-of-show wrap-up (ITER-175 / ITER-199)
  const [showWrapUp, setShowWrapUp] = useState(false);
  const [wrapUpInitialStep, setWrapUpInitialStep] = useState<WrapUpStep>('thanks');
  const wrapUpShownForRef = useRef(false);
  const [tookPart, setTookPart] = useState<TookPart>('unknown');
  const [tookPartLookupFailed, setTookPartLookupFailed] = useState(false);
  const [choseEndedPage, setChoseEndedPage] = useState(false);

  const voteRules = voteRulesOf(event?.settings);
  const participantCount = event?.participantCount ?? 0;
  const crowdVoteOutActive = voteOutActive(voteRules);
  const downvotedIds = useMemo(
    () => new Set([...votedRequests].filter(([, type]) => type === 'down').map(([id]) => id)),
    [votedRequests],
  );

  // localStorage can be blocked; the in-memory set keeps a notice from repeating in this tab either way
  const hasSeenCrowdNotice = useCallback((key: string): boolean => {
    if (crowdNoticeSeenRef.current.has(key)) return true;
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.warn('Could not read whether a crowd notice was seen:', error);
      return false;
    }
  }, []);

  const markCrowdNoticeSeen = useCallback((key: string) => {
    crowdNoticeSeenRef.current.add(key);
    try {
      localStorage.setItem(key, new Date().toISOString());
    } catch (error) {
      console.warn('Could not remember that a crowd notice was seen:', error);
    }
  }, []);

  useEffect(() => {
    if (!event?.id || !attendeeId || attendeeId === 'pending-auth') return;
    return subscribeToMyRallyChoices(event.id, attendeeId, setRallyChoices);
  }, [event?.id, attendeeId]);

  const rallyOutRequest = useCallback(
    (requestId: string | null | undefined): SongRequest | undefined => (requestId
      ? requests.find(r => r.id === requestId && r.status === 'queued' && isVotedOut(r))
      : undefined),
    [requests],
  );
  const rallyNoticeRequest = crowdVoteOutActive ? rallyOutRequest(rallyNoticeId) : undefined;
  const rallyDetailRequest = crowdVoteOutActive ? rallyOutRequest(rallyDetail?.id) : undefined;
  const rallyModalBlocked = showTriviaModal || showPredictionModal || showWrapUp || showAwardModal;

  useEffect(() => {
    if (rallyNoticeId && !rallyNoticeRequest) setRallyNoticeId(null);
    if (rallyDetail && !rallyDetailRequest) setRallyDetail(null);
  }, [rallyNoticeId, rallyNoticeRequest, rallyDetail, rallyDetailRequest]);

  useEffect(() => {
    if (!event?.id || !crowdVoteOutActive || !rallyChoices || rallyNoticeId || rallyDetail || rallyModalBlocked) return;
    const eventId = event.id;
    const [nextId] = rallyNoticeQueue(requests, rallyChoices, id => hasSeenCrowdNotice(rallyNoticeSeenKey(eventId, id)));
    if (nextId) setRallyNoticeId(nextId);
  }, [event?.id, requests, crowdVoteOutActive, rallyChoices, rallyNoticeId, rallyDetail, rallyModalBlocked, hasSeenCrowdNotice]);

  useEffect(() => {
    if (!event?.id || !crowdVoteOutActive || !rallyChoices || rallyNoticeId || rallyDetail || rallyModalBlocked) return;
    const [almostId] = rallyAlmostPopupIds(requests, rallyChoices, hasSeenCrowdNotice);
    const [flipId] = rallyFlipPopupIds(requests, rallyChoices, hasSeenCrowdNotice);
    const popupId = almostId ?? flipId;
    const trend = popupId ? requests.find(r => r.id === popupId)?.rallyTrend : null;
    if (!popupId || !trend) return;
    // One pop-up covers both when a flip and "almost back" land together
    if (trend.direction) markCrowdNoticeSeen(rallyFlipSeenKey(event.id, popupId, trend.flipCount));
    if (trend.almostThereSent) markCrowdNoticeSeen(rallyAlmostSeenKey(event.id, popupId, trend.flipCount));
    setRallyDetail({ id: popupId, reason: almostId ? 'almost' : 'flip' });
  }, [event?.id, requests, crowdVoteOutActive, rallyChoices, rallyNoticeId, rallyDetail, rallyModalBlocked, hasSeenCrowdNotice, markCrowdNoticeSeen]);

  useEffect(() => {
    const deepLinkId = rallyDeepLinkRef.current;
    if (!deepLinkId || !crowdVoteOutActive) return;
    const target = requests.find(r => r.id === deepLinkId);
    if (!target) return;
    rallyDeepLinkRef.current = null;
    const url = new URL(window.location.href);
    url.searchParams.delete('rally');
    window.history.replaceState({}, '', url.pathname + url.search);
    if (target.status !== 'queued' || !isVotedOut(target)) return;
    if (event?.id) markCrowdNoticeSeen(rallyNoticeSeenKey(event.id, deepLinkId));
    setRallyDetail({ id: deepLinkId, reason: 'detail' });
  }, [requests, crowdVoteOutActive, event?.id, markCrowdNoticeSeen]);

  useEffect(() => {
    const currentOut = new Set(requests.filter(r => r.status === 'queued' && isVotedOut(r)).map(r => r.id));
    const previousOut = prevQueuedVotedOutIdsRef.current;
    prevQueuedVotedOutIdsRef.current = currentOut;
    if (!previousOut || !event?.id || !crowdVoteOutActive) return;
    const eventId = event.id;
    const queuedIds = new Set(requests.filter(r => r.status === 'queued').map(r => r.id));
    const dontCareIds = new Set([...(rallyChoices ?? [])].filter(([, choice]) => choice === 'dontCare').map(([id]) => id));
    votedBackToastIds(previousOut, currentOut, queuedIds, id => hasSeenCrowdNotice(votedBackSeenKey(eventId, id)), dontCareIds)
      .forEach(id => {
        markCrowdNoticeSeen(votedBackSeenKey(eventId, id));
        const title = requests.find(r => r.id === id)?.song.title ?? 'A song';
        toast(`${title} is back in the queue!`, { icon: '🎉' });
      });
  }, [requests, event?.id, crowdVoteOutActive, rallyChoices, hasSeenCrowdNotice, markCrowdNoticeSeen]);

  const saveRallyChoice = useCallback(async (requestId: string, choice: RallyChoice): Promise<boolean> => {
    if (!event?.id || !attendeeId || attendeeId === 'pending-auth') {
      toast.error("Couldn't save your choice. Try again.");
      return false;
    }
    setRallySaving(true);
    try {
      await setRallyChoice(event.id, requestId, attendeeId, choice);
      markCrowdNoticeSeen(rallyNoticeSeenKey(event.id, requestId));
      const trend = requests.find(r => r.id === requestId)?.rallyTrend;
      if (choice === 'in' && trend?.direction) markCrowdNoticeSeen(rallyFlipSeenKey(event.id, requestId, trend.flipCount));
      if (choice === 'in' && trend?.almostThereSent) markCrowdNoticeSeen(rallyAlmostSeenKey(event.id, requestId, trend.flipCount));
      return true;
    } catch (error) {
      console.error('Failed to save rally choice:', error);
      toast.error("Couldn't save your choice. Try again.");
      return false;
    } finally {
      setRallySaving(false);
    }
  }, [event?.id, attendeeId, requests, markCrowdNoticeSeen]);

  const closeRallyNotice = useCallback(() => {
    if (event?.id && rallyNoticeId) markCrowdNoticeSeen(rallyNoticeSeenKey(event.id, rallyNoticeId));
    setRallyNoticeId(null);
  }, [event?.id, rallyNoticeId, markCrowdNoticeSeen]);

  const handleRallyHelp = useCallback(async () => {
    const requestId = rallyNoticeId;
    if (!requestId || !event?.id) return;
    if (!(await saveRallyChoice(requestId, 'in'))) return;
    setRallyNoticeId(null);
    setRallyDetail({ id: requestId, reason: 'joined' });
    void ensurePushTokenIfGranted(attendeeId, event.id);
  }, [rallyNoticeId, event?.id, attendeeId, saveRallyChoice]);

  const handleRallyDontCare = useCallback(async () => {
    const requestId = rallyNoticeId;
    if (!requestId) return;
    if (!(await saveRallyChoice(requestId, 'dontCare'))) return;
    setRallyNoticeId(null);
  }, [rallyNoticeId, saveRallyChoice]);

  const handleRallyEnableAlerts = useCallback(async () => {
    if (!event?.id || !attendeeId || attendeeId === 'pending-auth') return;
    const token = await requestPushPermission(attendeeId, event.id);
    if (token) {
      setRallyPushState('granted');
      toast.success('Alerts on');
      return;
    }
    const state = pushPermissionState();
    if (state === 'granted') {
      toast.error("Couldn't turn on alerts. Try again later.");
      setRallyPushState('granted');
    } else {
      setRallyPushState('denied');
    }
  }, [event?.id, attendeeId]);

  const openRallyDetail = useCallback((requestId: string) => {
    setRallyDetail({ id: requestId, reason: 'detail' });
  }, []);

  const wrapUpShowStop = event?.showStop ?? null;
  const eventEnded = event?.status === 'completed';
  useEffect(() => {
    if (!event?.id || wrapUpShownForRef.current) return;
    const seenKey = wrapUpSeenKey(event.id);
    let seen = false;
    try {
      seen = localStorage.getItem(seenKey) !== null;
    } catch (error) {
      console.warn('Could not read whether the wrap-up was seen:', error);
    }
    const otherModalOpen = showTriviaModal || showPredictionModal || showAwardModal;
    if (!shouldShowWrapUp({ showStop: wrapUpShowStop, ended: eventEnded, tookPart, seen, otherModalOpen })) return;
    try {
      localStorage.setItem(seenKey, new Date().toISOString());
    } catch (error) {
      console.warn('Could not remember that the wrap-up was seen:', error);
    }
    wrapUpShownForRef.current = true;
    setWrapUpInitialStep('thanks');
    setShowWrapUp(true);
  }, [event?.id, wrapUpShowStop, eventEnded, tookPart, showTriviaModal, showPredictionModal, showAwardModal]);

  // Whether this phone took part, once the event has ended (ITER-199): the guest check-in is the signal
  useEffect(() => {
    if (!event?.id || event.status !== 'completed' || !attendeeId || attendeeId === 'pending-auth') return;
    if (attendeeId === event.djId) {
      setTookPart('yes');
      return;
    }
    let cancelled = false;
    hasTakenPart(event.id, attendeeId)
      .then((took) => {
        if (!cancelled) setTookPart(took ? 'yes' : 'no');
      })
      .catch((error) => {
        console.error('Could not check whether this phone took part', error);
        if (!cancelled) setTookPartLookupFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [event?.id, event?.status, event?.djId, attendeeId]);

  useEffect(() => {
    if (!event?.settings?.superRequestEnabled) {
      setNominateMode(false);
    }
  }, [event?.settings?.superRequestEnabled]);

  // Show push notification banner after 10 seconds (if not already asked/enabled)
  useEffect(() => {
    if (isPushEnabled() || hasBeenAskedAboutPush()) return;
    const timer = setTimeout(() => setShowPushBanner(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  // Subscribe to attendee prizes
  useEffect(() => {
    if (!event?.id || !attendeeId) return;
    const unsubscribe = subscribeToAttendeePrizes(event.id, attendeeId, (prizes) => {
      setUnclaimedPrizes(prizes.filter(p => p.status === 'awarded'));
      // A roulette spotlight banner or an awards reveal already says "That's you!", so their shoutouts skip the overlay
      setActiveShoutouts(prizes.filter(p => p.type === 'shoutout' && p.status === 'awarded'
        && p.gameSource !== 'roulette' && p.gameSource !== 'awards'));
    });
    return () => unsubscribe();
  }, [event?.id, attendeeId]);

  // Subscribe to active prediction game
  useEffect(() => {
    if (!event?.id) return;
    const unsub = subscribeToPredictionGame(event.id, (game) => {
      setActivePredictionGame(game);
      if (game?.status === 'active' && !showPredictionModal) {
        setShowPredictionModal(true);
      }
      if (!game || game.status === 'completed' || game.status === 'cancelled') {
        setShowPredictionModal(false);
      }
    });
    return () => unsub();
  }, [event?.id]);

  // Subscribe to active trivia game
  useEffect(() => {
    if (!event?.id) return;
    const unsub = subscribeToTriviaGame(event.id, (game) => {
      setActiveTriviaGame(game);
      if (game && game.id !== dismissedTriviaGameId.current && !showTriviaModal) {
        setShowTriviaModal(true);
      }
      if (!game || game.status === 'completed' || game.status === 'cancelled') {
        setShowTriviaModal(false);
      }
    });
    return () => unsub();
  }, [event?.id]);

  // Subscribe to the latest dance-off: the vote pop-up opens once per dance-off and closes when voting ends
  useEffect(() => {
    if (!event?.id) return;
    const unsub = subscribeToLatestDanceOff(event.id, (danceOff) => {
      setGameBannerClock(Date.now());
      setLatestDanceOff(danceOff);
      setShowDanceOffModal(danceOff?.status === 'open' && danceOff.id !== dismissedDanceOffId.current);
    });
    return () => unsub();
  }, [event?.id]);

  // Subscribe to the event awards: the reveal pop-up opens once per new reveal, never on a plain re-render
  useEffect(() => {
    if (!event?.id) return;
    const seenKey = `votebeats_awards_seen_${event.id}`;
    if (seenAwardCount.current === null) {
      try {
        seenAwardCount.current = Number(sessionStorage.getItem(seenKey)) || 0;
      } catch (error) {
        console.warn('Could not read which awards were seen:', error);
        seenAwardCount.current = 0;
      }
    }
    const rememberSeen = (count: number) => {
      seenAwardCount.current = count;
      try {
        sessionStorage.setItem(seenKey, String(count));
      } catch (error) {
        console.warn('Could not remember which awards were seen:', error);
      }
    };
    const unsub = subscribeToEventAwards(event.id, (awards) => {
      setEventAwards(awards);
      if (!awards) {
        rememberSeen(0);
        setShowAwardModal(false);
        return;
      }
      const seen = seenAwardCount.current ?? 0;
      const marker = awards.revealed.length + 1;
      const watching = awards.status === 'revealing' || (awards.status === 'done' && seen > 0);
      if (watching && marker > seen) {
        rememberSeen(marker);
        setShowAwardModal(true);
      }
    });
    return () => unsub();
  }, [event?.id]);

  // Subscribe to the latest request roulette spotlight
  useEffect(() => {
    if (!event?.id) return;
    const unsub = subscribeToLatestRouletteRound(event.id, (round) => {
      setGameBannerClock(Date.now());
      setLatestRouletteRound(round);
    });
    return () => unsub();
  }, [event?.id]);

  // Re-render when a dance-off result or roulette spotlight runs out of its banner window
  useEffect(() => {
    const now = Date.now();
    const remaining = [
      msLeftInGameBannerWindow(latestRouletteRound?.drawnAt, now),
      latestDanceOff?.status === 'closed' ? msLeftInGameBannerWindow(latestDanceOff.closedAt, now) : 0,
    ].filter((ms) => ms > 0);
    if (remaining.length === 0) return;
    const timeout = setTimeout(() => setGameBannerClock(Date.now()), Math.min(...remaining) + 50);
    return () => clearTimeout(timeout);
  }, [latestRouletteRound, latestDanceOff, gameBannerClock]);

  // Vote close countdown timer effect
  useEffect(() => {
    if (!event?.settings?.votingCloseTime || event.settings.votingClosed) {
      setVoteCloseCountdown(null);
      setCountdownUrgency('normal');
      return;
    }

    const calculateCountdown = () => {
      const closeTime = new Date(event.settings.votingCloseTime!).getTime();
      const now = Date.now();
      const diff = closeTime - now;

      if (diff <= 0) {
        setVoteCloseCountdown(null);
        setCountdownUrgency('normal');
        return;
      }

      const totalHours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      // Determine urgency level
      if (minutes <= 5 && hours === 0 && days === 0) {
        setCountdownUrgency('last-call');
      } else if (totalHours <= 1) {
        setCountdownUrgency('urgent');
      } else {
        setCountdownUrgency('normal');
      }

      // Format countdown based on time remaining
      if (days > 0) {
        // For 72+ hours, show total hours for clarity (e.g., "72 hours")
        if (totalHours >= 72) {
          setVoteCloseCountdown(`${totalHours} hours`);
        } else {
          setVoteCloseCountdown(`${days}d ${hours}h`);
        }
      } else if (hours > 0) {
        setVoteCloseCountdown(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setVoteCloseCountdown(`${minutes}m ${seconds}s`);
      } else {
        setVoteCloseCountdown(`${seconds}s`);
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);

    return () => clearInterval(interval);
  }, [event?.settings?.votingCloseTime, event?.settings?.votingClosed]);

  useEffect(() => {
    // Load saved nickname or anonymous dismiss state
    const savedNickname = getAttendeeNickname();
    if (savedNickname) {
      setNickname(savedNickname);
      setIsEditingNickname(false);
    } else if (getAnonDismissed()) {
      setIsEditingNickname(false);
    }

    // Check if code word is set and generate QR URL if so
    setHasCodeWordSet(hasCodeWord());
    const existingHash = getCodeWordHash();
    if (existingHash && eventId) {
      const linkUrl = `${window.location.origin}/e/${eventId}?linkCode=${existingHash}`;
      setLinkQrUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(linkUrl)}`);
    }

    const loadEventData = async () => {
      try {
      // Get or create attendee identity (Firebase anonymous auth, or test slot if enabled)
      const urlParamsForTestUser = new URLSearchParams(window.location.search);
      const testUserParam = urlParamsForTestUser.get('testUser');
      const testSlot = testUserParam ? parseInt(testUserParam, 10) : null;

      let id: string;

      console.log('[TestIdentity] URL params:', window.location.search);
      console.log('[TestIdentity] testUserParam:', testUserParam, '| testSlot:', testSlot);
      console.log('[TestIdentity] isTestIdentityEnabled():', isTestIdentityEnabled());
      console.log('[TestIdentity] VITE_ENABLE_TEST_IDENTITIES:', import.meta.env.VITE_ENABLE_TEST_IDENTITIES);

      if (
        isTestIdentityEnabled() &&
        testSlot !== null &&
        !isNaN(testSlot) &&
        testSlot >= 1 &&
        testSlot <= 10
      ) {
        try {
          console.log('[TestIdentity] Calling signInAsTestUser for slot', testSlot);
          id = await signInAsTestUser(testSlot);
          console.log('[TestIdentity] SUCCESS — got UID:', id);
        } catch (err) {
          console.error('[TestIdentity] Failed to sign in as test user:', err);
          id = await getAttendeeIdAsync();
          console.log('[TestIdentity] FALLBACK — got UID:', id);
        }
      } else {
        console.log('[TestIdentity] SKIPPED — using normal attendee auth');
        id = await getAttendeeIdAsync();
        console.log('[TestIdentity] Normal auth UID:', id);
      }

      setAttendeeId(id);
        if (eventId) {
          // Try to find by URL slug first, then by ID
          let foundEvent = await getEventByUrl(eventId);
          if (!foundEvent) {
            foundEvent = await getEventById(eventId);
          }
          setEvent(foundEvent);

          if (foundEvent) {
            // Check on-site verification status
            if (foundEvent.settings.onSiteVerificationEnabled) {
              setVerificationRequired(true);

              // Check for ?verify= token in URL
              const urlParams = new URLSearchParams(window.location.search);
              const verifyToken = urlParams.get('verify');

              if (verifyToken) {
                // Attempt verification
                const result = await verifyAttendee(foundEvent.id, id, verifyToken);
                if (result.success) {
                  setIsVerified(true);
                  setVerifiedCache(foundEvent.id, true);
                  toast.success(result.message);
                } else {
                  toast.error(result.message);
                }
                // Clean URL by removing ?verify= param
                window.history.replaceState({}, '', window.location.pathname);
              } else {
                // Check localStorage cache first, then Firestore
                const cachedVerified = getVerifiedCache(foundEvent.id);
                if (cachedVerified) {
                  setIsVerified(true);
                } else {
                  const verified = await isAttendeeVerified(foundEvent.id, id);
                  if (verified) {
                    setIsVerified(true);
                    setVerifiedCache(foundEvent.id, true);
                  }
                }
              }
            } else {
              setVerificationRequired(false);
            }
            setVerificationChecked(true);

            // Check for ?linkCode= param (QR-based cross-device linking)
            {
              const urlParams = new URLSearchParams(window.location.search);
              const linkCode = urlParams.get('linkCode');
              if (linkCode) {
                try {
                  const registryRef = doc(db, 'codeWordRegistry', linkCode);
                  const snap = await getDoc(registryRef);
                  if (snap.exists()) {
                    const linkedId = snap.data().attendeeId;
                    if (linkedId !== id) {
                      localStorage.setItem('votebeats_attendee_id', linkedId);
                      localStorage.setItem('votebeats_code_word_hash', linkCode);
                      toast.success('Device linked successfully!');
                      window.location.replace(window.location.pathname);
                      return;
                    } else {
                      toast.success('Already linked to this identity');
                    }
                  } else {
                    toast.error('Link code not found');
                  }
                } catch {
                  toast.error('Failed to link device');
                }
                window.history.replaceState({}, '', window.location.pathname);
              }
            }

            // Get queued requests for this event, sorted by votes (highest first)
            const allEventRequests = await getRequestsByEventId(foundEvent.id);
            const eventRequests = allEventRequests
              .filter(r => r.status === 'queued')
              .filter(r => r.visibleToAttendees !== false)
              .sort((a, b) => b.voteCount - a.voteCount);
            setRequests(eventRequests);
            // Initialize static order for vote tab (frozen display order)
            staticOrderRef.current = eventRequests.map(r => r.id);
            // Load played requests for status indicators
            const played = allEventRequests
              .filter(r => r.status === 'played')
              .sort((a, b) => {
                const aTime = a.timestamps?.finishedPlaying || a.timestamps?.startedPlaying || a.createdAt;
                const bTime = b.timestamps?.finishedPlaying || b.timestamps?.startedPlaying || b.createdAt;
                return new Date(bTime).getTime() - new Date(aTime).getTime();
              });
            setPlayedRequests(played);

            // Check which requests the attendee has voted for (parallel)
            const votedResults = await Promise.all(
              eventRequests.map(req => getVoteStatus(id, req.id).then(v => ({ id: req.id, ...v })))
            );
            const voted = new Map<string, VoteType>();
            votedResults.forEach(r => {
              if (r.voted && r.type) voted.set(r.id, r.type);
            });
            setVotedRequests(voted);

            // Pre-compute trending status for all requests (parallel)
            const trendingResults = await Promise.all(
              eventRequests.map(req => isTrending(req.id).then(t => ({ id: req.id, trending: t })))
            );
            const trending = new Set<string>();
            trendingResults.forEach(r => {
              if (r.trending) trending.add(r.id);
            });
            setTrendingIds(trending);

            // Get DJ messages for this event
            try {
              const messages = await getDJMessagesForAttendees(foundEvent.id);
              setDJMessages(messages);
            } catch (msgErr) {
              console.error('Failed to load messages:', msgErr);
            }

            // Get now playing song
            try {
              const playing = await getNowPlaying(foundEvent.id);
              setNowPlaying(playing);
            } catch {
              // Non-critical — now playing will update via polling
            }

            // Get attendee's own requests (all statuses) for tracking
            const attendeeRequests = allEventRequests.filter(r => r.requestedBy.oderId === id);
            setMyRequests(attendeeRequests);

            // One feedback doc per attendee per event (ITER-199), readable by its author
            try {
              const feedback = await getAttendeeFeedback(foundEvent.id, id);
              if (feedback) {
                setFeedbackSubmitted(true);
                setExistingFeedback(feedback);
                setFeedbackRating(feedback.rating);
                setFeedbackMessage(feedback.message);
              }
            } catch (feedbackError) {
              console.error('Could not check for earlier feedback', feedbackError);
            }

            // Load ranked-choice voting data if in ranked mode
            if (foundEvent.settings.queueMode === 'ranked') {
              const queuedIds = new Set(eventRequests.map(r => r.id));
              // Load seen songs first (needed for unranked init)
              let seen = new Set<string>();
              try {
                seen = await getSeenSongs(foundEvent.id, id);
                setSeenSongIds(seen);
              } catch (seenErr) {
                console.error('Failed to load seen songs:', seenErr);
              }
              // Load the saved order (full order on the server; older docs only have the ranked list)
              let storedOrder: string[] = [];
              let hasFullOrder = false;
              try {
                const existingRanking = await getAttendeeRanking(foundEvent.id, id);
                if (existingRanking) {
                  hasFullOrder = Array.isArray(existingRanking.fullOrder);
                  storedOrder = existingRanking.fullOrder ?? existingRanking.rankings ?? [];
                  setSortedThroughId(resolveDivider(storedOrder, existingRanking.sortedThroughId, queuedIds));
                }
              } catch (rankErr) {
                console.error('Failed to load saved ranking:', rankErr);
              }
              // Docs saved before the full order existed: carry over the unranked list this browser kept
              if (!hasFullOrder) {
                try {
                  const stored = localStorage.getItem(`votebeats_unranked_${foundEvent.id}_${id}`);
                  if (stored) {
                    const parsed = JSON.parse(stored);
                    if (Array.isArray(parsed)) storedOrder = [...storedOrder, ...parsed.filter((rid): rid is string => typeof rid === 'string')];
                  }
                } catch (storageErr) {
                  console.error('Failed to read the old unranked list:', storageErr);
                }
              }
              const loadedOrder: string[] = [];
              for (const rid of storedOrder) {
                if (queuedIds.has(rid) && !loadedOrder.includes(rid)) loadedOrder.push(rid);
              }
              // Any seen songs not yet placed → end of the order
              for (const seenId of seen) {
                if (queuedIds.has(seenId) && !loadedOrder.includes(seenId)) loadedOrder.push(seenId);
              }
              const loadedDownvotes = new Set([...voted].filter(([, type]) => type === 'down').map(([rid]) => rid));
              setMyOrder(partitionDownvoted(loadedOrder, loadedDownvotes));
            }
          }
        }
      } catch (err) {
        console.error('Error loading event data:', err);
      } finally {
        setLoading(false);
        // Auto-show tutorial on first visit
        const tutorialDone = localStorage.getItem('votebeats_tutorial_done')
          || localStorage.getItem('votebeats_attendee_tutorial_seen'); // migrate existing users
        if (false && !tutorialDone) {
          setTimeout(() => setTutorialMode('consent'), 1500);
        }
      }
    };

    loadEventData();
  }, [eventId]);

  // Session tracking for analytics
  useEffect(() => {
    if (!event?.id || !attendeeId) return;
    let sessionId: string | null = null;

    const startTracking = async () => {
      try {
        const { startSession } = await import('../../services/analytics');
        sessionId = await startSession(event.id, attendeeId);
      } catch {
        // Session tracking is non-critical; silently fail
      }
    };
    startTracking();

    const handleEnd = () => {
      if (sessionId) {
        import('../../services/analytics').then(({ endSession }) => {
          endSession(event!.id, sessionId!).catch(() => {});
        });
      }
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handleEnd();
    });
    window.addEventListener('beforeunload', handleEnd);

    return () => {
      handleEnd();
      window.removeEventListener('beforeunload', handleEnd);
    };
  }, [event?.id, attendeeId]);

  // Guest check-in for the beta event-size gate (ITER-099). Only anonymous attendees count, never the DJ.
  // Skipped once the event has ended, so a first visit afterwards doesn't count as taking part (ITER-199)
  useEffect(() => {
    if (!event?.id || !attendeeId || attendeeId === 'pending-auth' || attendeeId === event.djId) return;
    if (event.status === 'completed') return;
    if (auth.currentUser?.uid !== attendeeId || !auth.currentUser.isAnonymous) return;
    recordEventGuest(event.id, attendeeId).catch((error) => console.error('Guest check-in failed', error));
  }, [event?.id, event?.djId, event?.status, attendeeId]);

  // Real-time polling for vote updates (simulates Firestore real-time sync)
  // Real-time listener for request changes (replaces 2-second poll for request data)
  // After the initial snapshot, only changed documents are transferred (delta reads)
  useEffect(() => {
    if (!event?.id) return;
    const unsub = subscribeToRequestsByEventId(event.id, (allRequests) => {
      allRequestsRef.current = allRequests;
      const eventRequests = allRequests
        .filter(r => r.status === 'queued')
        .filter(r => r.visibleToAttendees !== false)
        .sort((a, b) => b.voteCount - a.voteCount);
      setRequests(eventRequests);
      setMyRequests(allRequests.filter(r => r.requestedBy.oderId === attendeeId));

      // Update nominations (pending super requests) for People's Pick tab
      setNominations(allRequests.filter(r => r.superRequest && r.status === 'pending' && r.poolApproved));
      setMyPendingNominations(allRequests.filter(r => r.superRequest && r.status === 'pending' && !r.poolApproved && r.requestedBy.oderId === attendeeId));
      setUniqueAttendeeCount(new Set(allRequests.map(r => r.requestedBy.oderId)).size);

      // Maintain static order: keep existing order, append new, remove deleted
      const newIds = new Set(eventRequests.map(r => r.id));
      const kept = staticOrderRef.current.filter(id => newIds.has(id));
      const added = eventRequests.filter(r => !kept.includes(r.id)).map(r => r.id);
      staticOrderRef.current = [...kept, ...added];

      // New song notification (in onSnapshot to fire once per batch, not per re-render)
      if (previousRequestIdsRef.current.size > 0) {
        const currentIds = new Set(eventRequests.map(r => r.id));
        const newSongCount = [...currentIds].filter(id => !previousRequestIdsRef.current.has(id)).length;
        if (newSongCount > 0 && event.settings.queueMode === 'ranked') {
          toast(
            (t) => (
              <span
                onClick={() => { setActiveTab('rankings'); toast.dismiss(t.id); }}
                className="cursor-pointer"
              >
                {newSongCount} new song{newSongCount > 1 ? 's' : ''} added! Tap to rank.
              </span>
            ),
            { icon: '🎵', duration: 6000 }
          );
        }
        previousRequestIdsRef.current = currentIds;
      } else {
        previousRequestIdsRef.current = new Set(eventRequests.map(r => r.id));
      }

      // Refresh played requests
      const played = allRequests
        .filter(r => r.status === 'played')
        .sort((a, b) => {
          const aTime = a.timestamps?.finishedPlaying || a.timestamps?.startedPlaying || a.createdAt;
          const bTime = b.timestamps?.finishedPlaying || b.timestamps?.startedPlaying || b.createdAt;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        });
      setPlayedRequests(played);
    });
    return () => unsub();
  }, [event?.id]);

  // Periodic refresh for data that requires separate collection reads (every 10 seconds)
  useEffect(() => {
    if (!event?.id) return;
    let pollCount = 0;

    const pollInterval = setInterval(async () => {
      pollCount++;

      // Refresh event settings every ~30 seconds to detect verification mode changes
      if (pollCount % 3 === 0) {
        const refreshedEvent = await getEventById(event.id);
        if (!refreshedEvent) {
          setEvent(null);
          return;
        }
        setEvent(refreshedEvent);
        if (refreshedEvent.settings.onSiteVerificationEnabled) {
          setVerificationRequired(true);
        } else {
          setVerificationRequired(false);
        }
        // Refresh active polls + showToNewUsers polls
        const [freshActive, freshSticky] = await Promise.all([
          getActivePollsForEvent(event.id),
          getShowToNewUsersPollsForEvent(event.id),
        ]);
        const merged = new Map<string, Poll>();
        for (const p of freshActive) merged.set(p.id, p);
        for (const p of freshSticky) merged.set(p.id, p);
        const freshPolls = Array.from(merged.values());
        setActivePolls(freshPolls);
        if (attendeeId) {
          const answered = new Set<string>();
          const prevAnswers = new Map<string, PollAnswer[]>();
          for (const p of freshPolls) {
            if (answeredPollIds.has(p.id)) {
              answered.add(p.id);
              if (p.editable) {
                const response = await getPollResponse(p.id, attendeeId);
                if (response) prevAnswers.set(p.id, response.answers);
              }
            } else {
              const response = await getPollResponse(p.id, attendeeId);
              if (response) {
                answered.add(p.id);
                if (p.editable) prevAnswers.set(p.id, response.answers);
              }
            }
          }
          setAnsweredPollIds(answered);
          setPreviousAnswersMap(prevAnswers);
        }
      }

      // Update now playing
      const playing = await getNowPlaying(event.id);
      setNowPlaying(playing);
    }, 10000); // Poll every 10 seconds for supplementary data

    return () => clearInterval(pollInterval);
  }, [event?.id, attendeeId]);

  // Persist active tab to sessionStorage so it survives browser refresh
  useEffect(() => {
    sessionStorage.setItem('votebeats_active_tab', activeTab);
  }, [activeTab]);

  // Dynamic manifest for A2HS — set event-specific start_url and name
  useEffect(() => {
    if (!event) return;
    const manifest = {
      short_name: event.name,
      name: event.name + ' - VoteBeats',
      icons: [{ src: '/vite.svg', sizes: 'any', type: 'image/svg+xml' }],
      start_url: '/e/' + (event.eventUrl || event.id),
      display: 'standalone' as const,
      theme_color: '#6366F1',
      background_color: '#ffffff',
      gcm_sender_id: '103953800507',
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    const originalHref = link?.href;
    if (link) link.href = url;
    // Also update apple-mobile-web-app-title for iOS
    let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement | null;
    if (!appleMeta) {
      appleMeta = document.createElement('meta');
      appleMeta.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleMeta);
    }
    appleMeta.content = event.name;
    return () => {
      URL.revokeObjectURL(url);
      if (link && originalHref) link.href = originalHref;
    };
  }, [event?.id, event?.name, event?.eventUrl]);

  // Save scroll position before unload (preserves position across verification reload)
  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem('votebeats_scroll_y', String(window.scrollY));
    };
    window.addEventListener('beforeunload', saveScroll);
    return () => window.removeEventListener('beforeunload', saveScroll);
  }, []);

  // Restore scroll position after load (e.g. after verification QR scan reload)
  useEffect(() => {
    if (!loading) {
      const savedY = sessionStorage.getItem('votebeats_scroll_y');
      if (savedY) {
        requestAnimationFrame(() => window.scrollTo(0, Number(savedY)));
        sessionStorage.removeItem('votebeats_scroll_y');
      }
    }
  }, [loading]);

  // Idle detection: show refresh CTA after 2 minutes of no interaction
  useEffect(() => {
    const handleInteraction = () => {
      lastInteractionRef.current = Date.now();
      if (Date.now() - idleCTAShownAtRef.current > 120000) {
        idleCTAShownRef.current = false;
      }
    };
    document.addEventListener('touchstart', handleInteraction);
    document.addEventListener('click', handleInteraction);

    const idleCheck = setInterval(() => {
      const idleMs = Date.now() - lastInteractionRef.current;
      if (idleMs > 120000 && !idleCTAShownRef.current) {
        idleCTAShownRef.current = true;
        idleCTAShownAtRef.current = Date.now();
        toast(
          (t) => (
            <span
              onClick={() => { window.location.reload(); toast.dismiss(t.id); }}
              className="cursor-pointer"
            >
              Tap to refresh the latest data
            </span>
          ),
          { icon: '🔄', duration: 10000 }
        );
      }
    }, 30000);

    return () => {
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
      clearInterval(idleCheck);
    };
  }, []);

  // Manual refresh handler — re-fetches all data without changing active tab
  const handleManualRefresh = useCallback(async () => {
    if (!event?.id || isRefreshing) return;
    setIsRefreshing(true);
    lastInteractionRef.current = Date.now();
    if (Date.now() - idleCTAShownAtRef.current > 120000) {
      idleCTAShownRef.current = false;
    }
    try {
      const refreshedEvent = await getEventById(event.id);
      if (!refreshedEvent) {
        setEvent(null);
        return;
      }
      setEvent(refreshedEvent);

      const allRequests = await getRequestsByEventId(event.id);
      const eventRequests = allRequests.filter(r => r.status === 'queued').filter(r => r.visibleToAttendees !== false).sort((a, b) => b.voteCount - a.voteCount);
      setRequests(eventRequests);
      setNominations(allRequests.filter(r => r.superRequest && r.status === 'pending' && r.poolApproved));
      setMyPendingNominations(allRequests.filter(r => r.superRequest && r.status === 'pending' && !r.poolApproved && r.requestedBy.oderId === attendeeId));

      const playing = await getNowPlaying(event.id);
      setNowPlaying(playing);

      const attendeeRequests = allRequests.filter(r => r.requestedBy.oderId === attendeeId);
      setMyRequests(attendeeRequests);

      toast.success('Refreshed');
    } catch {
      toast.error('Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  }, [event?.id, attendeeId, isRefreshing]);

  // Voting open countdown (when scheduled to open in future)
  useEffect(() => {
    if (!event?.settings?.votingScheduledToOpen || !event?.settings?.votingOpenTime) {
      setVoteOpenCountdown(null);
      return;
    }

    const calculateOpenCountdown = () => {
      const openTime = new Date(event.settings.votingOpenTime!.replace('Z', '')).getTime();
      const now = Date.now();
      const diff = openTime - now;

      if (diff <= 0) {
        setVoteOpenCountdown(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        const openDate = new Date(openTime);
        const dayName = DAY_ABBR[openDate.getDay()];
        const h = openDate.getHours();
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        const m = String(openDate.getMinutes()).padStart(2, '0');
        setVoteOpenCountdown(`${dayName} at ${h12}:${m} ${ampm}`);
      } else if (hours > 0) {
        setVoteOpenCountdown(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setVoteOpenCountdown(`${minutes}m ${seconds}s`);
      } else {
        setVoteOpenCountdown(`${seconds}s`);
      }
    };

    calculateOpenCountdown();
    const interval = setInterval(calculateOpenCountdown, 1000);
    return () => clearInterval(interval);
  }, [event?.settings?.votingScheduledToOpen, event?.settings?.votingOpenTime]);

  // Check if event is in ranked-choice mode
  const isRankedMode = event?.settings?.queueMode === 'ranked';

  // Determine which tabs to show — Vote and Rank are mutually exclusive
  const showVoteTab = event?.settings?.attendeeVoteTab
    ?? (event?.settings?.queueMode !== 'ranked' && event?.settings?.queueMode !== 'fifo');
  const showRankTab = !showVoteTab && event?.settings?.queueMode !== 'fifo';
  const attendeeVotingStyle: AttendeeVotingStyle = showRankTab ? 'rank' : showVoteTab ? 'vote' : 'none';
  const showPeoplesPickTab = PEOPLES_PICK_HIDDEN ? false : (event?.settings?.superRequestEnabled ?? false);

  // Load PP upvote state when event and attendee are ready
  useEffect(() => {
    if (!event?.id || !attendeeId || !event?.settings?.superRequestEnabled) return;
    (async () => {
      const upvoted = await getUpvotedGroupKeysByAttendee(event.id, attendeeId);
      setUpvotedGroupKeys(upvoted);
      setDismissedGroupKeys(getDismissedGroupKeys(event.id));
    })();
    const unsub = subscribeToUpvoteCountsForEvent(event.id, (counts) => {
      setUpvoteCounts(counts);
    });
    return unsub;
  }, [event?.id, attendeeId, event?.settings?.superRequestEnabled]);

  // Group nominations by song for the People's Pick tab
  const nominationGroups = React.useMemo(() => {
    if (!nominations.length || !event?.settings?.superRequestEnabled) return [];
    const threshold = event.settings.superRequestThresholdType === 'percentage'
      ? Math.max(2, Math.ceil((event.settings.superRequestThresholdPercent ?? 10) / 100 * Math.max(1, uniqueAttendeeCount)))
      : (event.settings.superRequestThresholdCount ?? 5);
    const groups: Array<{
      title: string;
      artist: string;
      albumArtUrl?: string;
      count: number;
      threshold: number;
      primaryRequestId: string;
      requestIds: string[];
      groupKey: string;
      hasUpvoted: boolean;
      hasNominated: boolean;
    }> = [];
    for (const req of nominations) {
      const groupKey = makePPGroupKey(req.song.title, req.song.artist);
      if (dismissedGroupKeys.has(groupKey)) continue;
      const existing = groups.find(g => g.groupKey === groupKey);
      if (existing) {
        existing.requestIds.push(req.id);
        if (req.requestedBy.oderId === attendeeId) existing.hasNominated = true;
      } else {
        const submitterCount = nominations.filter(r => makePPGroupKey(r.song.title, r.song.artist) === groupKey).length;
        const upvoteCount = upvoteCounts.get(groupKey) ?? submitterCount;
        groups.push({
          title: req.song.title,
          artist: req.song.artist,
          albumArtUrl: req.song.albumArtUrl,
          count: upvoteCount,
          threshold,
          primaryRequestId: req.id,
          requestIds: [req.id],
          groupKey,
          hasUpvoted: upvotedGroupKeys.has(groupKey),
          hasNominated: req.requestedBy.oderId === attendeeId,
        });
      }
    }
    return groups.sort((a, b) => b.count - a.count);
  }, [nominations, event, attendeeId, upvoteCounts, upvotedGroupKeys, dismissedGroupKeys, uniqueAttendeeCount]);

  const ppBadgeCount = nominationGroups.filter(g => !g.hasUpvoted && !dismissedGroupKeys.has(g.groupKey)).length;

  // Redirect to Request tab if current tab gets hidden
  useEffect(() => {
    if (activeTab === 'browse' && !showVoteTab) setActiveTab('request');
    if (activeTab === 'rankings' && !showRankTab) setActiveTab('request');
    if (activeTab === 'peoples-pick' && !showPeoplesPickTab) setActiveTab('request');
  }, [showVoteTab, showRankTab, showPeoplesPickTab, activeTab]);

  // Request status tracking (F19) — uses preloaded subscriber data (no extra Firestore reads)
  // Refreshes every 5 seconds using allRequestsRef from the real-time listener
  useEffect(() => {
    if (!event?.id || !attendeeId) return;

    const updateRequestStatus = async () => {
      const preloaded = allRequestsRef.current;
      const isPostClose = event.settings.votingClosed && event.settings.votingClosedAt;
      const count = isPostClose && event.settings.votingClosedAt
        ? await countRequestsByAttendeeAfter(event.id, attendeeId, event.settings.votingClosedAt, preloaded)
        : await countRequestsByAttendee(event.id, attendeeId, preloaded);
      const max = isPostClose
        ? (event.settings.postCloseRequestLimit ?? 2)
        : (event.settings.maxRequestsPerUser ?? 5);
      setRequestStatusCount(count);
      setRequestStatusMax(max);

      // Track nomination count if People's Choice is enabled
      if (event.settings.superRequestEnabled) {
        const nomCount = await countNominationsByAttendee(event.id, attendeeId, preloaded);
        setNominationStatusCount(nomCount);
        setNominationStatusMax(event.settings.maxNominationsPerUser ?? 0);
      }

      const cooldownMinutes = event.settings.cooldownMinutes ?? 0;
      if (cooldownMinutes > 0) {
        const { remainingSeconds } = await isInCooldown(event.id, attendeeId, cooldownMinutes, preloaded);
        setCooldownRemaining(remainingSeconds);
      } else {
        setCooldownRemaining(0);
      }
    };

    updateRequestStatus();
    const interval = setInterval(updateRequestStatus, 5000);
    return () => clearInterval(interval);
  }, [event?.id, attendeeId, event?.settings?.votingClosed, event?.settings?.votingClosedAt, event?.settings?.maxRequestsPerUser, event?.settings?.cooldownMinutes, event?.settings?.postCloseRequestLimit, event?.settings?.superRequestEnabled, event?.settings?.maxNominationsPerUser]);

  // Helper: check if voting is blocked by schedule
  const isVotingScheduleBlocked = (): boolean => {
    if (!event) return false;
    return isVotingBlockedBySchedule(event.settings, Date.now());
  };

  // Check if requests are specifically closed (not just voting)
  const isRequestsClosed = (): boolean => {
    if (!event) return false;
    return isRequestsClosedForAttendee(event.settings, Date.now());
  };

  // The rules refuse a request once requests close (ITER-137); explain it from a fresh copy, not this page's stale one
  const explainClosedRequestRefusal = async (error: unknown): Promise<boolean> => {
    if (!event || !(error instanceof FirebaseError) || error.code !== 'permission-denied') return false;
    try {
      const freshEvent = await getEventById(event.id);
      if (!freshEvent || !areRequestsClosed(freshEvent.settings, Date.now())) return false;
      setEvent(freshEvent);
      toast.error(requestsJustClosedMessage(attendeeVotingStyle), { duration: 8000 });
      return true;
    } catch (rereadError) {
      console.error('Failed to re-read the event after a refused request:', rereadError);
      return false;
    }
  };

  // Check if Super Request mode is active
  // Activates when People's Choice is enabled AND any of:
  // 1. Requests are closed by DJ
  // 2. User has hit their per-user request limit
  // 3. Queue is full
  const isSuperRequestMode = (): boolean => {
    if (PEOPLES_PICK_HIDDEN) return false;
    if (!(event?.settings?.superRequestEnabled ?? false)) return false;
    if (nominateMode) return true;
    if (isRequestsClosed()) return true;
    if (requestStatusCount >= requestStatusMax) return true;
    const maxQueue = event?.settings?.maxQueueSize ?? 50;
    const queuedCount = requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying').length;
    if (queuedCount >= maxQueue) return true;
    return false;
  };

  // Why is super request mode active? (for context-sensitive messaging)
  const getSuperRequestReason = (): 'closed' | 'limit' | 'queueFull' | 'manual' | null => {
    if (!(event?.settings?.superRequestEnabled ?? false)) return null;
    if (nominateMode) return 'manual';
    if (isRequestsClosed()) return 'closed';
    if (requestStatusCount >= requestStatusMax) return 'limit';
    const maxQueue = event?.settings?.maxQueueSize ?? 50;
    const queuedCount = requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying').length;
    if (queuedCount >= maxQueue) return 'queueFull';
    return null;
  };

  // --- Doom Voting (vote rate limiting) ---
  const getVoteLog = (): number[] => {
    if (!event?.id) return [];
    try {
      const raw = localStorage.getItem(`votebeats_vote_log_${event.id}`);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };
  const recordVoteTimestamp = () => {
    if (!event?.id) return;
    const log = getVoteLog();
    log.push(Date.now());
    // Keep only last 100 entries to avoid localStorage bloat
    const trimmed = log.slice(-100);
    localStorage.setItem(`votebeats_vote_log_${event?.id}`, JSON.stringify(trimmed));
  };
  const checkDoomVoting = (): boolean => {
    if (!event?.settings?.doomVotingEnabled) return false;
    // Check if in cooldown
    const cooldownKey = `votebeats_vote_cooldown_${event?.id}`;
    const storedCooldown = localStorage.getItem(cooldownKey);
    if (storedCooldown) {
      const cooldownExpiry = parseInt(storedCooldown, 10);
      if (Date.now() < cooldownExpiry) {
        const remaining = Math.ceil((cooldownExpiry - Date.now()) / 1000);
        setVoteCooldownUntil(cooldownExpiry);
        toast.error(`Slow down! Take a break. You can vote again in ${remaining}s`, { icon: '⏳' });
        return true;
      } else {
        localStorage.removeItem(cooldownKey);
        setVoteCooldownUntil(0);
        setVoteCooldownDisplay(0);
      }
    }
    // Count votes in the active window
    const maxVotes = event.settings.doomVotingMaxVotes ?? 10;
    const activeMinutes = event.settings.doomVotingActiveMinutes ?? 3;
    const restMinutes = event.settings.doomVotingRestMinutes ?? 5;
    const windowMs = activeMinutes * 60 * 1000;
    const log = getVoteLog();
    const now = Date.now();
    const recentVotes = log.filter(t => now - t < windowMs);
    if (recentVotes.length >= maxVotes) {
      // Trigger cooldown
      const cooldownExpiry = now + restMinutes * 60 * 1000;
      localStorage.setItem(cooldownKey, cooldownExpiry.toString());
      setVoteCooldownUntil(cooldownExpiry);
      toast.error(`You're voting too fast! Take a ${restMinutes}-minute break.`, { icon: '⏳', duration: 5000 });
      return true;
    }
    return false;
  };

  // Doom voting cooldown countdown timer
  useEffect(() => {
    if (voteCooldownUntil <= 0) return;
    const tick = () => {
      const remaining = Math.ceil((voteCooldownUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setVoteCooldownUntil(0);
        setVoteCooldownDisplay(0);
        const cooldownKey = `votebeats_vote_cooldown_${event?.id}`;
        localStorage.removeItem(cooldownKey);
      } else {
        setVoteCooldownDisplay(remaining);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [voteCooldownUntil, event?.id]);

  // On mount: check for existing cooldown
  useEffect(() => {
    if (!event?.id) return;
    const cooldownKey = `votebeats_vote_cooldown_${event.id}`;
    const stored = localStorage.getItem(cooldownKey);
    if (stored) {
      const expiry = parseInt(stored, 10);
      if (Date.now() < expiry) {
        setVoteCooldownUntil(expiry);
      } else {
        localStorage.removeItem(cooldownKey);
      }
    }
  }, [event?.id]);

  // Load active polls + showToNewUsers polls
  useEffect(() => {
    if (!event?.id || !attendeeId) return;
    (async () => {
      // Fetch both active polls and "show to new users" polls, merge/deduplicate
      const [activeList, stickyList] = await Promise.all([
        getActivePollsForEvent(event.id),
        getShowToNewUsersPollsForEvent(event.id),
      ]);
      const merged = new Map<string, Poll>();
      for (const p of activeList) merged.set(p.id, p);
      for (const p of stickyList) merged.set(p.id, p);
      const allPolls = Array.from(merged.values());
      setActivePolls(allPolls);

      // Check which polls we've already answered, load previous answers for editable ones
      const answered = new Set<string>();
      const prevAnswers = new Map<string, PollAnswer[]>();
      for (const poll of allPolls) {
        const response = await getPollResponse(poll.id, attendeeId);
        if (response) {
          answered.add(poll.id);
          if (poll.editable) {
            prevAnswers.set(poll.id, response.answers);
          }
        }
      }
      setAnsweredPollIds(answered);
      setPreviousAnswersMap(prevAnswers);

      // Load seen resend timestamps from localStorage
      try {
        const stored = localStorage.getItem(`votebeats_poll_resend_seen_${event.id}`);
        if (stored) setSeenResendTimestamps(new Map(JSON.parse(stored)));
      } catch { /* ignore */ }
    })();
  }, [event?.id, attendeeId]);

  const handleVote = async (requestId: string, voteType: VoteType = 'up'): Promise<boolean> => {
    if (!attendeeId) return false;

    // Check doom voting cooldown
    if (checkDoomVoting()) return false;

    // Check voting schedule
    if (isVotingScheduleBlocked()) {
      if (event?.settings?.votingScheduledToOpen && event?.settings?.votingOpenTime) {
        const openTime = new Date(event.settings.votingOpenTime.replace('Z', ''));
        toast.error(`Voting opens at ${openTime.toLocaleString()}`, { icon: '🔒' });
      } else {
        toast.error('Voting is currently closed', { icon: '🔒' });
      }
      return false;
    }

    // Check on-site verification
    if (verificationRequired && !isVerified) {
      toast.error('Scan the on-site QR code at the venue to vote', { icon: '🔒' });
      return false;
    }

    // Check if the song is locked (now playing or already played)
    const freshRequest = await getRequestById(requestId);
    const isNowPlayingState = nowPlaying && nowPlaying.id === requestId;
    const isLocked = isNowPlayingState ||
      (freshRequest && (freshRequest.status === 'nowPlaying' || freshRequest.status === 'played'));

    if (isLocked) {
      toast.error('Voting is closed for this song', { icon: '🔒' });
      return false;
    }

    // Optimistic update
    const currentType = votedRequests.get(requestId);
    const prevVotedRequests = new Map(votedRequests);
    const newVotedRequests = new Map(votedRequests);

    // Compute optimistic count changes
    let voteCountDelta = 0;
    let upvoteCountDelta = 0;
    let downvoteCountDelta = 0;

    if (currentType === voteType) {
      // Same type — toggle off
      newVotedRequests.delete(requestId);
      if (voteType === 'up') { voteCountDelta = -1; upvoteCountDelta = -1; }
      else { voteCountDelta = 1; downvoteCountDelta = -1; }
    } else if (currentType && currentType !== voteType) {
      // Different type — switch
      newVotedRequests.set(requestId, voteType);
      if (voteType === 'up') { voteCountDelta = 2; upvoteCountDelta = 1; downvoteCountDelta = -1; }
      else { voteCountDelta = -2; upvoteCountDelta = -1; downvoteCountDelta = 1; }
    } else {
      // No existing vote — new vote
      newVotedRequests.set(requestId, voteType);
      if (voteType === 'up') { voteCountDelta = 1; upvoteCountDelta = 1; }
      else { voteCountDelta = -1; downvoteCountDelta = 1; }
    }

    setVotedRequests(newVotedRequests);

    const prevRequests = requests;
    setRequests(prev =>
      prev.map(r => {
        if (r.id !== requestId) return r;
        return {
          ...r,
          voteCount: r.voteCount + voteCountDelta,
          upvoteCount: r.upvoteCount + upvoteCountDelta,
          downvoteCount: r.downvoteCount + downvoteCountDelta,
        };
      })
    );

    try {
      await voteForRequest(attendeeId, requestId, voteType);
      if (isRankedMode) moveForDownvoteChange(requestId, currentType === 'down', newVotedRequests);
      // Record vote timestamp for doom voting tracking
      if (event?.settings?.doomVotingEnabled) {
        recordVoteTimestamp();
      }
      return true;
    } catch (error) {
      // Revert on error
      setVotedRequests(prevVotedRequests);
      setRequests(prevRequests);
      toast.error('Failed to vote');
      return false;
    }
  };

  // Handle song flag submission
  const handleFlagSubmit = async () => {
    if (!flagModal || !attendeeId || !event) return;
    if (flagReason.trim().length < 10) {
      toast.error('Please provide a reason (at least 10 characters)');
      return;
    }
    setFlagSubmitting(true);
    try {
      await flagSong(event.id, flagModal.requestId, flagModal.songTitle, flagModal.songArtist, flagReason.trim(), attendeeId);
      const updated = new Set(flaggedRequestIds);
      updated.add(flagModal.requestId);
      setFlaggedRequestIds(updated);
      localStorage.setItem('votebeats_flagged_songs', JSON.stringify([...updated]));
      toast.success('Flag submitted for DJ review');
      setFlagModal(null);
      setFlagReason('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit flag');
    } finally {
      setFlagSubmitting(false);
    }
  };

  // Tutorial handlers
  const handleTutorialClose = () => {
    setTutorialMode(null);
    setTutorialHelpTopic(null);
  };
  const handleTutorialPermanentDismiss = () => {
    localStorage.setItem('votebeats_tutorial_done', 'true');
    setTutorialMode(null);
    setTutorialHelpTopic(null);
  };
  const handlePPUpvote = async (groupKey: string, primaryRequestId: string, title: string, artist: string) => {
    if (!event || !attendeeId) return;
    const isUpvoted = upvotedGroupKeys.has(groupKey);
    if (isUpvoted) {
      await removeNominationUpvote(event.id, groupKey, attendeeId);
      setUpvotedGroupKeys(prev => { const n = new Set(prev); n.delete(groupKey); return n; });
      setUpvoteCounts(prev => { const n = new Map(prev); n.set(groupKey, Math.max(0, (n.get(groupKey) ?? 1) - 1)); return n; });
      return;
    }
    await upvoteNomination(event.id, groupKey, attendeeId, primaryRequestId);
    setUpvotedGroupKeys(prev => new Set([...prev, groupKey]));
    const newCount = (upvoteCounts.get(groupKey) ?? 0) + 1;
    setUpvoteCounts(prev => { const n = new Map(prev); n.set(groupKey, newCount); return n; });

    // Check threshold
    const threshold = event.settings.superRequestThresholdType === 'percentage'
      ? Math.max(2, Math.ceil((event.settings.superRequestThresholdPercent ?? 10) / 100 * Math.max(1, uniqueAttendeeCount)))
      : (event.settings.superRequestThresholdCount ?? 5);
    if (newCount >= threshold) {
      try {
        const totalAttendees = Math.max(1, uniqueAttendeeCount);
        const result = await checkAndApproveSuperRequest(
          event.id, title, artist, threshold, totalAttendees, event.settings.superRequestThresholdType ?? 'count'
        );
        if (result.approved) {
          toast.success(`The People's Pick! "${title}" added to the queue!`, { duration: 8000, icon: '👑' });
        }
      } catch { /* non-critical */ }
    }
  };

  const handlePPDismiss = (groupKey: string) => {
    if (!event) return;
    dismissGroupKey(event.id, groupKey);
    setDismissedGroupKeys(prev => new Set([...prev, groupKey]));
  };

  const openHelp = (topic?: HelpTopicId) => {
    setTutorialHelpTopic(topic ?? null);
    setTutorialMode('help');
  };

  // Handle feedback submission
  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !attendeeId) return;

    if (feedbackRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      const feedback = await submitFeedback(
        event.id,
        attendeeId,
        feedbackRating,
        feedbackMessage,
        nickname || undefined
      );
      setFeedbackSubmitted(true);
      setExistingFeedback(feedback);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Feedback error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit feedback');
    }
  };

  // Wrap-up feedback writes the same doc as the Feedback tab, and the tab shows it afterwards
  const handleWrapUpFeedback = async (rating: number, message: string) => {
    if (!event || !attendeeId) throw new Error('Still loading. Try again in a moment.');
    const feedback = await submitFeedback(event.id, attendeeId, rating, message, nickname || undefined);
    setFeedbackSubmitted(true);
    setExistingFeedback(feedback);
    setFeedbackRating(rating);
    setFeedbackMessage(message);
  };

  // Handle setting a code word (for cross-device identity)
  const handleSetCodeWord = async () => {
    if (!codeWordInput.trim()) {
      toast.error('Please enter a code word');
      return;
    }

    try {
      const result = await setCodeWord(codeWordInput);
      if (result.success) {
        toast.success(result.message);
        setHasCodeWordSet(true);
        setCodeWordInput('');
        setShowCodeWordPanel(false);
        // Generate QR code URL for cross-device linking
        const hash = getCodeWordHash();
        if (hash && eventId) {
          const linkUrl = `${window.location.origin}/e/${eventId}?linkCode=${hash}`;
          setLinkQrUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(linkUrl)}`);
        }
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error('Failed to set code word');
    }
  };

  // Handle linking identity with a code word (for access from another device)
  const handleLinkWithCodeWord = async () => {
    if (!codeWordInput.trim()) {
      toast.error('Please enter a code word');
      return;
    }

    try {
      const result = await linkWithCodeWord(codeWordInput);
      if (result.success) {
        toast.success(result.message);
        setHasCodeWordSet(true);
        setCodeWordInput('');
        setShowCodeWordPanel(false);

        // Refresh the page to load data with the new identity
        if (result.linkedAttendeeId && result.linkedAttendeeId !== attendeeId) {
          window.location.reload();
        }
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error('Failed to link code word');
    }
  };

  // Event share URL (derived)
  const eventShareUrl = event ? `${window.location.origin}/e/${event.eventUrl}` : '';

  // Generate QR code URL when event loads
  useEffect(() => {
    if (event?.eventUrl) {
      setQrCodeUrl(generateQrCodeUrl(event.eventUrl));
    }
  }, [event?.eventUrl]);

  // Set page title to event name for home screen shortcuts
  useEffect(() => {
    if (event) {
      document.title = `${event.name}, ${formatShortDate(event.date)} - VoteBeats`;
      return () => { document.title = 'VoteBeats'; };
    }
  }, [event?.name, event?.date]);

  // Show event name in sticky header when title scrolls out of view
  const [headerHeight, setHeaderHeight] = useState(49);
  useEffect(() => {
    const header = document.querySelector('header');
    if (header) setHeaderHeight(header.offsetHeight);

    const handleScroll = () => {
      const el = eventTitleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const hdrH = header?.offsetHeight ?? 49;
      // Title is hidden when its bottom edge is above the header bottom
      setShowStickyEventName(rect.bottom < hdrH);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, [event?.name]);

  // Modal escape key and body scroll lock
  useEffect(() => {
    if (showQrModal || showA2HSModal) {
      document.body.style.overflow = 'hidden';
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setShowQrModal(false);
          setShowA2HSModal(false);
        }
      };
      window.addEventListener('keydown', handleEsc);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleEsc);
      };
    }
  }, [showQrModal, showA2HSModal]);

  // Share event via Web Share API with clipboard fallback
  const handleShareEvent = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.name || 'VoteBeats Event',
          text: `Join ${event?.name} on VoteBeats and vote for your favorite songs!`,
          url: eventShareUrl,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const copyTextWithFallback = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(successMessage);
    }
  };

  const handleSharePlaylist = async () => {
    if (!event) return;
    const playlistUrl = `${window.location.origin}/e/${event.eventUrl}/playlist`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${event.name} playlist`, url: playlistUrl });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          copyTextWithFallback(playlistUrl, 'Playlist link copied to clipboard!');
        }
      }
    } else {
      copyTextWithFallback(playlistUrl, 'Playlist link copied to clipboard!');
    }
  };

  const handleShareVoteBeats = async () => {
    if (!event) return;
    const text = buildShareVoteBeatsText(event.name);
    const url = `${window.location.origin}${nextEventContactPath(event.id, 'shared')}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'VoteBeats', text, url });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          copyTextWithFallback(`${text} ${url}`, 'Message copied to clipboard!');
        }
      }
    } else {
      copyTextWithFallback(`${text} ${url}`, 'Message copied to clipboard!');
    }
  };

  const handleOpenEndedFeedback = () => {
    setWrapUpInitialStep('feedback');
    setShowWrapUp(true);
  };

  // Copy event link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventShareUrl);
      toast.success('Event link copied to clipboard!');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = eventShareUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Event link copied to clipboard!');
    }
  };

  // Detect device platform for A2HS instructions
  const getDevicePlatform = (): 'ios' | 'android' | 'desktop' => {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'desktop';
  };

  const handleNicknameChange = (value: string) => {
    const limited = value.slice(0, 30);
    const filtered = filterProfanity(limited);
    setNickname(filtered);
    // Persist to localStorage only when nickname is substantive (2+ chars)
    // or being cleared (so clearing works immediately)
    if (filtered.trim().length >= 2) {
      setAttendeeNickname(filtered.trim());
    } else if (filtered.trim() === '') {
      setAttendeeNickname('');
    }
  };

  // Song search with debouncing
  const handleSongSearch = useCallback(async (query: string) => {
    setSongSearchQuery(query);

    // Clear previous selection when user starts typing
    if (selectedSong && query !== selectedSong.trackName) {
      setSelectedSong(null);
      setRequestSong('');
      setRequestArtist('');
      setIsExplicitSong(false);
    }

    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (looksLikeLink(query)) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      setLinkInputNotice(true);
      return;
    }
    setLinkInputNotice(false);

    // Don't search if query is too short
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    // Debounce the search (300ms delay)
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await searchSongs(query, 8, 'song', searchCatalog);
        setSearchResults(results);
      } catch (error) {
        console.error('Song search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [selectedSong, event, searchCatalog]);

  // Handle inline audio preview play/pause
  const handlePreviewPlay = (previewUrl: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (playingPreviewUrl === previewUrl) {
      previewAudioRef.current?.pause();
      setPlayingPreviewUrl(null);
    } else {
      previewAudioRef.current?.pause();
      const audio = new Audio(previewUrl);
      audio.onended = () => setPlayingPreviewUrl(null);
      audio.play();
      previewAudioRef.current = audio;
      setPlayingPreviewUrl(previewUrl);
    }
  };

  // Stop preview audio when search results close or component unmounts
  useEffect(() => {
    if (!showSearchResults) {
      previewAudioRef.current?.pause();
      setPlayingPreviewUrl(null);
    }
  }, [showSearchResults]);

  // Handle selecting a song from search results
  const handleSelectSong = (song: SongSearchResult) => {
    setSelectedSong(song);
    setSongSearchQuery(song.trackName);
    setRequestSong(song.trackName);
    setRequestArtist(song.artistName);
    setIsExplicitSong(isExplicit(song));
    setExplicitWarningAcknowledged(false);
    setShowSearchResults(false);
    setSearchResults([]);
    setLinkInputNotice(false);
  };

  // Clear selected song
  const handleClearSong = () => {
    setSelectedSong(null);
    setSongSearchQuery('');
    setRequestSong('');
    setRequestArtist('');
    setIsExplicitSong(false);
    setExplicitWarningAcknowledged(false);
    setShowSearchResults(false);
    setSearchResults([]);
    setLinkInputNotice(false);
    searchInputRef.current?.focus();
  };

  const resetRequestForm = () => {
    setRequestSong('');
    setRequestArtist('');
    setRequestMessage('');
    setRequestNotes('');
    setIsExplicitSong(false);
    setExplicitWarningAcknowledged(false);
    setSelectedSong(null);
    setSongSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setLinkInputNotice(false);
  };

  const closeDuplicateModalAndReset = () => {
    setDuplicateModal(false);
    resetRequestForm();
  };

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.song-search-container')) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Guard against concurrent submissions (e.g. rapid taps on iPhone)
    if (!event) return;

    setIsSubmitting(true); // Disable button IMMEDIATELY, before any async validation

    // Use the super mode state captured at form render time, not re-evaluated now.
    // This prevents a race condition where conditions change between render and submit,
    // which could cause a nomination to be created as a regular request.
    let superMode = superModeAtRenderRef.current;

    try {
      // Check voting schedule — but allow super requests through
    if (isRequestsClosed() && !superMode) {
      if (event.settings.votingScheduledToOpen && event.settings.votingOpenTime) {
        const openTime = new Date(event.settings.votingOpenTime.replace('Z', ''));
        toast.error(`Requests open at ${openTime.toLocaleString()}`, { icon: '🔒' });
      } else {
        toast.error('Requests are currently closed', { icon: '🔒' });
      }
      return;
    }

    // Check if queue is full (maxQueueSize reached)
    const maxQueue = event.settings.maxQueueSize ?? 50;
    const queuedSongCount = requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying').length;
    if (queuedSongCount >= maxQueue) {
      if (superMode) {
        // People's Choice mode — allow nominations even when queue is full
        toast('Queue is full, but you can still nominate for The People\'s Pick!', { icon: '👑', duration: 5000 });
      } else if (event.settings.superRequestEnabled) {
        toast.error(
          `The queue is full (${queuedSongCount}/${maxQueue}). Try nominating through The People's Pick!`,
          { duration: 5000, icon: '🚫' }
        );
        return;
      } else {
        toast.error(
          `The queue is full (${queuedSongCount}/${maxQueue}). No more requests can be added right now.`,
          { duration: 5000, icon: '🚫' }
        );
        return;
      }
    }

    // Check on-site verification
    if (verificationRequired && !isVerified) {
      toast.error('Scan the on-site QR code at the venue to submit requests', { icon: '🔒' });
      return;
    }

    // Validate that either a song is selected or manual entry is complete
    if (!selectedSong && (!requestSong.trim() || !requestArtist.trim())) {
      toast.error('Please search for a song or enter the song title and artist manually.');
      return;
    }

    // Use preloaded requests from real-time subscriber (avoids Firestore reads)
    const preloaded = allRequestsRef.current;

    // Check cooldown period (skip if cooldown is 0 = disabled)
    const cooldownMinutes = event.settings.cooldownMinutes ?? 5;
    if (cooldownMinutes > 0) {
      const { inCooldown, remainingSeconds } = await isInCooldown(event.id, attendeeId, cooldownMinutes, preloaded);
      if (inCooldown) {
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
        toast.error(
          `Please wait ${timeStr} before your next request.`,
          { duration: 5000, icon: '⏱️' }
        );
        return;
      }
    }

    // Check request limit per person
    // Use postCloseRequestLimit if voting is closed, otherwise use regular limit
    const isPostClose = event.settings.votingClosed && event.settings.votingClosedAt;

    // If voting is closed, only count requests made after voting closed
    const currentRequestCount = isPostClose && event.settings.votingClosedAt
      ? await countRequestsByAttendeeAfter(event.id, attendeeId, event.settings.votingClosedAt, preloaded)
      : await countRequestsByAttendee(event.id, attendeeId, preloaded);

    const maxRequests = isPostClose
      ? (event.settings.postCloseRequestLimit ?? 2)
      : (event.settings.maxRequestsPerUser ?? 5);

    let overflowMode = false;
    if (currentRequestCount >= maxRequests && !superMode) {
      if (event.settings.overflowEnabled) {
        // Check overflow cap (30 per attendee)
        const overflowCount = preloaded.filter(r =>
          r.requestedBy.oderId === attendeeId &&
          (r.status === 'overflow' || (r.status === 'pending' && r.isOverflow))
        ).length;
        if (overflowCount >= 30) {
          toast.error('Your overflow pool is full (30 songs max).', { duration: 5000, icon: '🚫' });
          return;
        }
        overflowMode = true;
      } else if (event.settings.superRequestEnabled) {
        superMode = true;
      } else {
        const limitType = isPostClose ? 'post-close' : '';
        toast.error(
          `You've reached the maximum of ${maxRequests} ${limitType} requests for this event. (${currentRequestCount}/${maxRequests} used)`,
          { duration: 5000, icon: '🚫' }
        );
        return;
      }
    }

    // If in super mode, enforce nomination limit
    if (superMode) {
      const nomMax = event.settings.maxNominationsPerUser ?? 0;
      if (nomMax > 0) {
        const nomCount = await countNominationsByAttendee(event.id, attendeeId, preloaded);
        if (nomCount >= nomMax) {
          toast.error(
            `You've used all ${nomMax} nominations for The People's Pick. (${nomCount}/${nomMax})`,
            { duration: 5000, icon: '🚫' }
          );
          return;
        }
      }
    }

    if (!superMode) {
      // Check if this attendee has already requested this song (uses preloaded data)
      const { isDuplicate, existingRequest } = await hasDuplicateRequest(
        event.id,
        attendeeId,
        requestSong,
        requestArtist,
        preloaded
      );

      if (isDuplicate && existingRequest) {
        // F21: Show modal instead of toast for same-user duplicate
        setDuplicateExistingRequest(existingRequest);
        setDuplicateType('same-user');
        setDuplicateModal(true);
        return;
      }

      // Check if someone else has already requested this song (suggest voting instead)
      const existingSong = await findExistingSongRequest(event.id, requestSong, requestArtist, preloaded);
      if (existingSong) {
        // F21: Show modal instead of toast for cross-user duplicate
        setDuplicateExistingRequest(existingSong);
        setDuplicateType('cross-user');
        setDuplicateModal(true);
        return;
      }
    }

    // Check if song is blocked by DJ in Song Library
    if (event.settings.autoRejectBlocked) {
      const blocked = await isSongBlocked(requestSong, requestArtist);
      if (blocked) {
        toast.error('This song is not available for this event.', { duration: 4000 });
        return;
      }
    }

    // Check if explicit content is blocked
    if (isExplicitSong && (event.settings.blockExplicit || event.settings.filterExplicit)) {
      toast.error(
        'Explicit content is not allowed at this event. Please try a clean version of the song.',
        { duration: 6000, icon: '🚫' }
      );
      return;
    }

    // Check if explicit warning is enabled (warn but don't block)
    if (isExplicitSong && event.settings.warnExplicit && !explicitWarningAcknowledged) {
      setExplicitWarningAcknowledged(true);
      toast(
        'This song is flagged as explicit. The DJ may reject it. Submit again to confirm.',
        { duration: 6000, icon: '⚠️' }
      );
      return;
    }

      const newRequest = await createRequest(event.id, {
        song: {
          title: requestSong,
          artist: requestArtist,
          explicitFlag: isExplicitSong,
          // Include additional metadata if selected from iTunes search
          ...(selectedSong && {
            albumArtUrl: selectedSong.artworkUrl100 || undefined,
            durationMs: selectedSong.trackTimeMillis || undefined,
            itunesTrackId: selectedSong.trackId ? String(selectedSong.trackId) : undefined,
            previewUrl: selectedSong.previewUrl || undefined,
            isrc: selectedSong.isrc ?? null,
          }),
        },
        requestedBy: {
          oderId: attendeeId,
          nickname: nickname.trim() || undefined,
        },
        message: requestMessage.trim() || undefined,
        notes: requestNotes.trim() || undefined,
        // Auto-approve: skip pending and go straight to queued (not for super requests or overflow)
        // BUT if co-approval is enabled, stay pending for the DJ side to review first
        ...(!superMode && !overflowMode && attendeeSongSkipsApproval(event) && { initialStatus: 'queued' as const }),
        // Overflow: auto-approve routes to 'overflow' status
        ...(overflowMode && attendeeSongSkipsApproval(event) && { initialStatus: 'overflow' as const }),
        // Mark as super request if in super request mode
        // Under co-approval the nomination enters the pool only after the DJ side and the co-approver approve it
        ...(superMode && (event.settings.coApprovalEnabled
          ? { superRequest: true }
          : { superRequest: true, poolApproved: true })),
        // Mark as overflow if in overflow mode
        ...(overflowMode && {
          isOverflow: true,
          overflowPriority: preloaded.filter(r =>
            r.requestedBy.oderId === attendeeId &&
            (r.status === 'overflow' || (r.status === 'pending' && r.isOverflow))
          ).length,
        }),
      });

      // Auto-upvote: create a real vote for the requester's own song
      try {
        await voteForRequest(attendeeId, newRequest.id, 'up');
        setVotedRequests(prev => new Map(prev).set(newRequest.id, 'up'));
      } catch {
        // Non-critical — the request was still created successfully
      }

      // Auto-upvote: submitter counts as first upvote for People's Pick threshold
      if (superMode) {
        try {
          const groupKey = makePPGroupKey(newRequest.song.title, newRequest.song.artist);
          await upvoteNomination(event.id, groupKey, attendeeId, newRequest.id);
        } catch { /* non-critical — nomination was still created */ }
      }

      // Shortcut: check if this regular request matches a pool nomination
      if (!superMode && event.settings.superRequestEnabled) {
        try {
          const allReqs = await getRequestsByEventId(event.id);
          const poolNomination = allReqs.find(r =>
            r.superRequest && r.poolApproved && r.status === 'pending' &&
            isFuzzySongMatch(r.song.title, r.song.artist, requestSong, requestArtist)
          );
          if (poolNomination) {
            await approveSuperRequestManually(event.id, poolNomination.song.title, poolNomination.song.artist);
            toast.success(
              `Your request matched a People's Pick nomination — "${requestSong}" was added to the queue!`,
              { duration: 8000, icon: '👑' }
            );
          }
        } catch { /* non-critical */ }
      }

      // Check super request threshold
      if (superMode) {
        const threshold = event.settings.superRequestThresholdType === 'percentage'
          ? (event.settings.superRequestThresholdPercent ?? 10)
          : (event.settings.superRequestThresholdCount ?? 5);
        // Count unique attendees for this event (approximate from requests)
        const allReqs = await getRequestsByEventId(event.id);
        const totalAttendees = new Set(allReqs.map(r => r.requestedBy.oderId)).size;
        const { approved, count } = await checkAndApproveSuperRequest(
          event.id,
          requestSong,
          requestArtist,
          threshold,
          totalAttendees,
          event.settings.superRequestThresholdType ?? 'count'
        );
        const effectiveThreshold = event.settings.superRequestThresholdType === 'percentage'
          ? Math.max(2, Math.ceil((threshold / 100) * totalAttendees))
          : threshold;
        if (approved) {
          toast.success(
            `The People's Pick! "${requestSong}" has been added to the queue with ${count} nominations!`,
            { duration: 8000, icon: '👑' }
          );
        } else if (event.settings.coApprovalEnabled) {
          toast.success("Nomination sent for review! Once approved, it joins The People's Pick pool.", { duration: 6000, icon: '✊' });
        } else {
          toast.success(
            `Nomination added! ${count} of ${effectiveThreshold} needed for The People's Pick.`,
            { duration: 6000, icon: '✊' }
          );
        }
      } else if (overflowMode) {
        toast.success('Added to your overflow pool! The DJ can raise the limit to release it.', { duration: 6000, icon: '📦' });
      } else {
        // Normal request confirmation
        const queuePosition = requests.length + 1;
        toast.success(`Song request submitted! You're #${queuePosition} in the queue.`);
      }
      setRequestSong('');
      setRequestArtist('');
      setRequestMessage('');
      setRequestNotes('');
      setIsExplicitSong(false);
      setSelectedSong(null);
      setSongSearchQuery('');
      setSearchResults([]);
      setNominateMode(false);
      setLinkInputNotice(false);

      // Refresh the requests list, sorted by votes
      const allRefreshedRequests = await getRequestsByEventId(event.id);
      const updatedRequests = allRefreshedRequests
        .filter(r => r.status === 'queued')
        .sort((a, b) => b.voteCount - a.voteCount);
      setRequests(updatedRequests);

      // Refresh attendee's own requests for tracking
      const attendeeRequests = allRefreshedRequests.filter(r => r.requestedBy.oderId === attendeeId);
      setMyRequests(attendeeRequests);
    } catch (error) {
      if (await explainClosedRequestRefusal(error)) return;
      const errMsg = error instanceof Error ? error.message : String(error);
      toast.error(`Request failed: ${errMsg}`, { duration: 10000 });
      console.error('Request error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine current visibility phase and settings
  const getVisibilityPhase = (): 'pre_event' | 'post_close' | 'during_event' => {
    if (!event) return 'pre_event';
    if (event.status === 'active') return 'during_event';
    if (event.settings.votingClosed) return 'post_close';
    return 'pre_event';
  };

  const visibilityPhase = getVisibilityPhase();
  const preEventView = event?.settings?.preEventQueueView || 'full';
  const postCloseView = event?.settings?.postCloseQueueView || 'show';
  const duringEventView = event?.settings?.duringEventQueueView || 'now_and_upcoming';

  // Determine if queue should be visible and what should be shown
  const isQueueVisible = (): boolean => {
    if (visibilityPhase === 'during_event' && duringEventView === 'hide') return false;
    if (visibilityPhase === 'post_close' && postCloseView === 'hide') return false;
    return true;
  };

  // Determine if vote counts should be visible
  const areVoteCountsVisible = (): boolean => {
    if (isGameplayActive(event?.settings)) return false;
    if (event?.settings?.hideRankingInfo) return false;
    if (visibilityPhase === 'pre_event' && preEventView === 'blind') return false;
    return true;
  };

  // Filter requests based on visibility settings
  const getVisibleRequests = (): typeof requests => {
    if (!event) return [];

    // During event: respect duringEventQueueView setting
    if (visibilityPhase === 'during_event') {
      if (duringEventView === 'hide') return [];
      if (duringEventView === 'now_only') return []; // Only now playing shown separately
      // 'now_and_upcoming' shows the queue
    }

    // Post-close: respect postCloseQueueView setting
    if (visibilityPhase === 'post_close') {
      if (postCloseView === 'hide') return [];
      // 'show' displays the final queue
    }

    // Pre-event: respect preEventQueueView setting
    if (visibilityPhase === 'pre_event') {
      if (preEventView === 'own_only') {
        // Only show attendee's own requests
        return requests.filter(r => r.requestedBy.oderId === attendeeId && !isAttendeeCancelled(r));
      }
      // 'full' and 'blind' show all requests (blind just hides vote counts)
    }

    return requests;
  };

  const visibleRequests = getVisibleRequests();

  // Filter requests based on search
  const filteredRequests = searchQuery
    ? visibleRequests.filter(r =>
        r.song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.song.artist.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : visibleRequests;

  // Sort filtered requests for the Vote tab using frozen static order
  const gameplayActive = isGameplayActive(event?.settings);
  const staticOrderMap = new Map(staticOrderRef.current.map((id, i) => [id, i]));
  const sortedVoteRequests = [...filteredRequests].sort((a, b) =>
    (staticOrderMap.get(a.id) ?? 999) - (staticOrderMap.get(b.id) ?? 999)
  );

  // DJ queue position map (for displaying position numbers)
  const queuePositionMap = new Map(
    [...requests].sort((a, b) => (a.manualOrder ?? 999) - (b.manualOrder ?? 999))
      .map((r, i) => [r.id, i + 1])
  );

  // Momentary sort handler — re-orders the static list once, then stays
  const handleMomentarySort = (mode: 'queue' | 'votes' | 'newest') => {
    const sorted = [...filteredRequests].sort((a, b) => {
      if (mode === 'votes') return b.voteCount - a.voteCount;
      if (mode === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      // 'queue' = DJ queue order (by manualOrder field)
      return (a.manualOrder ?? 999) - (b.manualOrder ?? 999);
    });
    staticOrderRef.current = sorted.map(r => r.id);
    forceUpdate();
  };

  // Get user's voted songs for My Rankings tab
  const myRankings = requests.filter(r => votedRequests.has(r.id));

  // Check if downvoting is enabled for this event
  const downvotingEnabled = event?.settings?.downvotingEnabled ?? false;

  // Get rankable songs (queued songs for the ranking interface)
  const rankableSongs = requests.filter(r => r.status === 'queued');

  // Rank size from DJ settings (fallback to old rankingMaxSongs for backward compat)
  const rankSize = rankSizeOf(event?.settings);

  // Songs leaving the queue (played, removed, overflow) drop out of the order; no save needed
  useEffect(() => {
    if (!isRankedMode) return;
    const queuedIds = new Set(requests.filter(r => r.status === 'queued').map(r => r.id));
    dropFromRankingOrder(new Set(myOrderRef.current.filter(id => !queuedIds.has(id))));
  }, [requests, isRankedMode, dropFromRankingOrder]);

  // Ranking changes are refused while voting is closed or the attendee still has to verify on site
  const ensureRankingAllowed = (): boolean => {
    if (isVotingScheduleBlocked()) {
      if (event?.settings.votingScheduledToOpen && event.settings.votingOpenTime) {
        const openTime = new Date(event.settings.votingOpenTime.replace('Z', ''));
        toast.error(`Voting opens at ${openTime.toLocaleString()}`, { icon: '🔒' });
      } else {
        toast.error('Voting is currently closed', { icon: '🔒' });
      }
      return false;
    }
    if (verificationRequired && !isVerified) {
      toast.error('Scan the on-site QR code at the venue to rank songs', { icon: '🔒' });
      return false;
    }
    return true;
  };

  const markRankingSongSeen = (requestId: string) => {
    if (!event || seenSongIds.has(requestId)) return;
    setSeenSongIds(prev => new Set(prev).add(requestId));
    markSongAsSeen(event.id, attendeeId, requestId).catch(err => console.error('[ranking] mark seen failed', err));
  };

  // Auto-save: a burst of changes becomes one save, 5 s after the last one (or right away when the page is hidden)
  const rankingChangeVersionRef = useRef(0);
  const rankingSaveInFlightRef = useRef(false);

  const scheduleRankingSave = () => {
    rankingChangeVersionRef.current++;
    setRankingSaveDue(true);
  };

  const commitRankingOrder = (next: string[], movedId: string) => {
    markRankingSongSeen(movedId);
    setSortedThroughId(anchorBeforeMove(myOrderRef.current, sortedThroughIdRef.current, movedId));
    setMyOrder(next);
    scheduleRankingSave();
  };

  // Downvoted songs sit together at the bottom of the attendee's own order and never count in their ballot (ITER-144)
  const commitRespectingDownvotes = (next: string[], movedId: string): boolean => {
    if (!isPartitioned(next, downvotedIds)) {
      toast.error(downvotedIds.has(movedId)
        ? 'Remove your downvote first to rank it higher.'
        : 'Downvoted songs stay at the bottom of your ranking.');
      return false;
    }
    commitRankingOrder(next, movedId);
    return true;
  };

  const nonDownvotedOrder = () => myOrder.filter(id => !downvotedIds.has(id));
  const downvotedOrder = () => myOrder.filter(id => downvotedIds.has(id));

  // A new downvote sends the song to the end of the block; removing it puts the song just above the block
  const moveForDownvoteChange = (requestId: string, wasDown: boolean, votes: Map<string, VoteType>) => {
    const isDown = votes.get(requestId) === 'down';
    const order = myOrderRef.current;
    if (isDown && !wasDown) {
      commitRankingOrder(moveToDownvotedBlockEnd(order, requestId), requestId);
    } else if (wasDown && !isDown && order.includes(requestId)) {
      const stillDown = new Set([...votes].filter(([, type]) => type === 'down').map(([id]) => id));
      commitRankingOrder(moveAboveDownvotedBlock(order, requestId, stillDown), requestId);
    }
  };

  const saveRankingNow = async () => {
    if (!event || !attendeeId || !rankingSaveDue || rankingSaveInFlightRef.current) return;
    if (isVotingScheduleBlocked() || (verificationRequired && !isVerified)) return;
    const version = rankingChangeVersionRef.current;
    const fullOrder = myOrderRef.current;
    const queuedIds = new Set(allRequestsRef.current.filter(r => r.status === 'queued').map(r => r.id));
    rankingSaveInFlightRef.current = true;
    setRankingSaveState('saving');
    try {
      await submitRanking(event.id, attendeeId, {
        rankings: effectiveBallot({ rankings: [], fullOrder }, queuedIds, rankSize, downvotedIds),
        fullOrder,
        sortedThroughId: sortedThroughIdRef.current,
      });
      if (rankingChangeVersionRef.current === version) setRankingSaveDue(false);
      setRankingSaveState('saved');
    } catch (err) {
      console.error('[ranking] save failed', err);
      setRankingSaveState('error');
      toast.error("Couldn't save your ranking — will retry", { id: 'ranking-save-error' });
    } finally {
      rankingSaveInFlightRef.current = false;
    }
  };
  const saveRankingNowRef = useRef(saveRankingNow);
  saveRankingNowRef.current = saveRankingNow;

  useEffect(() => {
    if (!rankingSaveDue) return;
    const timer = setTimeout(() => { void saveRankingNowRef.current(); }, RANKING_AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [rankingSaveDue, myOrder, sortedThroughId, rankingSaveState]);

  useEffect(() => {
    const flush = () => { void saveRankingNowRef.current(); };
    const onVisibilityChange = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  useEffect(() => {
    if (!rankingSaveDue && rankingSaveState !== 'saving') return;
    const warnBeforeLeaving = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [rankingSaveDue, rankingSaveState]);

  // Add song to the bottom of the ranking (from New or Unranked); a full ranking asks which song to replace
  const handleAddToRanking = (requestId: string): boolean => {
    if (!ensureRankingAllowed()) return false;
    if (downvotedIds.has(requestId)) {
      commitRankingOrder(moveToDownvotedBlockEnd(myOrder, requestId), requestId);
      toast('You downvoted this song, so it goes with your downvoted songs.');
      return true;
    }
    const next = addToRankingBottom(nonDownvotedOrder(), requestId, rankSize);
    if (next === 'full') {
      setRankSwapModal({ incomingId: requestId });
      return true;
    }
    return commitRespectingDownvotes([...next, ...downvotedOrder()], requestId);
  };

  // Send song to the top or bottom of Unranked; the songs below a ranked song shift up
  const handleUnrankToTop = (requestId: string) => {
    if (!ensureRankingAllowed()) return;
    commitRespectingDownvotes([...unrankTo(nonDownvotedOrder(), requestId, rankSize, 'top'), ...downvotedOrder()], requestId);
  };

  const handleUnrankToBottom = (requestId: string) => {
    if (!ensureRankingAllowed()) return;
    commitRespectingDownvotes([...unrankTo(nonDownvotedOrder(), requestId, rankSize, 'bottom'), ...downvotedOrder()], requestId);
  };

  // Handle rank swap: replace outgoing ranked song with incoming one; outgoing goes to the top of Unranked
  const handleRankSwap = (outgoingId: string) => {
    if (!rankSwapModal) return;
    const incomingId = rankSwapModal.incomingId;
    setRankSwapModal(null);
    if (!ensureRankingAllowed()) return;
    commitRespectingDownvotes([...replaceInRanking(nonDownvotedOrder(), incomingId, outgoingId, rankSize), ...downvotedOrder()], incomingId);
  };

  const handleOpenMove = (requestId: string) => {
    if (!ensureRankingAllowed()) return;
    setMoveSongId(requestId);
  };

  const handleMoveToPosition = (requestId: string, position: number) => {
    setMoveSongId(null);
    if (!ensureRankingAllowed()) return;
    commitRespectingDownvotes(moveToIndex(myOrder, requestId, position - 1), requestId);
  };

  const handlePlaceBefore = (requestId: string, beforeId: string) => {
    setMoveSongId(null);
    if (!ensureRankingAllowed()) return;
    const beforeIndex = myOrder.filter(id => id !== requestId).indexOf(beforeId);
    if (beforeIndex < 0) return;
    commitRespectingDownvotes(moveToIndex(myOrder, requestId, beforeIndex), requestId);
  };

  const saveOverflowOrder = async (ids: string[]) => {
    try {
      await reorderOverflow(ids);
    } catch (err) {
      console.error('[overflow] reorder failed', err);
      toast.error("Couldn't save your overflow order — try again");
    }
  };

  // A drop lands before the song that now follows it, found by id since myOrder also holds songs not shown
  const handleDragRank = (activeId: string, overId: string) => {
    if (!ensureRankingAllowed()) return;
    if (!myOrderRef.current.includes(activeId)) {
      toast('That song just left the queue.');
      return;
    }
    const next = dropIntoOrder(myOrder, rankingDragIds, activeId, overId);
    if (next) commitRespectingDownvotes(next, activeId);
  };

  // "Sorted up to here" line: a personal reminder below a song; it never affects scoring
  const handleOpenLinePicker = () => {
    if (!ensureRankingAllowed()) return;
    setLinePickerOpen(true);
  };

  const handleSetSortedThrough = (requestId: string | null) => {
    setMoveSongId(null);
    setLinePickerOpen(false);
    if (!ensureRankingAllowed()) return;
    setSortedThroughId(requestId);
    scheduleRankingSave();
  };

  // Ranked = the first rankSize non-downvoted songs of the order, Unranked = the other non-downvoted songs,
  // Downvoted = the attendee's downvoted songs, kept at the bottom and left out of their ballot
  const notDownvotedIds = myOrder.filter(id => !downvotedIds.has(id));
  const rankedSongs = notDownvotedIds.slice(0, rankSize)
    .map(id => requests.find(r => r.id === id))
    .filter((r): r is SongRequest => r !== undefined);

  const unrankedSongs = notDownvotedIds.slice(rankSize)
    .map(id => requests.find(r => r.id === id && r.status === 'queued'))
    .filter((r): r is SongRequest => r !== undefined);

  const downvotedSongs = myOrder.filter(id => downvotedIds.has(id))
    .map(id => requests.find(r => r.id === id && r.status === 'queued'))
    .filter((r): r is SongRequest => r !== undefined);

  const rankingDragIds = [...rankedSongs, ...unrankedSongs, ...downvotedSongs].map(r => r.id);

  // New/unseen songs: queued, not seen and not yet placed in the order
  const newSongs = rankableSongs
    .filter(r => !seenSongIds.has(r.id) && !myOrder.includes(r.id))
    .sort((a, b) => b.voteCount - a.voteCount);

  const moveSong = moveSongId ? requests.find(r => r.id === moveSongId) ?? null : null;

  // "Sorted up to here" line: before it's placed it waits at the top of Unranked as a hint; tapping it opens the picker
  const renderSortedThroughLine = (isPlaced: boolean) => (
    <div className="flex items-center gap-2 my-1.5" role="separator" aria-label={isPlaced ? 'Sorted up to here' : 'Sorted-up-to-here marker, not placed yet'}>
      <div className={`flex-1 border-t-2 border-dashed ${isPlaced ? 'border-teal-400 dark:border-teal-500' : 'border-teal-300/60 dark:border-teal-700'}`} />
      <button
        type="button"
        onClick={handleOpenLinePicker}
        className={`text-xs font-medium whitespace-nowrap px-1.5 py-0.5 rounded transition-colors hover:bg-teal-50 dark:hover:bg-teal-900/30 ${isPlaced ? 'text-teal-600 dark:text-teal-400' : 'text-teal-500/80 dark:text-teal-500 italic'}`}
      >
        {isPlaced ? 'Sorted — everything above this line' : 'Tap to mark how far you’ve sorted'}
      </button>
      {isPlaced && (
        <button
          type="button"
          onClick={() => handleSetSortedThrough(null)}
          className="p-0.5 text-teal-500 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
          aria-label="Remove sorted-up-to-here line"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      <div className={`flex-1 border-t-2 border-dashed ${isPlaced ? 'border-teal-400 dark:border-teal-500' : 'border-teal-300/60 dark:border-teal-700'}`} />
    </div>
  );

  // One ranking card: rank number (tap to move) beside two lines — title and artist, then votes, rank buttons and preview links
  const renderRankingRow = (request: SongRequest, kind: 'new' | 'ranked' | 'unranked' | 'downvoted', index: number, sortable?: SortableRenderProps) => {
    const currentVote = votedRequests.get(request.id);
    const songVotedOut = isVotedOut(request);
    const blockStart = rankedSongs.length + unrankedSongs.length;
    const position = kind === 'ranked' ? index + 1
      : kind === 'unranked' ? rankedSongs.length + index + 1
      : kind === 'downvoted' ? blockStart + index + 1
      : null;
    const cardClass = kind === 'new'
      ? `border-l-4 ${songVotedOut ? 'border-red-500 opacity-50' : 'border-blue-500'}`
      : songVotedOut ? 'opacity-50 border-l-4 border-red-500'
      : kind === 'unranked' || kind === 'downvoted' ? 'opacity-70 hover:opacity-100' : '';
    const smallButton = 'inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded transition-colors bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600';
    const statusPill = songVotedOut
      ? { label: 'Voted out', className: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400' }
      : { label: 'NEW', className: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400' };

    return (
      <div
        key={request.id}
        ref={sortable?.setNodeRef}
        style={sortable?.style}
        className={`card p-3 flex items-center gap-2 ${sortable?.isDragging ? 'shadow-lg ring-2 ring-primary' : 'transition-all'} ${cardClass}`}
      >
        {sortable && (
          <DragHandle
            bindings={sortable.handleProps}
            label={`Drag ${request.song.title} to a new position`}
            className="-ml-1.5 -mr-1"
          />
        )}
        {position !== null && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => handleOpenMove(request.id)}
              className={`min-w-[2.25rem] h-9 px-1 rounded-md text-sm font-bold border border-dashed transition-colors ${
                kind === 'ranked'
                  ? 'text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                  : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Tap to move to another number"
              aria-label={`Move ${request.song.title}, now number ${position}`}
            >
              #{position}
            </button>
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-1">
          {/* A voted-out song gets its label on its own line, so the title keeps its room (ITER-144) */}
          {songVotedOut && (
            <p className="flex">
              {kind === 'new' ? (
                <button
                  type="button"
                  onClick={() => handleOpenMove(request.id)}
                  className={`text-xs leading-snug text-left px-2 py-0.5 rounded-md font-semibold ${statusPill.className}`}
                  title="Tap to put this song at a number"
                  aria-label={`Move ${request.song.title} to a number`}
                >
                  {statusPill.label}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => openRallyDetail(request.id)}
                  className="text-xs text-left bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 leading-snug px-2 py-0.5 rounded-md font-medium"
                  aria-label="Voted out: see how it can come back"
                >
                  Voted out
                </button>
              )}
            </p>
          )}
          <p className={`text-sm flex items-center gap-1.5 min-w-0 ${expandedTitleIds.has(request.id) ? '' : 'truncate'}`}>
            {kind === 'new' && !songVotedOut && (
              <button
                type="button"
                onClick={() => handleOpenMove(request.id)}
                className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 font-semibold ${statusPill.className}`}
                title="Tap to put this song at a number"
                aria-label={`Move ${request.song.title} to a number`}
              >
                {statusPill.label}
              </button>
            )}
            {isCrowdFavorite(request) && (
              <span className="text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium">{crowdFavoriteLabel(request, participantCount)}</span>
            )}
            {request.song.explicitFlag && (
              <span className="explicit-badge flex-shrink-0 text-xs" title="Explicit content">E</span>
            )}
            <span
              className={`min-w-0 cursor-pointer ${expandedTitleIds.has(request.id) ? 'whitespace-normal break-words' : 'truncate'}`}
              onClick={() => toggleTitle(request.id)}
              role="button"
              tabIndex={0}
            >
              <span className="font-medium text-gray-900 dark:text-white">{request.song.title}</span>
              <span className="text-gray-500 dark:text-gray-400"> — {request.song.artist}</span>
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-1">
            <button
              onClick={() => handleVote(request.id, 'up')}
              className={`p-1 rounded transition-colors ${currentVote === 'up' ? 'text-green-500' : 'text-gray-400 hover:text-green-500'}`}
              title="Upvote"
            >
              <ThumbsUp className={`w-3.5 h-3.5 ${currentVote === 'up' ? 'fill-current' : ''}`} />
            </button>
            {downvotingEnabled && (
              <button
                onClick={() => handleVote(request.id, 'down')}
                className={`p-1 rounded transition-colors ${currentVote === 'down' ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
                title={currentVote === 'down' ? 'Undo downvote' : 'Downvote'}
              >
                <ThumbsDown className={`w-3.5 h-3.5 ${currentVote === 'down' ? 'fill-current' : ''}`} />
              </button>
            )}
            {kind !== 'ranked' && kind !== 'downvoted' && (
              <button
                onClick={() => handleAddToRanking(request.id)}
                className="px-1.5 py-0.5 text-xs rounded transition-colors bg-primary/10 text-primary hover:bg-primary/20"
                title={kind === 'new' ? 'Add to ranking' : 'Promote to ranking'}
              >
                +Rank
              </button>
            )}
            {kind !== 'downvoted' && !(kind === 'unranked' && index === 0) && (
              <button onClick={() => handleUnrankToTop(request.id)} className={smallButton} title="Send to top of unranked">
                <ArrowUp className="w-3 h-3" />unRank
              </button>
            )}
            {kind !== 'downvoted' && !(kind === 'unranked' && index === unrankedSongs.length - 1) && (
              <button onClick={() => handleUnrankToBottom(request.id)} className={smallButton} title="Send to bottom of unranked">
                <ArrowDown className="w-3 h-3" />unRank
              </button>
            )}
            <span className="ml-auto flex items-center gap-2">
              {request.song.itunesTrackId && (
                <a
                  href={`https://music.apple.com/us/song/${request.song.itunesTrackId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[10px] text-pink-500 hover:text-pink-400 transition-colors"
                  title="Listen on Apple Music"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-2.5 h-2.5" />
                  Apple
                </a>
              )}
              {request.spotifyUri && (
                <a
                  href={`https://open.spotify.com/track/${request.song.catalogIds?.spotify ?? spotifyTrackIdFrom(request.spotifyUri)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[10px] text-green-500 hover:text-green-400 transition-colors"
                  title="Listen on Spotify"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-2.5 h-2.5" />
                  Spotify
                </a>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Played songs for the played section (use playedRequests state, already sorted)
  const playedSongs = playedRequests;

  if (loading) {
    const isVerifying = new URLSearchParams(window.location.search).has('verify');
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {isVerifying ? 'Verifying your access...' : 'Loading event...'}
          </p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
            <Music className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Event Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            The event you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // At a live Last Song stop the event isn't completed and tookPart is unknown; the attendee has the live page open
  // and checked in, so feedback is offered (ITER-199)
  const renderWrapUpModal = () => {
    if (!showWrapUp) return null;
    return (
      <ShowWrapUpModal
        eventId={event.id}
        eventName={event.name}
        showFeedback={eventEnded ? canLeaveFeedback({ tookPart, alreadyGaveFeedback: feedbackSubmitted }) : !feedbackSubmitted}
        initialStep={wrapUpInitialStep}
        onSubmitFeedback={handleWrapUpFeedback}
        onShare={handleShareEvent}
        onClose={() => setShowWrapUp(false)}
      />
    );
  };

  // An award revealed while the wrap-up is open waits for it to close, then shows the latest award
  const renderAwardModal = () => {
    if (!showAwardModal || showWrapUp || !eventAwards || !attendeeId || attendeeId === 'pending-auth' || attendeeId === event.djId) return null;
    return <AwardRevealModal awards={eventAwards} attendeeId={attendeeId} onClose={() => setShowAwardModal(false)} />;
  };

  if (event.status === 'completed') {
    const view = endedView({ tookPart, lookupFailed: tookPartLookupFailed, choseEndedPage });
    if (view === 'loading') {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }
    if (view === 'missed') {
      return <EventMissedView eventId={event.id} eventName={event.name} onSeePlaylist={() => setChoseEndedPage(true)} />;
    }
    const playedSongs = playedRequests
      .filter((request) => !request.practiceMode)
      .slice()
      .reverse()
      .map((request) => ({ id: request.id, title: request.song.title, artist: request.song.artist }));
    return (
      <>
        <EventEndedView
          eventId={event.id}
          eventName={event.name}
          eventUrl={event.eventUrl}
          playedSongs={playedSongs}
          canLeaveFeedback={canLeaveFeedback({ tookPart, alreadyGaveFeedback: feedbackSubmitted })}
          onLeaveFeedback={handleOpenEndedFeedback}
          onSharePlaylist={handleSharePlaylist}
          onShareVoteBeats={handleShareVoteBeats}
          awards={eventAwards}
          attendeeId={attendeeId || undefined}
        />
        {renderWrapUpModal()}
        {renderAwardModal()}
      </>
    );
  }

  const handleCancelOwnRequest = async (requestId: string, successMessage: string) => {
    setCancellingId(requestId);
    try {
      await cancelOwnRequest(requestId);
      setConfirmCancelId(null);
      toast.success(successMessage);
    } catch (error) {
      console.error('[EventPage] cancelOwnRequest failed', { requestId, error });
      const reason = cancelRefusalReason(error);
      if (reason) {
        setCancelLockedReasons(previous => ({ ...previous, [requestId]: reason }));
        setConfirmCancelId(null);
        toast.error(cancelBlockMessage(reason));
      } else {
        toast.error("Couldn't cancel this request. Try again.");
      }
    } finally {
      setCancellingId(null);
    }
  };

  const renderCancelAction = (request: SongRequest) => {
    const lockedReason = cancelLockedReasons[request.id];
    if (lockedReason) {
      if (lockedReason !== 'voted' && lockedReason !== 'ranked') return null;
      return (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{cancelBlockMessage(lockedReason)}</p>
      );
    }
    if (requestOnlyCancelBlock(request, attendeeId) !== null) return null;
    const isCancelling = cancellingId === request.id;
    if (confirmCancelId === request.id) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Cancel this request?</span>
          <button
            type="button"
            onClick={() => handleCancelOwnRequest(request.id, 'Request cancelled')}
            disabled={isCancelling}
            className="font-semibold text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
          >
            {isCancelling ? 'Cancelling…' : 'Yes'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmCancelId(null)}
            disabled={isCancelling}
            className="font-semibold text-gray-600 dark:text-gray-300 hover:underline disabled:opacity-50"
          >
            No
          </button>
        </div>
      );
    }
    return (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setConfirmCancelId(request.id)}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
        >
          <X className="w-3 h-3" />
          Cancel
        </button>
      </div>
    );
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'request':
        return (
          <div className="space-y-6">
            {/* Request Status Card (F19) */}
            {event && (
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-gray-600 dark:text-gray-400">Requests:</span>
                  <span className={`font-semibold ${requestStatusCount >= requestStatusMax ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                    {requestStatusCount}/{requestStatusMax}
                  </span>
                  <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${requestStatusCount >= requestStatusMax ? 'bg-red-500' : 'bg-primary'}`}
                      style={{ width: `${Math.min(100, (requestStatusCount / requestStatusMax) * 100)}%` }}
                    />
                  </div>
                </div>
                {event.settings.superRequestEnabled && isSuperRequestMode() && (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-amber-600 dark:text-amber-400 text-xs">Nominations:</span>
                    <span className={`font-semibold text-xs ${
                      nominationStatusMax > 0 && nominationStatusCount >= nominationStatusMax
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-amber-700 dark:text-amber-300'
                    }`}>
                      {nominationStatusCount}{nominationStatusMax > 0 ? `/${nominationStatusMax}` : ''}
                    </span>
                  </div>
                )}
                {OVERFLOW_POOL_ENABLED && event.settings.overflowEnabled && requestStatusCount >= requestStatusMax && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                    Overflow Mode
                  </span>
                )}
                {cooldownRemaining > 0 && (
                  <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="font-medium">
                      {Math.floor(cooldownRemaining / 60)}m {cooldownRemaining % 60}s
                    </span>
                  </div>
                )}
                {(() => {
                  const requestsClosed = isRequestsClosed();
                  const hasSchedule = event.settings.votingScheduledToOpen && event.settings.votingOpenTime;
                  return (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      !requestsClosed
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    }`}>
                      {!requestsClosed
                        ? 'Open'
                        : hasSchedule
                          ? `Opens ${new Date(event.settings.votingOpenTime!.replace('Z', '')).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                          : 'Closed'}
                    </span>
                  );
                })()}
              </div>
            )}

            {/* Collapsible Current Queue — hidden; attendees use Browse tab instead */}
            {false && requests.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowQueueInRequest(!showQueueInRequest)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <ListMusic className="w-4 h-4" />
                    Current Queue ({requests.length} songs)
                  </span>
                  {showQueueInRequest ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                {showQueueInRequest && (
                  <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
                    {requests.map((req, i) => (
                      <div key={req.id} className="px-3 py-2 flex items-center gap-2 text-sm">
                        <span className="text-xs text-gray-400 w-5 text-right">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-gray-900 dark:text-white">{req.song.title}</p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">{req.song.artist}</p>
                        </div>
                        <span className="text-xs text-gray-400">{req.voteCount} votes</span>
                      </div>
                    ))}
                    {playedRequests.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                          <Check className="w-3 h-3" />
                          Played ({playedRequests.length})
                        </div>
                        {playedRequests.map((req) => (
                          <div key={req.id} className="px-3 py-2 flex items-center gap-2 text-sm opacity-50">
                            <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="truncate text-gray-500 dark:text-gray-400 line-through">{req.song.title}</p>
                              <p className="truncate text-xs text-gray-400 dark:text-gray-500">{req.song.artist}</p>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Super Request Banner — only show when forced into super mode, not manual toggle */}
            {isSuperRequestMode() && getSuperRequestReason() !== 'manual' && (
              <div className="card p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-amber-200 dark:border-amber-700">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👑</span>
                  <div>
                    <p className="font-semibold text-amber-800 dark:text-amber-200">The People's Pick</p>
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      {getSuperRequestReason() === 'closed'
                        ? 'Requests are closed, but you can still nominate songs! If enough people nominate the same song, it gets added to the queue.'
                        : getSuperRequestReason() === 'limit'
                          ? "You've used all your direct requests. You can still nominate songs for The People's Pick! Songs that get enough nominations from other attendees will be added to the queue."
                          : 'The queue is full, but you can still nominate songs! If enough people nominate the same song, it gets added to the queue.'}
                    </p>
                    {nominationStatusMax > 0 && (
                      <p className="text-xs text-amber-500 mt-1">
                        {nominationStatusCount} of {nominationStatusMax} nominations used
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isRankedMode && !event?.settings?.hideRankingInfo && !rankBannerDismissed && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  <button
                    type="button"
                    onClick={() => setActiveTab('rankings')}
                    className="flex-1 text-left text-sm text-yellow-800 dark:text-yellow-300 hover:underline"
                  >
                    Don't forget to rank your favorites in <strong>My Rankings</strong>!
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRankBannerDismissed(true);
                      try { sessionStorage.setItem('votebeats_rank_banner_dismissed', 'true'); } catch {}
                    }}
                    className="text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-300 flex-shrink-0"
                    aria-label="Dismiss ranking reminder"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            {OVERFLOW_POOL_ENABLED && event.settings.overflowEnabled && requestStatusCount >= requestStatusMax && !overflowBannerDismissed && (
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <ListMusic className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                      You can still request songs!
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                      Extra requests go to your overflow pool. Manage them in{' '}
                      <button
                        type="button"
                        onClick={() => setActiveTab('my-requests')}
                        className="inline-flex items-center gap-0.5 font-semibold underline hover:no-underline"
                      >
                        <ListMusic className="w-3 h-3" />
                        My Requests
                      </button>.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setOverflowBannerDismissed(true);
                      try { sessionStorage.setItem('votebeats_overflow_banner_dismissed', 'true'); } catch {}
                    }}
                    className="text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 flex-shrink-0"
                    aria-label="Dismiss overflow info"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Request Form */}
            {/* Capture super mode at render time so it's stable through submission */}
            {(() => { superModeAtRenderRef.current = isSuperRequestMode(); return null; })()}
            <form onSubmit={handleRequest} className="card p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isSuperRequestMode() ? 'Nominate a Song' : 'Request a Song'}
              </h2>

              {event?.settings?.superRequestEnabled && !isRequestsClosed() && requestStatusCount < requestStatusMax && requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying').length < (event?.settings?.maxQueueSize ?? 50) && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={nominateMode}
                      onChange={(e) => setNominateMode(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm text-amber-800 dark:text-amber-300">
                      Submit as a People's Pick nomination instead
                    </span>
                  </label>
                  <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                </div>
              )}

              {/* Nickname - shown prominently at top */}
              {!isEditingNickname ? (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Requesting as <strong className="text-gray-900 dark:text-white">
                    {nickname || attendeeDisplayName(null, attendeeId)}
                  </strong></span>
                  <button
                    type="button"
                    onClick={() => setIsEditingNickname(true)}
                    className="text-primary hover:text-primary/80 underline text-sm"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    What should we call you?
                  </label>
                  <div className="relative">
                    <input
                      ref={nicknameInputRef}
                      type="text"
                      value={nickname}
                      onChange={(e) => handleNicknameChange(e.target.value)}
                      onBlur={() => { if (nickname.trim().length >= 2) setIsEditingNickname(false); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && nickname.trim()) setIsEditingNickname(false); }}
                      placeholder="Enter a nickname (optional)"
                      className="input input-action pr-9"
                      maxLength={30}
                    />
                    {nickname.length > 0 && (
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { handleNicknameChange(''); nicknameInputRef.current?.focus(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        aria-label="Clear nickname"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Shown alongside your song requests
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setNickname('');
                        setAttendeeNickname('');
                        setAnonDismissed(true);
                        setIsEditingNickname(false);
                      }}
                      className="text-xs text-primary hover:text-primary/80 underline"
                    >
                      Stay anonymous
                    </button>
                  </div>
                </div>
              )}

              {/* Song Search with Autocomplete */}
              <div className="song-search-container relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {isSuperRequestMode() ? 'Search for a Song to Nominate' : 'Search for a Song'}
                  </label>
                  {false && (<button
                    type="button"
                    onClick={() => openHelp('requesting')}
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="How to request a song"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>)}
                </div>

                {selectedSong ? (
                  /* Selected Song Display */
                  <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    {selectedSong.artworkUrl60 ? (
                      <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-gray-200 dark:bg-gray-700">
                        <img
                          src={selectedSong.artworkUrl60}
                          alt="Album art"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/60/6366F1/ffffff?text=♪';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 flex-shrink-0 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <Music className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                        <span className="truncate">{selectedSong.trackName}</span>
                        {isExplicit(selectedSong) && (
                          <span className="explicit-badge flex-shrink-0 text-xs">E</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {selectedSong.artistName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                        {`${selectedSong.collectionName} • ${formatDuration(selectedSong.trackTimeMillis)}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearSong}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      aria-label="Clear selection"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  /* Search Input */
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={songSearchQuery}
                        onChange={(e) => handleSongSearch(e.target.value)}
                        onFocus={() => songSearchQuery.length >= 2 && setShowSearchResults(true)}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData('text');
                          if (looksLikeLink(pasted)) {
                            e.preventDefault();
                            setLinkInputNotice(true);
                          }
                        }}
                        placeholder="Search for a song or artist..."
                        className="input input-action pl-10 pr-10 py-3 text-base ring-2 ring-primary/30 focus:ring-primary border-primary/30 focus:border-primary"
                        autoComplete="off"
                      />
                      {isSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary animate-spin" />
                      )}
                    </div>
                    {linkInputNotice && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{LINKS_NOT_SUPPORTED_MESSAGE}</p>
                    )}
                  </div>
                )}

                {/* Search Results Dropdown — iTunes */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {searchResults.map((song) => (
                      <button
                        key={song.trackId}
                        type="button"
                        onClick={() => handleSelectSong(song)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-gray-200 dark:bg-gray-700">
                          <img
                            src={song.artworkUrl60}
                            alt={`${song.trackName} album art`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/60/6366F1/ffffff?text=♪';
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate text-sm flex items-center gap-1.5">
                            <span className="truncate">{song.trackName}</span>
                            {isExplicit(song) && (
                              <span className="explicit-badge flex-shrink-0 text-xs">E</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {song.artistName}
                          </p>
                        </div>
                        {song.previewUrl && (
                          <button
                            type="button"
                            onClick={(e) => handlePreviewPlay(song.previewUrl!, e)}
                            className="p-1.5 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-primary/20 transition-colors flex-shrink-0"
                            title={playingPreviewUrl === song.previewUrl ? 'Stop preview' : 'Preview 30s'}
                          >
                            {playingPreviewUrl === song.previewUrl ? (
                              <Pause className="w-3.5 h-3.5 text-primary" />
                            ) : (
                              <Play className="w-3.5 h-3.5 text-gray-500" />
                            )}
                          </button>
                        )}
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {formatDuration(song.trackTimeMillis)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* No Results Message */}
                {showSearchResults && !isSearching && songSearchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-sm">No songs found</p>
                    <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">Try a different search, or enter it manually below</p>
                  </div>
                )}
              </div>

              {/* Manual Entry (fallback) — collapsed by default */}
              {!selectedSong && (
                <>
                  <button
                    type="button"
                    onClick={() => setManualEntryExpanded(prev => !prev)}
                    className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors pt-2 border-t border-gray-200 dark:border-gray-700 w-full"
                  >
                    {manualEntryExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    Enter song manually
                  </button>
                  {manualEntryExpanded && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Song Title
                          </label>
                          <input
                            type="text"
                            value={requestSong}
                            onChange={(e) => setRequestSong(e.target.value)}
                            placeholder="Enter song name"
                            className="input input-action"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Artist
                          </label>
                          <input
                            type="text"
                            value={requestArtist}
                            onChange={(e) => setRequestArtist(e.target.value)}
                            placeholder="Enter artist name"
                            className="input input-action"
                          />
                        </div>
                      </div>
                      {/* Notes for DJ */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Notes for the DJ (optional)
                          </label>
                          <span className={`text-xs ${
                            requestNotes.length > 200
                              ? 'text-danger font-medium'
                              : requestNotes.length > 160
                              ? 'text-amber-500'
                              : 'text-gray-500'
                          }`}>
                            {requestNotes.length}/200
                          </span>
                        </div>
                        <textarea
                          value={requestNotes}
                          onChange={(e) => {
                            if (e.target.value.length <= 200) {
                              setRequestNotes(e.target.value);
                            }
                          }}
                          placeholder="e.g., Play this for my friend's birthday!"
                          className="input w-full text-sm resize-none"
                          rows={2}
                          maxLength={200}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Dedication (optional)
                  </label>
                  <span className={`text-xs ${
                    requestMessage.length > 100
                      ? 'text-danger font-medium'
                      : requestMessage.length > 80
                      ? 'text-amber-500'
                      : 'text-gray-500'
                  }`}>
                    {requestMessage.length}/100
                  </span>
                </div>
                <input
                  type="text"
                  value={requestMessage}
                  onChange={(e) => {
                    // Enforce 100 character limit
                    if (e.target.value.length <= 100) {
                      setRequestMessage(e.target.value);
                    }
                  }}
                  placeholder="Add a message for the DJ..."
                  className={`input ${requestMessage.length > 100 ? 'border-danger' : ''}`}
                  maxLength={100}
                />
              </div>
              {/* Show explicit checkbox only for manual entry (search results auto-detect) */}
              {!selectedSong && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="explicit-checkbox"
                    checked={isExplicitSong}
                    onChange={(e) => setIsExplicitSong(e.target.checked)}
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <label htmlFor="explicit-checkbox" className="text-sm text-gray-700 dark:text-gray-300">
                    This song contains explicit content
                  </label>
                  {(event.settings.blockExplicit || event.settings.filterExplicit) && isExplicitSong && (
                    <span className="text-xs text-danger ml-auto">Not allowed</span>
                  )}
                  {!(event.settings.blockExplicit || event.settings.filterExplicit) && event.settings.warnExplicit && isExplicitSong && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 ml-auto">DJ prefers clean</span>
                  )}
                </div>
              )}
              {/* Show explicit block warning for selected song */}
              {selectedSong && isExplicitSong && (event.settings.blockExplicit || event.settings.filterExplicit) && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                    <span className="explicit-badge">E</span>
                    This song contains explicit content and cannot be requested at this event.
                  </p>
                </div>
              )}
              {/* Show explicit caution warning (not blocked, but DJ prefers clean) */}
              {selectedSong && isExplicitSong && !(event.settings.blockExplicit || event.settings.filterExplicit) && event.settings.warnExplicit && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <span className="explicit-badge">E</span>
                    This song is flagged as explicit. The DJ prefers clean content and may reject this request.
                  </p>
                </div>
              )}
              <button
                type="submit"
                className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting ||
                  (!selectedSong && (!requestSong.trim() || !requestArtist.trim())) ||
                  (isExplicitSong && (event.settings.blockExplicit || event.settings.filterExplicit)) ||
                  (verificationRequired && !isVerified)}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : verificationRequired && !isVerified ? (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Scan QR to Request
                  </>
                ) : isSuperRequestMode() ? (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Nominate Song
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Request
                  </>
                )}
              </button>
            </form>
          </div>
        );

      case 'browse':
        return (
          <div className="space-y-4">
            {/* Edit Mode Banner for Attendees */}
            {event.editMode && (
              <div className="p-4 bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-200 dark:bg-orange-800 rounded-full">
                    <Lock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="font-medium text-orange-800 dark:text-orange-200">
                      Queue is being reorganized
                    </p>
                    <p className="text-sm text-orange-600 dark:text-orange-400">
                      The DJ is making changes. The queue will update shortly.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Vote Cooldown Banner (Doom Voting) */}
            {voteCooldownDisplay > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center gap-3">
                <div className="p-1.5 bg-amber-200 dark:bg-amber-800 rounded-full">
                  <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Vote cooldown active
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    You can vote again in {Math.floor(voteCooldownDisplay / 60)}:{(voteCooldownDisplay % 60).toString().padStart(2, '0')}
                  </p>
                </div>
              </div>
            )}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search the queue..."
                className="input pl-10"
              />
            </div>

            {/* Queue List Header + Sort Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!gameplayActive && isQueueVisible() && (
                  <button
                    onClick={() => handleMomentarySort('queue')}
                    className="px-2 py-1 text-xs rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-800"
                    title="Sort by DJ queue order"
                  >Queue</button>
                )}
                <span className="text-sm text-gray-500">{sortedVoteRequests.length} songs</span>
              </div>
              {gameplayActive ? (
                <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Game in progress
                </span>
              ) : (
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
                <span className="px-2 py-1 text-xs rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm font-medium">Static</span>
                <button
                  onClick={() => handleMomentarySort('votes')}
                  className="px-2 py-1 text-xs rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  title="Sort by votes"
                >Votes</button>
                <button
                  onClick={() => handleMomentarySort('newest')}
                  className="px-2 py-1 text-xs rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  title="Sort by newest"
                >Newest</button>
              </div>
              )}
            </div>

            {/* Blind voting notice */}
            {visibilityPhase === 'pre_event' && preEventView === 'blind' && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Blind voting is enabled - vote counts will be revealed when voting closes
                </p>
              </div>
            )}

            {/* Queue hidden message */}
            {!isQueueVisible() ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <Lock className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Queue is Hidden
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {visibilityPhase === 'post_close'
                    ? "The final playlist will be revealed when the event starts. Stay tuned!"
                    : visibilityPhase === 'during_event' && duringEventView === 'hide'
                    ? "The DJ has hidden the queue during the event. Enjoy the surprise!"
                    : "The queue is currently not visible."}
                </p>
              </div>
            ) : sortedVoteRequests.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <Music className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {searchQuery ? 'No matches found' : preEventView === 'own_only' ? 'No Requests Yet' : 'Queue is Empty'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchQuery
                    ? 'Try a different search term'
                    : preEventView === 'own_only'
                    ? "You haven't requested any songs yet. Submit a request to see it here!"
                    : 'Be the first to request a song!'}
                </p>
              </div>
            ) : (() => {
              // F17: Compute attendee-side cutoff index based on event end time
              const attendeeCutoffIndex = (() => {
                if (!event?.endTime) return -1;
                const now = new Date();
                const [eyr, emo, edy] = event.date ? event.date.split('-').map(Number) : [now.getFullYear(), now.getMonth() + 1, now.getDate()];
                const [endH, endM] = event.endTime.split(':').map(Number);
                const endDate = new Date(eyr, emo - 1, edy, endH, endM, 0, 0);
                const remainingMs = endDate.getTime() - now.getTime();
                if (remainingMs <= 0) return 0;
                let cumulativeMs = 0;
                // Account for now-playing song remaining time (estimate half remaining)
                if (nowPlaying?.song?.durationMs) {
                  cumulativeMs += nowPlaying.song.durationMs / 2;
                }
                for (let i = 0; i < sortedVoteRequests.length; i++) {
                  const songDuration = sortedVoteRequests[i].song?.durationMs || 210000;
                  cumulativeMs += songDuration;
                  if (cumulativeMs > remainingMs) {
                    return event.settings?.allowOverlap ? i + 1 : i;
                  }
                }
                return -1;
              })();

              return (
              <div className="space-y-2">
                {/* Now Playing card */}
                {nowPlaying && (
                  <div className="card p-4 flex items-center gap-4 border-l-4 border-green-500 bg-green-50 dark:bg-green-900/20">
                    <span className="w-8 text-center text-lg font-bold text-green-600 dark:text-green-400">
                      &#9654;
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-gray-900 dark:text-white flex items-center gap-1.5 ${expandedTitleIds.has(nowPlaying.id) ? '' : 'truncate'}`}>
                        <span className={expandedTitleIds.has(nowPlaying.id) ? 'whitespace-normal break-words cursor-pointer' : 'truncate cursor-pointer'} onClick={() => toggleTitle(nowPlaying.id)} role="button" tabIndex={0}>{nowPlaying.song.title}</span>
                        <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium">NOW PLAYING</span>
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{nowPlaying.song.artist}</p>
                    </div>
                  </div>
                )}
                {myPendingNominations.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">Your Pending Nominations</p>
                    {myPendingNominations.map((req) => (
                      <div key={req.id} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/50 opacity-75">
                        {req.song.albumArtUrl ? (
                          <img src={req.song.albumArtUrl} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded flex items-center justify-center flex-shrink-0">
                            <Music className="w-5 h-5 text-amber-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate text-sm">{req.song.title}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{req.song.artist}</p>
                        </div>
                        <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Awaiting review
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {sortedVoteRequests.map((request, index) => {
                  const currentVote = votedRequests.get(request.id);
                  const songVotedOut = isVotedOut(request);
                  const pastCutoff = attendeeCutoffIndex >= 0 && index >= attendeeCutoffIndex;
                  return (
                    <React.Fragment key={request.id}>
                      {/* F17: Cutoff divider */}
                      {!event?.settings?.hideRankingInfo && !gameplayActive && attendeeCutoffIndex >= 0 && index === attendeeCutoffIndex && (
                        <div className="flex items-center gap-2 py-1">
                          <div className="flex-1 border-t-2 border-dashed border-amber-400 dark:border-amber-500" />
                          <span className="text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            May not make the cut
                          </span>
                          <div className="flex-1 border-t-2 border-dashed border-amber-400 dark:border-amber-500" />
                        </div>
                      )}
                    <div
                      className={`card p-4 flex items-center gap-4 ${songVotedOut ? 'opacity-60 border-l-4 border-red-500' : ''} ${pastCutoff && !event?.settings?.hideRankingInfo && !gameplayActive ? 'opacity-50' : ''}`}
                    >
                      {!event?.settings?.hideRankingInfo && !gameplayActive && (
                        <span className="w-8 text-center text-lg font-bold text-primary">
                          {queuePositionMap.get(request.id) ?? (index + 1)}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        {songVotedOut && (
                          <p className="flex mb-1">
                            <button
                              type="button"
                              onClick={() => openRallyDetail(request.id)}
                              className="text-xs text-left leading-snug bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 px-2 py-0.5 rounded-md font-medium"
                              title={`It comes back when fewer than ${returnLinePercent(voteRules)}% are against it`}
                              aria-label="Voted out: see how it can come back"
                            >
                              Voted out
                            </button>
                          </p>
                        )}
                        <p className={`font-medium text-gray-900 dark:text-white flex items-center gap-1.5 ${expandedTitleIds.has(request.id) ? '' : 'truncate'}`}>
                          <span className={expandedTitleIds.has(request.id) ? 'whitespace-normal break-words cursor-pointer' : 'truncate cursor-pointer'} onClick={() => toggleTitle(request.id)} role="button" tabIndex={0}>{request.song.title}</span>
                          {request.song.explicitFlag && (
                            <span className="explicit-badge flex-shrink-0" title="Explicit content">E</span>
                          )}
                          {isCrowdFavorite(request) && (
                            <span className="text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium">{crowdFavoriteLabel(request, participantCount)}</span>
                          )}
                          {request.requestedBy.oderId === attendeeId && (
                            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium">YOUR REQUEST</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {request.song.artist}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {request.song.itunesTrackId && (
                            <a
                              href={`https://music.apple.com/us/song/${request.song.itunesTrackId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-pink-500 hover:text-pink-400 transition-colors"
                              title="Listen on Apple Music"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Apple Music
                            </a>
                          )}
                          {request.spotifyUri && (
                            <a
                              href={`https://open.spotify.com/track/${request.song.catalogIds?.spotify ?? spotifyTrackIdFrom(request.spotifyUri)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-green-500 hover:text-green-400 transition-colors"
                              title="Listen on Spotify"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Spotify
                            </a>
                          )}
                        </div>
                      </div>
                      <div className={`flex items-center gap-1.5 ${songVotedOut ? 'opacity-100' : ''}`}>
                        {areVoteCountsVisible() && trendingIds.has(request.id) && (
                          <span title="Trending! Getting lots of recent votes"><Flame className="w-5 h-5 text-orange-500 animate-pulse" /></span>
                        )}
                        {/* Upvote button */}
                        <button
                          onClick={() => handleVote(request.id, 'up')}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors ${
                            verificationRequired && !isVerified
                              ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed'
                              : currentVote === 'up'
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-900/20'
                          }`}
                        >
                          <ThumbsUp className={`w-4 h-4 ${currentVote === 'up' ? 'fill-current' : ''}`} />
                          {areVoteCountsVisible() && (
                            <span className="text-sm font-medium">{downvotingEnabled ? request.upvoteCount : request.voteCount}</span>
                          )}
                          {!areVoteCountsVisible() && <span className="text-sm font-medium">?</span>}
                        </button>
                        {/* Downvote button (only when enabled) */}
                        {downvotingEnabled && (
                          <button
                            onClick={() => handleVote(request.id, 'down')}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors ${
                              verificationRequired && !isVerified
                                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed'
                                : currentVote === 'down'
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20'
                            }`}
                          >
                            <ThumbsDown className={`w-4 h-4 ${currentVote === 'down' ? 'fill-current' : ''}`} />
                            {areVoteCountsVisible() && (
                              <span className="text-sm font-medium">{request.downvoteCount}</span>
                            )}
                          </button>
                        )}
                        {/* Flag button (when enabled) */}
                        {event?.settings?.allowAttendeeFlagging && !flaggedRequestIds.has(request.id) && (
                          <button
                            onClick={() => setFlagModal({ requestId: request.id, songTitle: request.song.title, songArtist: request.song.artist })}
                            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                            title="Flag this song"
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                        )}
                        {event?.settings?.allowAttendeeFlagging && flaggedRequestIds.has(request.id) && (
                          <span className="p-1.5 text-amber-500" title="You flagged this song">
                            <Flag className="w-4 h-4 fill-current" />
                          </span>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
                {/* Played cards */}
                {playedRequests.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 pt-2">
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                      <span className="text-xs text-gray-400 dark:text-gray-500">Played</span>
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                    </div>
                    {playedRequests.map((request) => (
                      <div key={request.id} className="card p-4 flex items-center gap-4 opacity-40">
                        <span className="w-8 text-center text-lg font-bold text-gray-400">&#10003;</span>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5 ${expandedTitleIds.has(request.id) ? '' : 'truncate'}`}>
                            <span className={expandedTitleIds.has(request.id) ? 'whitespace-normal break-words line-through cursor-pointer' : 'truncate line-through cursor-pointer'} onClick={() => toggleTitle(request.id)} role="button" tabIndex={0}>{request.song.title}</span>
                            <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium">PLAYED</span>
                          </p>
                          <p className="text-sm text-gray-400 dark:text-gray-500 truncate">{request.song.artist}</p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
              );
            })()}
          </div>
        );

      case 'rankings':
        // Ranked-choice voting mode
        if (isRankedMode) {
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Rank Your Favorites
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 space-y-0.5">
                    <span className="block">Drag ⠿ or tap a number to move a song • Changes save automatically</span>
                    <span className="block text-xs flex items-center gap-1">
                      <ThumbsUp className="w-3 h-3 text-green-500 flex-shrink-0" />
                      Upvotes help break ties between equally ranked songs
                    </span>
                    {downvotingEnabled && (
                      <span className="block text-xs flex items-center gap-1">
                        <ThumbsDown className="w-3 h-3 text-red-500 flex-shrink-0" />
                        {voteOutActive(voteRules)
                          ? `If more than ${voteRules.thresholdPercent}% of everyone taking part downvotes a song, it's voted out until fewer than ${returnLinePercent(voteRules)}% are against it.`
                          : "Downvotes count against a song's place."}
                      </span>
                    )}
                    <span className="block text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      Rank your top {rankSize} song{rankSize !== 1 ? 's' : ''}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {false && (<button
                    type="button"
                    onClick={() => openHelp('rankings')}
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="How rankings work"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>)}
                  {(rankingSaveState === 'saving' || (rankingSaveDue && rankingSaveState !== 'error')) && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Saving…
                    </span>
                  )}
                  {rankingSaveState === 'error' && (
                    <span className="text-xs text-red-600 dark:text-red-400">Not saved — retrying</span>
                  )}
                  {rankingSaveState !== 'saving' && rankingSaveState !== 'error' && !rankingSaveDue && myOrder.length > 0 && (
                    <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Saved
                    </span>
                  )}
                </div>
              </div>

              {/* Section 1: New Songs (unseen, unacknowledged) */}
              {newSongs.length > 0 && (
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-1.5">
                    New Songs ({newSongs.length})
                  </h3>
                  {newSongs.map((request, index) => renderRankingRow(request, 'new', index))}
                </div>
              )}

              <SortableList ids={rankingDragIds} onMove={handleDragRank}>
              {/* Section 2: Ranked Songs (exactly rankSize slots) */}
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5" />
                  Ranked ({rankedSongs.length}/{rankSize})
                </h3>
                {rankedSongs.length === 0 && (
                  <div className="card p-4 text-center text-sm text-gray-400 border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-lg">
                    Tap "+Rank" on a song to start ranking
                  </div>
                )}
                {rankedSongs.map((request, index) => (
                  <React.Fragment key={request.id}>
                    <SortableItem id={request.id}>{sortable => renderRankingRow(request, 'ranked', index, sortable)}</SortableItem>
                    {sortedThroughId === request.id && renderSortedThroughLine(true)}
                  </React.Fragment>
                ))}
              </div>

              {/* Section 3: Unranked Songs (acknowledged but not ranked) */}
              {unrankedSongs.length > 0 && (
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Unranked ({unrankedSongs.length})
                  </h3>
                  {!sortedThroughId && renderSortedThroughLine(false)}
                  {unrankedSongs.map((request, index) => (
                    <React.Fragment key={request.id}>
                      <SortableItem id={request.id}>{sortable => renderRankingRow(request, 'unranked', index, sortable)}</SortableItem>
                      {sortedThroughId === request.id && renderSortedThroughLine(true)}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Section: the attendee's downvoted songs, not counted in their ranking (ITER-144) */}
              {downvotedSongs.length > 0 && (
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2 flex items-center gap-1.5">
                    <ThumbsDown className="w-3.5 h-3.5" />
                    Downvoted — not counted in your ranking ({downvotedSongs.length})
                  </h3>
                  {downvotedSongs.map((request, index) => (
                    <React.Fragment key={request.id}>
                      <SortableItem id={request.id}>{sortable => renderRankingRow(request, 'downvoted', index, sortable)}</SortableItem>
                      {sortedThroughId === request.id && renderSortedThroughLine(true)}
                    </React.Fragment>
                  ))}
                </div>
              )}
              </SortableList>

              {/* Section 4: Played Songs (read-only, most recent first) */}
              {playedSongs.length > 0 && (
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1.5">
                    <Play className="w-3.5 h-3.5" />
                    Played ({playedSongs.length})
                  </h3>
                  {playedSongs.map((request) => (
                    <div
                      key={request.id}
                      className="card p-3 flex items-center gap-3 opacity-50"
                    >
                      <div className="w-4" />
                      <div className="w-6" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-500 dark:text-gray-400 truncate text-sm line-through">
                          {request.song.title}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {request.song.artist}
                        </p>
                      </div>
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}

              {rankedSongs.length === 0 && newSongs.length === 0 && unrankedSongs.length === 0 && playedSongs.length === 0 && (
                <div className="card p-8 text-center">
                  <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <Trophy className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Songs to Rank
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Wait for songs to be added to the queue, then rank them here!
                  </p>
                </div>
              )}

              {/* Rank Swap Modal — pick which ranked song to replace */}
              {rankSwapModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setRankSwapModal(null)}>
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full p-4" onClick={e => e.stopPropagation()}>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Ranking Full</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Pick a song to replace:</p>
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {rankedSongs.map((request, index) => (
                        <button
                          key={request.id}
                          onClick={() => handleRankSwap(request.id)}
                          className="w-full text-left p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                        >
                          <span className="w-6 text-center text-sm font-bold text-amber-600 dark:text-amber-400">#{index + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{request.song.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{request.song.artist}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setRankSwapModal(null)}
                      className="mt-3 w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Picker for the "sorted up to here" line */}
              {linePickerOpen && (
                <SortedLinePicker
                  order={[...rankedSongs, ...unrankedSongs].map(r => ({ id: r.id, title: r.song.title, artist: r.song.artist }))}
                  rankSize={rankSize}
                  currentId={sortedThroughId}
                  onPick={id => handleSetSortedThrough(id)}
                  onRemove={() => handleSetSortedThrough(null)}
                  onClose={() => setLinePickerOpen(false)}
                />
              )}

              {/* Move window — send a song straight to a position (GROUP-32) */}
              {moveSong && (
                <MoveSongModal
                  song={{ id: moveSong.id, title: moveSong.song.title, artist: moveSong.song.artist }}
                  isNew={!myOrder.includes(moveSong.id)}
                  order={[...rankedSongs, ...unrankedSongs].map(r => ({ id: r.id, title: r.song.title, artist: r.song.artist }))}
                  rankSize={rankSize}
                  dividerIsBelowThisSong={sortedThroughId === moveSong.id}
                  onMoveToPosition={position => handleMoveToPosition(moveSong.id, position)}
                  onPlaceBefore={beforeId => handlePlaceBefore(moveSong.id, beforeId)}
                  onMarkSortedHere={() => handleSetSortedThrough(moveSong.id)}
                  onRemoveSortedLine={() => handleSetSortedThrough(null)}
                  onClose={() => setMoveSongId(null)}
                />
              )}

            </div>
          );
        }

        // Standard voting mode - show voted songs
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">My Rankings</h2>
              <span className="text-sm text-gray-500">{myRankings.length} songs voted</span>
            </div>

            {myRankings.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <Star className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Votes Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Vote for songs in the Browse tab to see your rankings here!
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {myRankings.map((request, index) => {
                  const currentVote = votedRequests.get(request.id);
                  return (
                    <div
                      key={request.id}
                      className="card p-4 flex items-center gap-4"
                    >
                      <span className="w-8 text-center text-lg font-bold text-primary">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-gray-900 dark:text-white flex items-center gap-1.5 ${expandedTitleIds.has(request.id) ? '' : 'truncate'}`}>
                          <span className={expandedTitleIds.has(request.id) ? 'whitespace-normal break-words cursor-pointer' : 'truncate cursor-pointer'} onClick={() => toggleTitle(request.id)} role="button" tabIndex={0}>{request.song.title}</span>
                          {request.song.explicitFlag && (
                            <span className="explicit-badge flex-shrink-0" title="Explicit content">E</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {request.song.artist}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleVote(request.id, 'up')}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors ${
                            currentVote === 'up'
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-900/20'
                          }`}
                        >
                          <ThumbsUp className={`w-4 h-4 ${currentVote === 'up' ? 'fill-current' : ''}`} />
                          <span className="text-sm font-medium">{downvotingEnabled ? request.upvoteCount : request.voteCount}</span>
                        </button>
                        {downvotingEnabled && (
                          <button
                            onClick={() => handleVote(request.id, 'down')}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors ${
                              currentVote === 'down'
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20'
                            }`}
                          >
                            <ThumbsDown className={`w-4 h-4 ${currentVote === 'down' ? 'fill-current' : ''}`} />
                            <span className="text-sm font-medium">{request.downvoteCount}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'peoples-pick':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {nominationGroups.length > 0
                  ? `Songs need ${nominationGroups[0].threshold} upvotes to enter the queue.`
                  : 'No pool nominations yet.'}
              </p>
              {false && (<button
                type="button"
                onClick={() => openHelp('overflow')}
                className="text-gray-400 hover:text-primary transition-colors flex-shrink-0 ml-2"
                aria-label="How overflow pool works"
              >
                <HelpCircle className="w-4 h-4" />
              </button>)}
            </div>
            {nominationGroups.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No nominations in the pool yet</p>
            ) : (
              nominationGroups.map((nom) => (
                <div key={nom.groupKey} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  {nom.albumArtUrl && <img src={nom.albumArtUrl} alt="" className="w-10 h-10 rounded" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate text-gray-900 dark:text-white">{nom.title}</p>
                    <p className="text-xs text-gray-500 truncate">{nom.artist}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div className="bg-amber-500 h-full rounded-full transition-all"
                          style={{ width: `${Math.min(100, (nom.count / nom.threshold) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-gray-500">{nom.count}/{nom.threshold}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handlePPUpvote(nom.groupKey, nom.primaryRequestId, nom.title, nom.artist)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors text-sm ${
                        nom.hasUpvoted
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-amber-50 hover:text-amber-600'
                      }`}
                      title={nom.hasUpvoted ? 'Remove upvote' : 'Upvote'}
                    >
                      <ThumbsUp className={`w-4 h-4 ${nom.hasUpvoted ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => handlePPDismiss(nom.groupKey)}
                      className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Dismiss (hide from your view)"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      case 'my-requests': {
        const visibleMyRequests = myRequests.filter(r => !isAttendeeCancelled(r));
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">My Requests</h2>
                {false && (<button
                  type="button"
                  onClick={() => openHelp('request-statuses')}
                  className="text-gray-400 hover:text-primary transition-colors"
                  aria-label="What do the request statuses mean?"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>)}
              </div>
              <span className="text-sm text-gray-500">{visibleMyRequests.length} request{visibleMyRequests.length !== 1 ? 's' : ''}</span>
            </div>

            {visibleMyRequests.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Requests Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Submit a song request to see its status here!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {visibleMyRequests
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((request) => {
                    // Determine status display
                    let statusColor = '';
                    let statusLabel = '';
                    let statusIcon = null;

                    switch (request.status) {
                      case 'pending':
                        statusColor = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
                        statusLabel = 'Pending';
                        statusIcon = <Clock className="w-3 h-3" />;
                        break;
                      case 'queued':
                        statusColor = 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
                        statusLabel = 'Queued';
                        statusIcon = <ListMusic className="w-3 h-3" />;
                        break;
                      case 'nowPlaying':
                        statusColor = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                        statusLabel = 'Now Playing';
                        statusIcon = <Play className="w-3 h-3 fill-current" />;
                        break;
                      case 'played':
                        statusColor = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                        statusLabel = 'Played';
                        statusIcon = <Music className="w-3 h-3" />;
                        break;
                      case 'overflow':
                        statusColor = 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
                        statusLabel = 'In Overflow';
                        statusIcon = <ListMusic className="w-3 h-3" />;
                        break;
                      case 'rejected':
                        statusColor = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                        statusLabel = 'Declined';
                        statusIcon = null;
                        break;
                    }

                    // Override for overflow songs promoted to queue by another attendee
                    if (request.status === 'rejected' && (
                      request.rejectionReason?.includes('Promoted to queue') ||
                      request.rejectionReason?.includes('Merged')
                    )) {
                      statusColor = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                      statusLabel = 'In Queue (via another request)';
                      statusIcon = <CheckCircle className="w-3 h-3" />;
                    }

                    // Override for pending overflow
                    if (request.status === 'pending' && request.isOverflow) {
                      statusColor = 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
                      statusLabel = 'Overflow — Pending Approval';
                      statusIcon = <Clock className="w-3 h-3" />;
                    }

                    return (
                      <div
                        key={request.id}
                        className={`card p-4 ${request.status === 'nowPlaying' ? 'ring-2 ring-green-500 ring-offset-2 dark:ring-offset-gray-900' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                              {request.song.title}
                              {request.song.explicitFlag && (
                                <span className="explicit-badge flex-shrink-0" title="Explicit content">E</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {request.song.artist}
                            </p>
                            {request.status === 'queued' && (isVotedOut(request) || isCrowdFavorite(request)) && (
                              <p className="mt-1 flex flex-wrap gap-1">
                                {isVotedOut(request) && (
                                  <button
                                    type="button"
                                    onClick={() => openRallyDetail(request.id)}
                                    className="text-xs text-left bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded-full font-medium"
                                    aria-label="Voted out: see how it can come back"
                                  >
                                    Voted out
                                  </button>
                                )}
                                {isCrowdFavorite(request) && (
                                  <span className="text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded-full font-medium">{crowdFavoriteLabel(request, participantCount)}</span>
                                )}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Requested {new Date(request.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                            {statusIcon}
                            <span>{statusLabel}</span>
                          </div>
                        </div>
                        {request.status === 'queued' && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                            <ThumbsUp className="w-3 h-3" />
                            <span>{request.voteCount} vote{request.voteCount !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        {renderCancelAction(request)}
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Overflow Pool Section */}
            {OVERFLOW_POOL_ENABLED && event?.settings?.overflowEnabled && (() => {
              const overflowSongs = attendeeId
                ? getOverflowByAttendee(event.id, attendeeId, [...requests, ...myRequests.filter(r => !requests.find(rr => rr.id === r.id))])
                : [];
              if (overflowSongs.length === 0) return null;
              return (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-indigo-700 dark:text-indigo-400 flex items-center gap-2">
                      <ListMusic className="w-4 h-4" />
                      My Overflow Pool ({overflowSongs.length})
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Keep your top picks at the top — they get promoted first if the DJ raises the limit
                    </p>
                  </div>
                  <div className="space-y-2">
                    <SortableList
                      ids={overflowSongs.map(s => s.id)}
                      onMove={(activeId, overId) => {
                        const ids = overflowSongs.map(s => s.id);
                        const from = ids.indexOf(activeId);
                        const to = ids.indexOf(overId);
                        if (from < 0 || to < 0) {
                          toast('That song just left your overflow pool.');
                          return;
                        }
                        void saveOverflowOrder(arrayMove(ids, from, to));
                      }}
                    >
                    {overflowSongs.map((song, index) => (
                      <SortableItem key={song.id} id={song.id}>{sortable => (
                      <div
                        ref={sortable.setNodeRef}
                        style={sortable.style}
                        className={`card p-3 flex items-center gap-3 border-l-4 border-indigo-400 dark:border-indigo-600 ${sortable.isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
                      >
                        <DragHandle
                          bindings={sortable.handleProps}
                          label={`Drag ${song.song.title} to a new position`}
                          className="-ml-1.5 -mr-1.5"
                        />
                        <span className="text-xs font-mono text-gray-400 w-5 text-center">{index + 1}</span>
                        {song.song.albumArtUrl && (
                          <img
                            src={song.song.albumArtUrl}
                            alt=""
                            className="w-10 h-10 rounded object-cover flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{song.song.title}</p>
                          <p className="text-xs text-gray-500 truncate">{song.song.artist}</p>
                        </div>
                        <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          song.status === 'overflow'
                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {song.status === 'overflow' ? 'Waiting' : 'Pending'}
                        </div>
                        <button
                          onClick={() => handleCancelOwnRequest(song.id, 'Removed from overflow pool')}
                          disabled={cancellingId === song.id}
                          className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-50 transition-colors"
                          title="Remove from overflow"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      )}</SortableItem>
                    ))}
                    </SortableList>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      }

      case 'messages':
        // Mark all messages as read when viewing the messages tab
        if (event && attendeeId && djMessages.length > 0) {
          // Use setTimeout to mark messages as read after render
          setTimeout(async () => {
            await markAllMessagesReadByAttendee(event.id, attendeeId);
          }, 500);
        }

        return (
          <div className="space-y-4">
            {/* Answered Polls (inline summary + results) */}
            {activePolls.filter(p => answeredPollIds.has(p.id)).length > 0 && (
              <div className="space-y-3">
                {activePolls.filter(p => answeredPollIds.has(p.id)).map(poll => (
                  <div key={poll.id}>
                    <PollDisplay
                      poll={poll}
                      alreadyAnswered={true}
                      previousAnswers={previousAnswersMap.get(poll.id)}
                      isUpdate={poll.editable}
                      onSubmit={async (answers) => {
                        if (!event?.id || !attendeeId || !poll.editable) return;
                        await submitPollResponse(poll.id, event.id, attendeeId, answers, true);
                        setPreviousAnswersMap(prev => {
                          const next = new Map(prev);
                          next.set(poll.id, answers);
                          return next;
                        });
                      }}
                    />
                    {poll.showResultsToAttendees && (
                      <div className="mt-2 card p-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Results</h4>
                        <PollResultsComponent pollId={poll.id} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">DJ Messages</h2>

            {djMessages.length === 0 && activePolls.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <MessageSquare className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Messages Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  The DJ hasn't sent any messages to attendees yet.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {djMessages.map((msg) => (
                  <div key={msg.id} className="card p-4">
                    <p className="text-gray-900 dark:text-white">{msg.content}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'stats':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Event Stats</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="card p-4 text-center">
                <p className="text-3xl font-bold text-primary">{requests.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Songs in Queue</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-3xl font-bold text-primary">{myRankings.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Your Votes</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-3xl font-bold text-primary">
                  {requests.reduce((sum, r) => sum + r.voteCount, 0)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Votes</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-3xl font-bold text-primary">{djMessages.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">DJ Messages</p>
              </div>
            </div>

            {/* Top Voted Songs */}
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Top Voted Songs</h3>
              {requests.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No songs in queue yet</p>
              ) : (
                <div className="space-y-2">
                  {[...requests]
                    .sort((a, b) => b.voteCount - a.voteCount)
                    .slice(0, 5)
                    .map((request, index) => (
                      <div key={request.id} className="flex items-center gap-3">
                        <span className="w-6 text-center text-sm font-bold text-primary">{index + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {request.song.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {request.song.artist}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {trendingIds.has(request.id) && (
                            <span title="Trending!"><Flame className="w-4 h-4 text-orange-500 animate-pulse" /></span>
                          )}
                          <span className="text-sm font-medium text-primary">{request.voteCount} votes</span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'feedback':
        return (
          <div className="space-y-6">
            <div className="card p-6">
              {tookPart === 'no' ? (
                <p className="text-center text-gray-600 dark:text-gray-400">Feedback is for people who were at the event.</p>
              ) : feedbackSubmitted && existingFeedback ? (
                // Show submitted feedback
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center">
                    <Check className="w-8 h-8 text-success" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Thanks for your feedback!
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Your feedback helps the DJ improve future events.
                  </p>

                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-left">
                    <div className="mb-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Your rating:</span>
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-6 h-6 ${
                              star <= existingFeedback.rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300 dark:text-gray-600'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    {existingFeedback.message && (
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">Your message:</span>
                        <p className="mt-1 text-gray-900 dark:text-white">
                          {existingFeedback.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Show feedback form
                <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                  <div className="text-center">
                    <Heart className="w-12 h-12 mx-auto text-primary mb-3" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      How was your experience?
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      Let the DJ know how they did tonight
                    </p>
                  </div>

                  {/* Star Rating */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                      Your Rating
                    </label>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFeedbackRating(star)}
                          className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary rounded"
                          aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                        >
                          <Star
                            className={`w-10 h-10 transition-colors ${
                              star <= feedbackRating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-yellow-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    {feedbackRating > 0 && (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                        {feedbackRating === 1 && 'Poor'}
                        {feedbackRating === 2 && 'Fair'}
                        {feedbackRating === 3 && 'Good'}
                        {feedbackRating === 4 && 'Great'}
                        {feedbackRating === 5 && 'Excellent!'}
                      </p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Comments (optional)
                    </label>
                    <textarea
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder="Tell the DJ what you loved or what could be improved..."
                      className="input min-h-[100px] resize-none"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                      {feedbackMessage.length}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={feedbackRating === 0}
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Submit Feedback
                  </button>
                </form>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Compute polls that need overlay (unanswered + not dismissed, OR editable + resent)
  const overlayPoll = activePolls.find(p => {
    // Never-answered, not dismissed = always show
    if (!answeredPollIds.has(p.id) && !dismissedPollIds.has(p.id)) return true;
    // Editable + resent: show if DJ resent since we last saw it
    if (p.editable && p.lastResendAt) {
      const lastSeen = seenResendTimestamps.get(p.id);
      if (!lastSeen || p.lastResendAt > lastSeen) return true;
    }
    return false;
  });
  const overlayIsUpdate = overlayPoll ? answeredPollIds.has(overlayPoll.id) : false;

  // Helper to mark a resend as seen and persist to localStorage
  const markResendSeen = (poll: Poll) => {
    if (poll.lastResendAt && event?.id) {
      setSeenResendTimestamps(prev => {
        const next = new Map(prev);
        next.set(poll.id, poll.lastResendAt!);
        localStorage.setItem(
          `votebeats_poll_resend_seen_${event.id}`,
          JSON.stringify(Array.from(next.entries()))
        );
        return next;
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4 p-4">
      {/* Sticky event name — appears below header when title scrolls out of view */}
      {showStickyEventName && event && (
        <div ref={eventNameBannerRef} className="fixed left-0 right-0 z-40 bg-primary/95 backdrop-blur-sm text-white text-center py-1.5 text-sm font-medium shadow-sm"
          style={{ top: headerHeight }}>
          {event.name}
        </div>
      )}

      {/* Fullscreen Poll Overlay */}
      {overlayPoll && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6">
            <PollDisplay
              poll={overlayPoll}
              alreadyAnswered={false}
              overlay={true}
              previousAnswers={previousAnswersMap.get(overlayPoll.id)}
              isUpdate={overlayIsUpdate}
              onSubmit={async (answers) => {
                if (!event?.id || !attendeeId) return;
                await submitPollResponse(overlayPoll.id, event.id, attendeeId, answers, overlayIsUpdate);
                setAnsweredPollIds(prev => new Set(prev).add(overlayPoll.id));
                setPreviousAnswersMap(prev => {
                  const next = new Map(prev);
                  next.set(overlayPoll.id, answers);
                  return next;
                });
                markResendSeen(overlayPoll);
              }}
              onDismiss={overlayPoll.mandatory ? undefined : () => {
                setDismissedPollIds(prev => new Set(prev).add(overlayPoll.id));
                markResendSeen(overlayPoll);
              }}
            />
            {/* Show results inline if DJ enabled and just answered */}
            {overlayPoll.showResultsToAttendees && answeredPollIds.has(overlayPoll.id) && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Results</h4>
                <PollResultsComponent pollId={overlayPoll.id} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Push Notification Opt-in Banner */}
      {showPushBanner && (
        <div className="flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-lg px-4 py-3">
          <Bell className="w-5 h-5 text-primary flex-shrink-0" />
          <p className="text-sm text-gray-800 dark:text-gray-200 flex-1">
            Get notified when your song plays!
          </p>
          <button
            onClick={async () => {
              const token = await requestPushPermission(attendeeId, event?.id);
              setShowPushBanner(false);
              if (token) {
                toast.success('Notifications enabled!');
              }
            }}
            className="text-sm font-semibold text-primary hover:text-primary/80 whitespace-nowrap"
          >
            Enable
          </button>
          <button
            onClick={() => setShowPushBanner(false)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Event Header */}
      <div className="space-y-1">
        {/* Title row: QR + Name + Refresh/Tutorial/A2HS */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowQrModal(true)}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Show QR Code"
          >
            <QrCode className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
          <h1 ref={eventTitleRef} className="flex-1 text-xl font-bold text-gray-900 dark:text-white truncate text-center">{event.name}</h1>
          <div className="flex-shrink-0 flex items-center gap-0.5">
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-gray-600 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            {false && (<button
              onClick={() => openHelp()}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
              title="How to use VoteBeats"
              aria-label="Show tutorial"
            >
              <HelpCircle className="w-4 h-4 text-primary" />
            </button>)}
            <button
              onClick={() => setShowA2HSModal(true)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Add to Home Screen"
            >
              <Smartphone className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
        {/* Date / time / venue — single line */}
        {(() => {
          const parts = formatEventDateTimeParts(event.date, event.startTime);
          return (
            <p className="text-xs text-center truncate">
              <span className="text-teal-500 dark:text-teal-400">{parts.weekday}</span>
              {', '}
              <span className="text-gray-700 dark:text-gray-300">{parts.date}</span>
              {parts.time && <>
                {' \u2022 '}
                <span className="text-amber-500 dark:text-amber-400">{parts.time}</span>
              </>}
              {event.location && <span className="text-gray-600 dark:text-gray-400"> {'\u2022'} {event.location}</span>}
            </p>
          );
        })()}
        {/* Contact info — collapsed on mobile */}
        {(event.address || event.contactInfo) && (
          <details className="text-center sm:[&>summary]:hidden sm:open">
            <summary className="text-xs text-primary cursor-pointer list-none select-none">Contact info &amp; map</summary>
            <div className="flex items-center justify-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {event.contactInfo?.name && (
                <span className="font-medium text-gray-700 dark:text-gray-300">{event.contactInfo.name}</span>
              )}
              {event.address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary flex items-center gap-0.5 underline"
                >
                  <MapPin className="w-3 h-3" />Map
                </a>
              )}
              {event.contactInfo?.phone && (
                <a href={`tel:${event.contactInfo.phone}`} className="hover:text-primary flex items-center gap-0.5">
                  <Phone className="w-3 h-3" />{event.contactInfo.phone}
                </a>
              )}
              {event.contactInfo?.email && (
                <a href={`mailto:${event.contactInfo.email}`} className="hover:text-primary flex items-center gap-0.5">
                  <Mail className="w-3 h-3" />Email
                </a>
              )}
            </div>
          </details>
        )}
        <div className="text-center">
          <a
            href={nextEventContactPath(event.id, 'event-link')}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary underline-offset-2 hover:underline"
            data-testid="next-event-link"
          >
            VoteBeats at your next event?
          </a>
        </div>
      </div>

      {/* Voting Opens Countdown */}
      {voteOpenCountdown && !event.settings.votingClosed && (
        <div className="card p-3 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40 border-blue-300 dark:border-blue-700">
          <div className="flex items-center justify-center gap-2">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Voting opens in
            </span>
            <span className="text-lg font-bold text-blue-800 dark:text-blue-200">
              {voteOpenCountdown}
            </span>
          </div>
        </div>
      )}

      {/* Vote Close Countdown */}
      {voteCloseCountdown && !event.settings.votingClosed && (
        <div className={`card p-3 ${
          countdownUrgency === 'last-call'
            ? 'bg-gradient-to-r from-red-100 to-red-200 dark:from-red-900/40 dark:to-red-800/40 border-red-400 dark:border-red-600 animate-pulse'
            : countdownUrgency === 'urgent'
            ? 'bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 border-orange-400 dark:border-orange-600'
            : 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800'
        }`}>
          <div className="flex items-center justify-center gap-2">
            <Clock className={`w-5 h-5 ${
              countdownUrgency === 'last-call'
                ? 'text-red-600 dark:text-red-400'
                : countdownUrgency === 'urgent'
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-amber-600 dark:text-amber-400'
            }`} />
            <span className={`text-sm font-medium ${
              countdownUrgency === 'last-call'
                ? 'text-red-800 dark:text-red-200'
                : countdownUrgency === 'urgent'
                ? 'text-orange-800 dark:text-orange-200'
                : 'text-amber-800 dark:text-amber-200'
            }`}>
              {countdownUrgency === 'last-call' ? '🚨 LAST CALL!' : 'Voting closes in:'}
            </span>
            <span className={`text-lg font-bold tabular-nums ${
              countdownUrgency === 'last-call'
                ? 'text-red-600 dark:text-red-400'
                : countdownUrgency === 'urgent'
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-amber-600 dark:text-amber-400'
            }`}>
              {voteCloseCountdown}
            </span>
          </div>
        </div>
      )}

      {!isRequestsClosed() && <RequestsCloseNotice closeAtMs={requestsCloseAtMs} nowMs={requestWindowNowMs} />}

      {/* Voting/Requests Closed Banner */}
      {(event.settings.votingClosed || areRequestsClosed(event.settings, Date.now())) && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-700">
          <div className="flex items-center justify-center gap-3">
            <Lock className="w-6 h-6 text-red-500 dark:text-red-400" />
            <span className="text-base font-semibold text-red-700 dark:text-red-300">
              {event.settings.votingClosed && areRequestsClosed(event.settings, Date.now())
                ? 'Voting has closed for this event'
                : areRequestsClosed(event.settings, Date.now())
                  ? requestsClosedBannerMessage(attendeeVotingStyle)
                  : 'Voting is paused'}
            </span>
            {!PEOPLES_PICK_HIDDEN && event.settings.superRequestEnabled && isRequestsClosed() && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 text-center">
                You can still nominate songs for The People's Pick!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Queue Full Banner */}
      {event && requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying').length >= (event.settings.maxQueueSize ?? 50) && !(event.settings.votingClosed || areRequestsClosed(event.settings, Date.now())) && (
        <div className="card p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-700">
          <div className="flex flex-col items-center justify-center gap-1">
            <span className="text-base font-semibold text-orange-700 dark:text-orange-300">
              The queue is full ({requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying').length}/{event.settings.maxQueueSize ?? 50})
            </span>
            {!PEOPLES_PICK_HIDDEN && event.settings.superRequestEnabled && (
              <p className="text-xs text-orange-600 dark:text-orange-400 text-center">
                You can still nominate songs for The People's Pick!
              </p>
            )}
          </div>
        </div>
      )}

      {/* On-Site Verification Banner */}
      {verificationRequired && !isVerified && verificationChecked && (
        <div className="card p-3 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-teal-200 dark:border-teal-800">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-teal-800 dark:text-teal-200">
                On-Site Verification Required
              </p>
              <p className="text-xs text-teal-600 dark:text-teal-400">
                Scan the QR code at the venue to request songs and vote. You can still browse the queue.
              </p>
            </div>
            <button
              onClick={() => setShowQrScanner(true)}
              className="flex-shrink-0 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 transition-colors"
            >
              <Camera className="w-3.5 h-3.5" />
              Scan
            </button>
          </div>
        </div>
      )}

      {/* Now Playing Section - Always visible at top */}
      {nowPlaying && (
        <div className="card p-4 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/30">
          <div className="flex items-center gap-2 mb-3">
            <Play className="w-5 h-5 text-primary fill-primary" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Now Playing</h2>
          </div>
          <div className="flex items-center gap-4">
            {/* Album Art */}
            <div className="w-16 h-16 flex-shrink-0 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
              {nowPlaying.song.albumArtUrl ? (
                <img
                  src={nowPlaying.song.albumArtUrl}
                  alt={`${nowPlaying.song.title} album art`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Music className="w-8 h-8 text-gray-400" />
              )}
            </div>
            {/* Song Info */}
            <div className="flex-1 min-w-0">
              <h3 className={`text-lg font-bold text-gray-900 dark:text-white cursor-pointer ${expandedTitleIds.has('np-bottom') ? 'whitespace-normal break-words' : 'truncate'}`} onClick={() => toggleTitle('np-bottom')} role="button" tabIndex={0}>
                {nowPlaying.song.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                {nowPlaying.song.artist}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <ThumbsUp className="w-3 h-3 text-primary" />
                <span className="text-xs font-medium text-primary">{nowPlaying.voteCount} votes</span>
              </div>
            </div>
            {/* Flag button on now playing */}
            {event?.settings?.allowAttendeeFlagging && !flaggedRequestIds.has(nowPlaying.id) && (
              <button
                onClick={() => setFlagModal({ requestId: nowPlaying.id, songTitle: nowPlaying.song.title, songArtist: nowPlaying.song.artist })}
                className="p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors flex-shrink-0"
                title="Flag this song"
              >
                <Flag className="w-5 h-5" />
              </button>
            )}
            {event?.settings?.allowAttendeeFlagging && flaggedRequestIds.has(nowPlaying.id) && (
              <span className="p-2 text-amber-500 flex-shrink-0" title="You flagged this song">
                <Flag className="w-5 h-5 fill-current" />
              </span>
            )}
          </div>
        </div>
      )}

      {/* Event awards (ITER-041) */}
      {eventAwards && <EventAwardsCard awards={eventAwards} attendeeId={attendeeId || undefined} collapsible />}

      {/* Tab Navigation */}
      <div
        className="flex overflow-x-auto gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 sticky z-30 shadow-sm"
        style={{ top: headerHeight + (showStickyEventName ? (eventNameBannerRef.current?.offsetHeight ?? 30) : 0) }}
      >
        {TABS.filter((tab) => {
          if (tab.id === 'browse') return showVoteTab;
          if (tab.id === 'rankings') return showRankTab;
          if (tab.id === 'peoples-pick') return showPeoplesPickTab;
          return true;
        }).map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-primary shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <div className="relative flex-shrink-0">
                <Icon className={`w-4 h-4 ${tab.color}`} />
                {tab.id === 'peoples-pick' && ppBadgeCount > 0 && activeTab !== 'peoples-pick' && (
                  <span className="absolute -top-1 -right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                )}
              </div>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Prediction Game Countdown Banner */}
      {activePredictionGame?.status === 'countdown' && activePredictionGame.countdownVisible && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            Prediction game starts in {activePredictionGame.songsRemaining} song{activePredictionGame.songsRemaining !== 1 ? 's' : ''}!
          </span>
        </div>
      )}

      {/* Prediction Play Modal */}
      {showPredictionModal && activePredictionGame && (activePredictionGame.status === 'active' || activePredictionGame.status === 'revealing') && attendeeId && (
        <PredictionPlayModal
          game={activePredictionGame}
          eventId={event.id}
          attendeeId={attendeeId}
          nickname={nickname || undefined}
          queuedSongs={requests.filter(r => r.status === 'queued')}
          onClose={() => setShowPredictionModal(false)}
        />
      )}

      {showTriviaModal && activeTriviaGame && attendeeId && (
        <TriviaPlayModal
          game={activeTriviaGame}
          eventId={event.id}
          attendeeId={attendeeId}
          nickname={nickname || undefined}
          playedSongs={playedRequests}
          onClose={() => {
            dismissedTriviaGameId.current = activeTriviaGame.id;
            setShowTriviaModal(false);
          }}
        />
      )}

      {rallyNoticeRequest && !rallyDetail && (
        <VotedOutModal
          song={{
            id: rallyNoticeRequest.id,
            title: rallyNoticeRequest.song.title,
            artist: rallyNoticeRequest.song.artist,
            sharePercent: rallyShareOf(rallyNoticeRequest.rallyTrend, sharePercent(rallyNoticeRequest.downvoteCount, participantCount)),
          }}
          returnPercent={returnLinePercent(voteRules)}
          saving={rallySaving}
          onHelp={handleRallyHelp}
          onDontCare={handleRallyDontCare}
          onClose={closeRallyNotice}
        />
      )}

      {rallyDetail && rallyDetailRequest && (
        <RallyDetailModal
          song={{ id: rallyDetailRequest.id, title: rallyDetailRequest.song.title, artist: rallyDetailRequest.song.artist }}
          sharePercent={rallyShareOf(rallyDetailRequest.rallyTrend, sharePercent(rallyDetailRequest.downvoteCount, participantCount))}
          returnPercent={returnLinePercent(voteRules)}
          trend={rallyDetailRequest.rallyTrend}
          choice={rallyChoices?.get(rallyDetailRequest.id)}
          goal={rallyGoalOf(rallyChoices?.get(rallyDetailRequest.id), downvotedIds.has(rallyDetailRequest.id))}
          reason={rallyDetail.reason}
          pushState={rallyPushState}
          saving={rallySaving}
          onChoose={choice => { void saveRallyChoice(rallyDetailRequest.id, choice); }}
          onEnableAlerts={handleRallyEnableAlerts}
          onClose={() => setRallyDetail(null)}
        />
      )}

      {renderWrapUpModal()}

      {/* Event awards reveal pop-up (ITER-041) */}
      {renderAwardModal()}

      {/* Dance-off vote pop-up, "voting open" banner and result banner (ITER-022) */}
      {latestDanceOff?.status === 'open' && attendeeId && attendeeId !== 'pending-auth' && attendeeId !== event.djId && (
        showDanceOffModal ? (
          <DanceOffVoteModal
            danceOff={latestDanceOff}
            attendeeId={attendeeId}
            onClose={() => {
              dismissedDanceOffId.current = latestDanceOff.id;
              setShowDanceOffModal(false);
            }}
          />
        ) : (
          <DanceOffResultBanner danceOff={latestDanceOff} onOpenVote={() => setShowDanceOffModal(true)} />
        )
      )}
      {latestDanceOff?.status === 'closed'
        && latestDanceOff.id !== dismissedDanceOffResultId
        && msLeftInGameBannerWindow(latestDanceOff.closedAt, gameBannerClock) > 0 && (
        <DanceOffResultBanner danceOff={latestDanceOff} onDismiss={() => setDismissedDanceOffResultId(latestDanceOff.id)} />
      )}

      {/* Request roulette spotlight (ITER-020) */}
      {latestRouletteRound
        && latestRouletteRound.id !== dismissedRouletteRoundId
        && msLeftInGameBannerWindow(latestRouletteRound.drawnAt, gameBannerClock) > 0 && (
        <RouletteSpotlightBanner
          round={latestRouletteRound}
          attendeeId={attendeeId}
          onDismiss={() => setDismissedRouletteRoundId(latestRouletteRound.id)}
        />
      )}

      {/* Prize Claim Banner */}
      {unclaimedPrizes.filter(p => !dismissedPrizeIds.has(p.id)).slice(0, 1).map(prize => (
        <PrizeClaimBanner
          key={prize.id}
          prize={prize}
          onClaim={() => {
            if (prize.type === 'pick_next_song') {
              setPrizeSongPickerPrize(prize);
            } else {
              claimPrize(prize.id).then(() => {
                toast.success('Prize claimed!');
              }).catch(() => toast.error('Failed to claim prize'));
            }
          }}
          onDismiss={() => setDismissedPrizeIds(prev => new Set(prev).add(prize.id))}
        />
      ))}

      {/* Shoutout Overlay */}
      {activeShoutouts.filter(p => !dismissedPrizeIds.has(p.id)).slice(0, 1).map(prize => (
        <ShoutoutOverlay
          key={prize.id}
          prize={prize}
          onDismiss={() => setDismissedPrizeIds(prev => new Set(prev).add(prize.id))}
        />
      ))}

      {/* Prize Song Picker (full-screen) */}
      {prizeSongPickerPrize && (
        <PrizeSongPicker
          eventId={event.id}
          prizeId={prizeSongPickerPrize.id}
          attendeeId={attendeeId}
          expiresAt={prizeSongPickerPrize.expiresAt}
          blockExplicit={event.settings.blockExplicit}
          coApprovalEnabled={event.settings.coApprovalEnabled ?? false}
          autoApprovalHold={event.autoApprovalHold === true}
          searchCatalog={searchCatalog}
          onComplete={() => setPrizeSongPickerPrize(null)}
          onCancel={() => setPrizeSongPickerPrize(null)}
        />
      )}

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {renderTabContent()}
      </div>

      {/* Footer Info */}
      <div className="space-y-2 text-center text-sm text-gray-500 dark:text-gray-400">
        <div>Event ID: {event.id}</div>
        <div className="flex items-center justify-center gap-1">
          <User className="w-3 h-3" />
          <span>Your ID: {attendeeId.substring(0, 8)}...</span>
        </div>

        {/* Code Word Section */}
        <div className="pt-2">
          {!showCodeWordPanel && !showLinkQr ? (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCodeWordPanel(true)}
                className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 transition-colors"
              >
                <Link2 className="w-3.5 h-3.5" />
                <span>{hasCodeWordSet ? 'Code Word Set' : 'Link Another Device'}</span>
              </button>
              {hasCodeWordSet && linkQrUrl && (
                <button
                  onClick={() => setShowLinkQr(true)}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary transition-colors"
                  title="Show QR code to link another device"
                >
                  <QrCode className="w-3.5 h-3.5" />
                  QR
                </button>
              )}
            </div>
          ) : showLinkQr && linkQrUrl ? (
            <div className="card p-4 text-center max-w-xs mx-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm flex items-center gap-2">
                  <QrCode className="w-4 h-4" />
                  Scan to Link Device
                </h4>
                <button onClick={() => setShowLinkQr(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <img src={linkQrUrl} alt="Link device QR code" className="mx-auto rounded-lg" width={200} height={200} />
              <p className="text-xs text-gray-500 mt-2">
                Scan this QR code on your other device to link your identity
              </p>
            </div>
          ) : (
            <div className="card p-4 text-left max-w-sm mx-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Cross-Device Sync
                </h4>
                <button
                  onClick={() => {
                    setShowCodeWordPanel(false);
                    setCodeWordInput('');
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Set a code word to sync your votes and requests across devices.
              </p>

              <input
                type="text"
                value={codeWordInput}
                onChange={(e) => setCodeWordInput(e.target.value.toUpperCase())}
                placeholder="Enter code word (min 4 chars)"
                className="input text-sm w-full mb-3"
                maxLength={20}
              />

              <div className="flex gap-2">
                <button
                  onClick={handleSetCodeWord}
                  className="flex-1 btn btn-primary text-sm py-1.5"
                  title="Set code word on this device"
                >
                  <Check className="w-3.5 h-3.5 mr-1" />
                  Set
                </button>
                <button
                  onClick={handleLinkWithCodeWord}
                  className="flex-1 btn btn-secondary text-sm py-1.5"
                  title="Link to existing code word"
                >
                  <Link2 className="w-3.5 h-3.5 mr-1" />
                  Link
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                <strong>Set:</strong> Create new code word on this device<br />
                <strong>Link:</strong> Connect to code word set on another device
              </p>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          {resolvedTheme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          <span>{resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
        </button>
      </div>

      {/* QR Code Share Modal */}
      {showQrModal && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQrModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl max-w-sm w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Share Event
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Scan to join
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowQrModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-lg shadow-inner">
                {qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt="Event QR Code"
                    className="w-56 h-56 object-contain"
                  />
                ) : (
                  <div className="w-56 h-56 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            <p className="text-center text-xs text-gray-500 dark:text-gray-400 mb-4 truncate px-2">
              {eventShareUrl}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleCopyLink}
                className="btn btn-secondary flex-1"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </button>
              <button
                onClick={handleShareEvent}
                className="btn btn-primary flex-1"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Home Screen Instructions Modal */}
      {showA2HSModal && event && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowA2HSModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl max-w-sm w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Add to Home Screen
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Quick access to this event
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowA2HSModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Shortcut name:</p>
              <p className="font-medium text-gray-900 dark:text-white text-sm">
                {event.name}, {formatShortDate(event.date)}
              </p>
            </div>

            {getDevicePlatform() === 'ios' ? (
              <div className="space-y-3 mb-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Instructions for Safari:
                </h4>
                <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Tap the <strong>Share</strong> button (square with arrow) at the bottom of Safari</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>Change the name to: <strong>{event.name}, {formatShortDate(event.date)}</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <span>Tap <strong>&quot;Add&quot;</strong> in the top right</span>
                  </li>
                </ol>
              </div>
            ) : getDevicePlatform() === 'android' ? (
              <div className="space-y-3 mb-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Instructions for Chrome:
                </h4>
                <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Tap the <strong>menu</strong> button (three dots) in the top right</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Tap <strong>&quot;Add to Home screen&quot;</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>Change the name to: <strong>{event.name}, {formatShortDate(event.date)}</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <span>Tap <strong>&quot;Add&quot;</strong></span>
                  </li>
                </ol>
              </div>
            ) : (
              <div className="space-y-3 mb-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Instructions:
                </h4>
                <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Bookmark this page (<strong>Ctrl+D</strong> or <strong>Cmd+D</strong>) for quick access</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Rename the bookmark to: <strong>{event.name}, {formatShortDate(event.date)}</strong></span>
                  </li>
                </ol>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowA2HSModal(false)}
                className="btn btn-secondary flex-1"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const shortcutName = `${event.name}, ${formatShortDate(event.date)}`;
                  navigator.clipboard.writeText(shortcutName).then(() => {
                    toast.success('Shortcut name copied!');
                  }).catch(() => {
                    toast.success('Shortcut name copied!');
                  });
                }}
                className="btn btn-primary flex-1"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Name
              </button>
            </div>
          </div>
        </div>
      )}

      {/* F22b: Floating save button when ranking has unsaved changes */}

      {/* F21: Duplicate request modal */}
      {duplicateModal && duplicateExistingRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeDuplicateModalAndReset} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Song Already Requested</h3>
              <button
                onClick={closeDuplicateModalAndReset}
                className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex items-center gap-3">
              {duplicateExistingRequest.song.albumArtUrl ? (
                <img src={duplicateExistingRequest.song.albumArtUrl} alt={duplicateExistingRequest.song.title} className="w-12 h-12 rounded object-cover" />
              ) : (
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                  <Music className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{duplicateExistingRequest.song.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{duplicateExistingRequest.song.artist}</p>
                <p className="text-xs text-gray-500">{duplicateExistingRequest.voteCount} vote{duplicateExistingRequest.voteCount !== 1 ? 's' : ''}</p>
              </div>
            </div>
            {duplicateType === 'same-user' ? (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  You already requested this song. Would you like to vote for it instead?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={closeDuplicateModalAndReset}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const voted = await handleVote(duplicateExistingRequest.id, 'up');
                      setDuplicateModal(false);
                      if (voted) {
                        toast.success('Vote added!');
                        resetRequestForm();
                      }
                    }}
                    className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Vote for It
                  </button>
                </div>
              </div>
            ) : isSuperRequestMode() ? (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  This song is already nominated for People's Pick. Nominate it to count toward the threshold!
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={async () => {
                      if (!event) return;
                      // Create a nomination for the existing song (counts as a unique attendee nomination)
                      try {
                        await createRequest(event.id, {
                          song: duplicateExistingRequest.song,
                          requestedBy: { oderId: attendeeId, nickname: nickname.trim() || undefined },
                          spotifyUri: duplicateExistingRequest.spotifyUri,
                          superRequest: true,
                        });
                        const threshold = event.settings.superRequestThresholdType === 'percentage'
                          ? (event.settings.superRequestThresholdPercent ?? 10)
                          : (event.settings.superRequestThresholdCount ?? 5);
                        const allReqs = await getRequestsByEventId(event.id);
                        const totalAttendees = new Set(allReqs.map(r => r.requestedBy.oderId)).size;
                        const { approved, count } = await checkAndApproveSuperRequest(
                          event.id, duplicateExistingRequest.song.title, duplicateExistingRequest.song.artist,
                          threshold, totalAttendees, event.settings.superRequestThresholdType ?? 'count'
                        );
                        const effectiveThreshold = event.settings.superRequestThresholdType === 'percentage'
                          ? Math.max(2, Math.ceil((threshold / 100) * totalAttendees))
                          : threshold;
                        if (approved) {
                          toast.success(`The People's Pick! "${duplicateExistingRequest.song.title}" added to queue with ${count} nominations!`, { duration: 8000, icon: '👑' });
                        } else if (event.settings.coApprovalEnabled) {
                          toast.success("Nomination sent for review! Once approved, it joins The People's Pick pool.", { duration: 6000, icon: '✊' });
                        } else {
                          toast.success(`Nomination added! ${count} of ${effectiveThreshold} needed.`, { duration: 6000, icon: '✊' });
                        }
                        resetRequestForm();
                      } catch { toast.error('Failed to nominate'); }
                      setDuplicateModal(false);
                    }}
                    className="btn btn-primary w-full flex items-center justify-center gap-2"
                  >
                    👑 Nominate This Song
                  </button>
                  <button
                    onClick={closeDuplicateModalAndReset}
                    className="btn btn-secondary w-full"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  This song has already been requested by another attendee. You can:
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={async () => {
                      const voted = await handleVote(duplicateExistingRequest.id, 'up');
                      setDuplicateModal(false);
                      if (voted) {
                        toast.success('Vote added!');
                        resetRequestForm();
                      }
                    }}
                    className="btn btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Vote for It
                  </button>
                  {event?.settings?.queueMode === 'ranked' && !myOrder.slice(0, rankSize).includes(duplicateExistingRequest.id) && (
                    <button
                      onClick={() => {
                        const added = handleAddToRanking(duplicateExistingRequest.id);
                        setDuplicateModal(false);
                        if (added) {
                          toast.success('Added to your ranking!');
                          resetRequestForm();
                        }
                      }}
                      className="btn btn-secondary w-full flex items-center justify-center gap-2"
                    >
                      <Star className="w-4 h-4" />
                      Add to Ranking
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      if (!event) return;
                      try {
                        const newReq = await createRequest(event.id, {
                          song: {
                            title: requestSong,
                            artist: requestArtist,
                            explicitFlag: isExplicitSong,
                            ...(selectedSong && {
                              albumArtUrl: selectedSong.artworkUrl100 || undefined,
                              durationMs: selectedSong.trackTimeMillis || undefined,
                              itunesTrackId: selectedSong.trackId ? String(selectedSong.trackId) : undefined,
                              previewUrl: selectedSong.previewUrl || undefined,
                              isrc: selectedSong.isrc ?? null,
                            }),
                          },
                          requestedBy: { oderId: attendeeId, nickname: nickname.trim() || undefined },
                          message: requestMessage.trim() || undefined,
                          contested: true,
                          ...(attendeeSongSkipsApproval(event) && { initialStatus: 'queued' as const }),
                        });
                        try {
                          await voteForRequest(attendeeId, newReq.id, 'up');
                          setVotedRequests(prev => new Map(prev).set(newReq.id, 'up'));
                        } catch { /* non-critical */ }
                        toast.success('Contested request submitted! The DJ will review it.');
                        resetRequestForm();
                      } catch (error) {
                        if (!(await explainClosedRequestRefusal(error))) {
                          console.error('Contested request error:', error);
                          toast.error('Failed to submit contested request');
                        }
                      }
                      setDuplicateModal(false);
                    }}
                    className="btn bg-amber-500 hover:bg-amber-600 text-white w-full flex items-center justify-center gap-2"
                  >
                    <Music className="w-4 h-4" />
                    Contest as Different Song
                  </button>
                  <button
                    onClick={closeDuplicateModalAndReset}
                    className="btn btn-secondary w-full"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Song Flag Modal */}
      {flagModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setFlagModal(null); setFlagReason(''); }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Flag className="w-5 h-5 text-amber-500" />
                Flag Song
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {flagModal.songTitle} — {flagModal.songArtist}
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Why are you flagging this song?
                </label>
                <textarea
                  value={flagReason}
                  onChange={e => setFlagReason(e.target.value)}
                  placeholder="Please describe why this song is inappropriate (min 10 characters)..."
                  className="input w-full h-24 resize-none"
                  maxLength={500}
                />
                <p className="text-xs text-gray-400 mt-1">{flagReason.length}/500</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => { setFlagModal(null); setFlagReason(''); }}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleFlagSubmit}
                disabled={flagSubmitting || flagReason.trim().length < 10}
                className="btn btn-sm bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
              >
                {flagSubmitting ? 'Submitting...' : 'Submit Flag'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Playlist Import Modal */}

      {/* QR Scanner Modal for On-Site Verification */}
      {showQrScanner && event && (
        <QrScannerModal
          onClose={() => setShowQrScanner(false)}
          onVerified={() => {
            setIsVerified(true);
            setVerifiedCache(event.id, true);
            setShowQrScanner(false);
            toast.success('Verified! You can now request and vote.', { icon: '🎉' });
          }}
          eventId={event.id}
          attendeeId={attendeeId}
        />
      )}

      {/* Attendee Tutorial */}
      {tutorialMode !== null && event && (
        <AttendeeTutorial
          mode={tutorialMode}
          initialHelpTopic={tutorialHelpTopic ?? undefined}
          isRankedMode={event.settings?.queueMode === 'ranked'}
          showOverflowHelp={OVERFLOW_POOL_ENABLED && (event.settings?.overflowEnabled ?? false)}
          downvotingEnabled={event.settings?.doomVotingEnabled ?? false}
          eventName={event.name}
          onClose={handleTutorialClose}
          onPermanentDismiss={handleTutorialPermanentDismiss}
        />
      )}

      {/* Report a bug footer link */}
      <div className="text-center py-4 mt-4">
        <p className="text-xs text-gray-400 mb-1 px-4">
          By using this page you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600 dark:hover:text-gray-300">Terms</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600 dark:hover:text-gray-300">Privacy Policy</a>.
        </p>
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLSf48WscH-c1_hvSAIpiiaU3uGXuAMRqqxx91CzXsmJ1-k5b_A/viewform"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          Report an issue
        </a>
        <span className="text-xs text-gray-300 dark:text-gray-600 ml-2">
          · Build {typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'}
        </span>
      </div>
    </div>
  );
}
